import { useGenerationLifecycle, finishGenerationRun, hasResumableGenerationOperation, type PausedRecoveryMetadata } from './hooks/useGenerationLifecycle';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Ledger, StoryBible, Chapter, AppStatus, AgentRole, LogEntry, ValidationResult, StorySeed, ProjectMetadata, FullProjectState, EditorResult, StoryMode, ChapterLength, WeightedGenre, SoftStateBrief, WorkspaceActionState } from './types';
import { isProjectUnlocked } from './utils/entitlements';
import * as geminiService from '../services/gemini';
import { FourBeatStructuralFailureError, FourBeatCallBudgetExhaustedError, clearFourBeatChapterSession, isFourBeatInspectorPass, shouldUseLegacyInspectorCorrectionLoop, validateFourBeatSessionScope, PhysicalContinuityExtractionError } from '../services/gemini';
import { shouldAttemptInspectorBeatRepair } from './utils/fourBeatInspectorOrchestration.service';
import {
  buildInspectorFailureUserMessage,
  classifyInspectorDecision,
  isInfrastructureInspectorError,
  logInspectorDecision,
  promoteRepairableInspectorVerdict,
  recordInspectorLogicalCall,
  syncInspectorCallCount,
} from './utils/inspectorDecision.service';
import {
  buildInspectorInputCheckpoint,
  buildInspectorInputCheckpointPath,
  hashRecoveryContext,
  isRecoveryCheckpointExpired,
  validateInspectorCheckpointResume,
  type InspectorInputCheckpoint,
} from './utils/recoveryCheckpoint.service';
import {
  mapInspectorTerminalReasonToFailureCategory,
  resolveRecoveryAction,
  type InspectorFailureCategory,
} from './utils/recoveryAction.service';
import { shouldReleaseReservationOnRecoveryCancel, buildPhysicalRecoveryExpiresAt } from './utils/reservationRecovery.service';
import {
  filterInspectorPhysicalStateChangesForFinalProse,
  logPhysicalContinuityExtractionBlocked,
} from './utils/physicalContinuityExtraction.service';
import type { InspectorPhysicalStateChange, AuthoritativeContinuitySnapshot } from './types';
import { CONTINUITY_ENGINE_VERSION } from './constants';
import { buildAuthoritativeContinuitySnapshot } from './utils/authoritativeContinuitySnapshot.service';
import { latestStateDeltaToNarrativeStateDelta } from './utils/latestStateDelta.service';
import { RateLimitError, GenerationOutcomeUnknownError } from '../services/geminiTransport';
import AgentCard from '../components/AgentCard';
import LedgerView from '../components/LedgerView';
import StoryFeed from '../components/StoryFeed';
import LogPanel from '../components/LogPanel';
import StorySetup from '../components/StorySetup';
import ProjectDashboard from '../components/ProjectDashboard';
import SequelSetup, { type SequelSourceContext } from '../components/SequelSetup';
import RoadmapEditor from '../components/RoadmapEditor';
import { PublicationExport } from '../components/PublicationExport';
import { ManuscriptEditor } from '../components/ManuscriptEditor';
import { useFirebase } from '../components/FirebaseProvider';
import { PaywallModal } from '../components/PaywallModal';
import type { PaywallModalState } from './utils/manuscriptCreditRouting.service';
import { MissingChapterModal } from '../components/MissingChapterModal';
import { ProjectIntegrityModal } from '../components/ProjectIntegrityModal';
import { db } from './firebaseConfig';
import { doc, setDoc, getDoc, getDocFromServer, getDocs, collection, query, where, deleteDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import {
  saveChapterContentToStorage,
  loadChapterContentFromStorage,
  loadChapterFromStorage,
  getLastStorageLoadError,
  StorageChapter,
  migrateFirestoreChapterToStorage,
  saveCheckpointToStorage,
  loadCheckpointFromStorage,
  deleteCheckpointFromStorage,
  saveRecoveryCheckpointToStorage,
  loadRecoveryCheckpointFromStorage,
  resolveChapterStoragePath,
  buildChapterStoragePath,
} from '../services/storageChapter.service';
import BlueprintReview from '../components/BlueprintReview';
import { LegalGate } from '../components/LegalGate';
import { resolveChapterTitle } from '../services/chapterTitle.service';
import { compileDeterministicContext } from '../services/contextCompiler.service';
import { runAcceptanceChecks } from '../services/acceptanceGate.service';
import {
  assessChapterContractSufficiency,
  buildChapterExpansionPlan,
  formatExpansionProgressMessage,
  isIncompleteInitialDraft,
  resolveChapterWordTargets,
  resolvePostExpansionDraftState,
  shouldBlockPipelineAfterExpansionFailure,
} from './utils/chapterExpansion.service';
import { enrichPlanWithFourBeatContract } from './utils/fourBeatChapterContract.service';
import { findUndefinedPaths, sanitizeForFirestore } from './utils/firestoreSanitizer';
import { projectSaveQueue } from './utils/projectSaveQueue';

const CURRENT_LEGAL_VERSION = "2026-06";
const USE_DETERMINISTIC_CONTEXT = true;
const FALLBACK_TO_LLM_SOFT_STATE = true;
const ACCEPTANCE_GATE_BLOCKING_MODE = false;
const ENABLE_UNDER_TARGET_EXPANSION = true;
const UNDER_TARGET_EXPANSION_THRESHOLD = 0.80;
const ADMIN_UIDS = ['ADMIN_TEST_UID', 'skald-scribe-admin', 'your-admin-uid-here', '4WdZq8521c7', '4WdZq8521c7P'];
const isAdmin = (user: any) => user && (ADMIN_UIDS.includes(user.uid) || user.email === 'skaldhaven@gmail.com');


const ENABLE_BLUEPRINT_SUFFICIENCY = import.meta.env.VITE_ENABLE_BLUEPRINT_SUFFICIENCY === "true";
const BLUEPRINT_PIPELINE_VERSION = import.meta.env.VITE_BLUEPRINT_PIPELINE_VERSION || "1.0";

const ArchitectIcon = () => <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>;
const ScribeIcon = () => <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;
const EditorIcon = () => <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>;
const ArchivistIcon = () => <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>;
const InspectorIcon = () => <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const PublicationIcon = () => <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>;
const RewriterIcon = () => <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>;
const ExportIcon = () => <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z" /></svg>;
const BackupIcon = () => <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>;

const getManifestKey = (uid: string) => `SHS_MANIFEST_${uid}`;
const getProjectPrefix = (uid: string) => `SHS_PROJECT_${uid}_`;
const MAX_EDITOR_RETRIES = 2;
const MAX_CORRECTION_ATTEMPTS = 1; // Stabilized: 1 correction retry per chapter attempt. Keeps AI call count low.

function ensureArray(value: any): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === "string") {
    return value
      .split(/\n|;|•|-/)
      .map(v => v.trim())
      .filter(Boolean);
  }
  return [];
}

import { getDisplayChapterCount, getVerifiedChapterCount, getCanonicalCompletionState, getManuscriptWorkspaceState, formatInternalGapGenerationMessage } from './utils/projectCounts';
import { resolveChapterProseText } from './utils/chapterProseResolver';
import { resolveChapterSummary } from './utils/chapterSummaryResolver';
import {
  filterUserProjects,
  normalizeProjectFromFirestore,
  PROJECTS_LOAD_ERROR_MESSAGE,
  type ProjectsLoadState,
} from './utils/projectLoader';
import SequelOperationPanel from '../components/SequelOperationPanel';
import type { SequelOperation } from './types';
import {
  filterDashboardProjects,
  requestSequelCancel,
} from './utils/sequelInitialization.service';
import {
  querySequelOperationForResume,
  runSequelInitialization,
} from './utils/sequelInitialization.runner';
import { toUserFacingRoadmapError } from './utils/roadmapPlanning.service';
import { buildSequelPlanningPromptAddendum } from './utils/sequelPlanContract';
import {
  buildApprovedRoadmapPackage,
  storyBibleWithApprovedRoadmap,
  verifyPersistedChapterPlans,
  verifyPersistedRoadmapSource,
  readChapterPlansFromSavedState,
  validateApprovedRoadmap,
} from './utils/roadmapPersistence.service';
import {
  applyPremadeRoadmapParse,
  logPremadeRoadmapDiagnostic,
  detectSuspectRoadmapEntries,
  parsePremadeRoadmap,
  sourceForArchitectInvocation,
  tagPlansWithSource,
} from './utils/premadeRoadmap.service';
import { validateForbiddenProseForPersistence } from './utils/forbiddenProse.service';
import { ChapterCanonPreflightError } from './utils/chapterCanonKnowledge.service';
import {
  countWords,
  evaluatePublicationRepairAcceptance,
  logPublicationRepairGrowthRejected,
  PUBLICATION_REPAIR_MAX_GROWTH_RATIO,
} from './utils/publicationRepairPolicy.service';
import {
  dedupeChaptersByNumber,
  verifyProjectChapterCountWithRetry,
} from './utils/metadataVerification.service';
import type { ParsedReservationError } from './utils/generationRunContext';
import { reconcileStaleActiveRun, resolveGenerationRunId } from './utils/generationRunContext';

export default function App() {
  const { user, userData, login, logout, reserveTokens, releaseTokenReservation, consumeTokenReservation, updateUserData, refreshUserData, applyManuscriptCreditToProject } = useFirebase();
  const initializingRef = useRef(false);
  const [status, setStatus] = useState<AppStatus>(AppStatus.DASHBOARD);
  const { runGenerationLifecycle } = useGenerationLifecycle();
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectMetadata[]>([]);
  const [projectsLoadState, setProjectsLoadState] = useState<ProjectsLoadState>('idle');
  const [projectsLoadError, setProjectsLoadError] = useState<string | null>(null);
  const [projectsLoadAttempt, setProjectsLoadAttempt] = useState(0);
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [storyBible, setStoryBible] = useState<StoryBible | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [ledgerHistory, setLedgerHistory] = useState<Ledger[]>([]);
  const [currentChapterNum, setCurrentChapterNum] = useState(1);
  const [correctionOrder, setCorrectionOrder] = useState<string | undefined>(undefined);
  const [isAutoRunning, setIsAutoRunning] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [directorsNote, setDirectorsNote] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [failedDraft, setFailedDraft] = useState<string | undefined>(undefined);
  const [rewriteModal, setRewriteModal] = useState<{isOpen: boolean, chapterId: number | null}>({isOpen: false, chapterId: null});
  const [rewriteInstruction, setRewriteInstruction] = useState("");
  const [isRoadmapEditorOpen, setIsRoadmapEditorOpen] = useState(false);
  const [isPublicationModalOpen, setIsPublicationModalOpen] = useState(false);
  const [isManuscriptEditorOpen, setIsManuscriptEditorOpen] = useState(false);
  const [roadmapVersions, setRoadmapVersions] = useState<any[]>([]);
  const [publicationError, setPublicationError] = useState<string | null>(null);
  const [activeProgress, setActiveProgress] = useState<{ chapterNum: number; agentName: string; elapsedSecs: number; reassuranceMsg: string; requestId?: string } | null>(null);
  const [lastSeed, setLastSeed] = useState<StorySeed | null>(null);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [showAgentDebug, setShowAgentDebug] = useState(false);
  const [showAdvancedLogs, setShowAdvancedLogs] = useState(false);
  const [showAdvancedControls, setShowAdvancedControls] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [chapterLoadErrors, setChapterLoadErrors] = useState<Record<number, string>>({});
  const [showReconciliationPrompt, setShowReconciliationPrompt] = useState(false);
  const [paywallModal, setPaywallModal] = useState<PaywallModalState>({ isOpen: false, reason: 'generation' });
  const [chaptersAuthoritative, setChaptersAuthoritative] = useState(false);
  const [isClearingLock, setIsClearingLock] = useState(false);
  const [projectIntegrityModalOpen, setProjectIntegrityModalOpen] = useState(false);
  const [missingChapterModalOpen, setMissingChapterModalOpen] = useState(false);
  const sequelBusyRef = useRef(false);
  const generationInFlightRef = useRef(false);
  const paywallModalOpenRef = useRef(false);
  const pendingGenerationRunIdRef = useRef<string | null>(null);
  const [generationBlockNotice, setGenerationBlockNotice] = useState<ParsedReservationError | null>(null);
  const [generationCooldownUntil, setGenerationCooldownUntil] = useState(0);
  const [isEnteringHub, setIsEnteringHub] = useState(false);
  const [roadmapPersistError, setRoadmapPersistError] = useState<string | null>(null);
  const [sequelOperation, setSequelOperation] = useState<SequelOperation | null>(null);
  const [sequelPanelOpen, setSequelPanelOpen] = useState(false);
  const [sequelStepMessage, setSequelStepMessage] = useState<string | null>(null);
  const [sequelSourceContext, setSequelSourceContext] = useState<SequelSourceContext | null>(null);
  const [checkpointDraftText, setCheckpointDraftText] = useState<string | null>(null);
  const [pausedRecoveryMetadata, setPausedRecoveryMetadata] = useState<PausedRecoveryMetadata | null>(null);

  const addLog = useCallback((
    agent: AgentRole | 'System',
    message: string,
    type: LogEntry['type'] = 'info',
    audience: LogEntry['audience'] = 'user',
    technicalMessage?: string,
    requestId?: string
  ) => {
    setLogs(prev => [...prev, {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toLocaleTimeString(),
      agent,
      message,
      type,
      audience,
      technicalMessage,
      requestId
    }]);
    if (type === 'error') {
      setShowAdvancedLogs(true);
    }
  }, []);

  const releaseRecoveryReservationIfNeeded = useCallback(async (metadata: PausedRecoveryMetadata | null) => {
    if (!metadata || !shouldReleaseReservationOnRecoveryCancel(metadata.reservationRecoveryState)) return;
    const runId = metadata.reservationRecoveryRunId || metadata.activeRunId;
    if (!runId) return;
    try {
      await releaseTokenReservation(runId, 'recovery_cancelled');
      addLog('System', 'Recoverable chapter charge released.', 'info', 'developer');
    } catch (error: any) {
      console.warn('[Recovery] Failed to release retained reservation:', error?.message || error);
    }
  }, [releaseTokenReservation, addLog]);

  const ap = projects.find(p => p.id === currentProjectId);
  const totalChapters =
    storyBible?.targetChapterCount ??
    ap?.targetChapterCount ??
    ap?.chapterCount ??
    0;

  const canonicalState = getCanonicalCompletionState(chapters, totalChapters, chaptersAuthoritative);
  const workspaceState = getManuscriptWorkspaceState(chapters, totalChapters, chaptersAuthoritative);

  let completedChapters: number | null = workspaceState.existingChapterNumbers.length || canonicalState.completedChapterCount;
  if (!chaptersAuthoritative && workspaceState.existingChapterNumbers.length === 0) {
    completedChapters = typeof ap?.trustedCompletedChapterCount === 'number' ? ap.trustedCompletedChapterCount : null;
  }

  const creditTier = storyBible?.creditTier ?? ap?.creditTier ?? 'free_preview';
  const maxChaptersAllowed = storyBible?.maxChaptersAllowed ?? ap?.maxChaptersAllowed ?? 1;
  const hasPaidEntitlement =
    isProjectUnlocked(storyBible ?? ap ?? {}) ||
    !!(storyBible?.paidEntitlementId || ap?.paidEntitlementId);
  const manuscriptBalances = userData?.manuscriptCreditBalances;
  const userHasBookCredit = !!(
    (manuscriptBalances?.short_manuscript ?? 0) > 0 ||
    (manuscriptBalances?.standard_manuscript ?? 0) > 0 ||
    (manuscriptBalances?.large_manuscript ?? 0) > 0 ||
    (manuscriptBalances?.epic_manuscript ?? 0) > 0 ||
    (userData?.standardCredits ?? 0) > 0 ||
    (userData?.longCredits ?? 0) > 0 ||
    (userData?.epicCredits ?? 0) > 0
  );

  const gapsInTarget = workspaceState.missingChapterNumbers;
  const hasMissingChapters = gapsInTarget.length > 0;

  const isCorrupted =
    workspaceState.duplicateChapterNumbers.length > 0 ||
    (chaptersAuthoritative && canonicalState.invalidChapterRecords > 0);

  const isGenerating = [
    AppStatus.SCRIBING,
    AppStatus.INSPECTING,
    AppStatus.EDITING,
    AppStatus.PUBLISHING,
    AppStatus.ARCHIVING,
    AppStatus.RETRIEVING,
  ].includes(status);

  const isFailed = ap?.generationStatus === 'failed' || status === AppStatus.ERROR;
  const isStuck = ap?.generationStatus === 'running' && !!pausedRecoveryMetadata;
  const hasResumableOperation = hasResumableGenerationOperation(pausedRecoveryMetadata);

  const isComplete = workspaceState.manuscriptIsComplete;

  const exportReady = workspaceState.manuscriptIsComplete;

  const finalTextCompleted = exportReady;

  const isPaywalled =
    (!hasPaidEntitlement && (completedChapters || 0) >= 1) ||
    ((completedChapters || 0) >= maxChaptersAllowed);

  const integrityIssueLabel = (() => {
    if (workspaceState.duplicateChapterNumbers.length > 0) {
      return `Integrity issue: duplicate Chapter ${workspaceState.duplicateChapterNumbers[0]}`;
    }
    if (workspaceState.hasInternalGap && workspaceState.firstMissingChapterNumber) {
      return `Integrity issue: Chapter ${workspaceState.firstMissingChapterNumber} missing`;
    }
    if (hasMissingChapters && workspaceState.firstMissingChapterNumber) {
      return `Integrity issue: Chapter ${workspaceState.firstMissingChapterNumber} missing`;
    }
    return 'Integrity';
  })();

  let workspaceActionState: WorkspaceActionState;

  if (isCorrupted) {
    workspaceActionState = {
      kind: 'PROJECT_REPAIR_REQUIRED',
      buttonLabel: 'Repair Project',
      subLabel: workspaceState.duplicateChapterNumbers.length > 0
        ? `Duplicate chapter numbers detected (${workspaceState.duplicateChapterNumbers.join(', ')}).`
        : 'Project metadata needs repair before writing can continue.',
      disabled: false,
    };
  } else if (isGenerating) {
    const runningLabel =
      status === AppStatus.SCRIBING ? 'Draft Writer' :
      status === AppStatus.INSPECTING ? 'Continuity Check' :
      status === AppStatus.EDITING ? 'Style Edit' :
      status === AppStatus.PUBLISHING ? 'Final Polish' :
      status === AppStatus.ARCHIVING ? 'Story Memory' :
      status;
    workspaceActionState = {
      kind: 'GENERATION_RUNNING',
      buttonLabel: runningLabel,
      subLabel: 'Generation in progress.',
      disabled: true,
    };
  } else if (workspaceState.hasInternalGap && workspaceState.firstMissingChapterNumber) {
    const gap = workspaceState.firstMissingChapterNumber;
    workspaceActionState = {
      kind: 'MISSING_CHAPTER',
      buttonLabel: `Repair Chapter ${gap}`,
      subLabel: `Your manuscript already reaches Chapter ${workspaceState.highestExistingChapterNumber}, but Chapter ${gap} is missing. Repair the gap before continuing or exporting.`,
      disabled: false,
    };
  } else if (isComplete) {
    workspaceActionState = {
      kind: 'BOOK_COMPLETE',
      buttonLabel: 'Review Manuscript',
      subLabel: `${totalChapters} of ${totalChapters} chapters`,
      disabled: false,
    };
  } else if (isPaywalled) {
    if (userHasBookCredit) {
      workspaceActionState = {
        kind: 'BOOK_CREDIT_REQUIRED',
        buttonLabel: 'Unlock Project',
        subLabel: 'Apply a Book Credit to continue this manuscript.',
        disabled: false,
      };
    } else {
      workspaceActionState = {
        kind: 'BUY_CREDIT_REQUIRED',
        buttonLabel: 'Buy Book Credit',
        subLabel: 'Purchase a Book Credit to continue this manuscript.',
        disabled: false,
      };
    }
  } else if ((isFailed || isStuck) && hasResumableOperation) {
    workspaceActionState = {
      kind: 'CLEAR_FAILED_LOCK',
      buttonLabel: isClearingLock ? 'Resetting…' : 'Resume Writing',
      subLabel: 'The previous writing session did not finish. Reset the session and continue.',
      disabled: isClearingLock,
    };
  } else if (workspaceState.canGenerateNextSequentialChapter && workspaceState.nextSequentialChapterNumber) {
    workspaceActionState = {
      kind: 'WRITE_NEXT',
      buttonLabel: 'Write Next Chapter',
      subLabel: `Ready for Chapter ${workspaceState.nextSequentialChapterNumber}`,
      disabled: false,
    };
  } else {
    workspaceActionState = {
      kind: 'BOOK_COMPLETE',
      buttonLabel: 'Review Manuscript',
      subLabel: hasMissingChapters
        ? 'Manuscript has unresolved chapter gaps.'
        : 'No further sequential chapters are available.',
      disabled: true,
    };
  }

  if (import.meta.env.VITE_DEBUG_ENGINE === 'true') {
    console.log('workspaceActionState =', {
      projectId: currentProjectId,
      completedChapters,
      totalChapters,
      isBookComplete: isComplete,
      creditTier,
      maxChaptersAllowed,
      exportUnlocked: storyBible?.exportUnlocked,
      hasPaidEntitlement,
      userHasBookCredit,
      hasMissingChapters,
      gapsInTarget,
      status,
      actionKind: workspaceActionState.kind,
      buttonLabel: workspaceActionState.buttonLabel,
      subLabel: workspaceActionState.subLabel,
    });
  }

  const ledgerRef = useRef(ledger);
  const chaptersRef = useRef(chapters);
  const statusRef = useRef(status);
  const correctionRef = useRef(correctionOrder);
  const bibleRef = useRef(storyBible);
  const retryCountRef = useRef(retryCount);
  const failedDraftRef = useRef(failedDraft);
  const checkpointDraftTextRef = useRef(checkpointDraftText);
  const pausedRecoveryMetadataRef = useRef(pausedRecoveryMetadata);
  const inspectorPhysicalStateChangesRef = useRef<InspectorPhysicalStateChange[]>([]);
  const checkpointStoragePathRef = useRef<string | null>(null);

  
  useEffect(() => {
    if (!currentProjectId) {
      setChaptersAuthoritative(false);
      return;
    }
    const checkHydration = async () => {
      let isAllAuthoritative = true;
      for (const ch of chapters) {
        const hasInline = typeof ch.content === 'string' && ch.content.trim().length > 0;
        const hasStorage = typeof ch.storagePath === 'string' && ch.storagePath.trim().length > 0;
        if (!hasInline && hasStorage && !ch.storageContentVerified) {
          isAllAuthoritative = false;
        }
      }
      setChaptersAuthoritative(isAllAuthoritative);
    };
    checkHydration();
  }, [chapters, currentProjectId]);

  useEffect(() => { ledgerRef.current = ledger; }, [ledger]);
  useEffect(() => { chaptersRef.current = chapters; }, [chapters]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { paywallModalOpenRef.current = paywallModal.isOpen; }, [paywallModal.isOpen]);
  useEffect(() => { correctionRef.current = correctionOrder; }, [correctionOrder]);
  useEffect(() => {
    clearFourBeatChapterSession('project_switch');
  }, [currentProjectId]);
  useEffect(() => () => clearFourBeatChapterSession('component_teardown'), []);
  useEffect(() => { bibleRef.current = storyBible; }, [storyBible]);
  useEffect(() => { retryCountRef.current = retryCount; }, [retryCount]);
  useEffect(() => { failedDraftRef.current = failedDraft; }, [failedDraft]);
  useEffect(() => { checkpointDraftTextRef.current = checkpointDraftText; }, [checkpointDraftText]);
  useEffect(() => { pausedRecoveryMetadataRef.current = pausedRecoveryMetadata; }, [pausedRecoveryMetadata]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');
    if (checkoutStatus === 'success') {
      alert('Payment complete. Your Book Credit has been added.');
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (user) {
        const refreshUserData = async () => {
          try {
            const snap = await getDoc(doc(db, 'users', user.uid));
            if (snap.exists()) {
              await updateUserData(snap.data() as any);
              addLog('System', 'Book Credits successfully refreshed from billing ledger.', 'success');
            }
          } catch (e: any) {
            if (import.meta.env.VITE_DEBUG_BILLING === 'true') {
              console.error('Failed to sync billing balances:', e);
            }
          }
        };
        refreshUserData();
      }
    } else if (checkoutStatus === 'cancel') {
      alert('Checkout canceled. No payment was processed.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user, updateUserData, addLog]);


  useEffect(() => {
    if (pausedRecoveryMetadata) {
      const isChapterComplete = chapters.some(c => c.id === pausedRecoveryMetadata.failedChapterNumber && resolveChapterStoragePath(c));
      const isStale = pausedRecoveryMetadata.failedChapterNumber < currentChapterNum;
      const retentionExpired = pausedRecoveryMetadata.checkpointCreatedAt
        ? isRecoveryCheckpointExpired({
            expiresAt: new Date(
              Date.parse(pausedRecoveryMetadata.checkpointCreatedAt) + 7 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          })
        : false;
      if (isChapterComplete) {
        console.log(`[STORAGE AUDIT] Checkpoint ignored — chapter already committed | chapter: ${pausedRecoveryMetadata.failedChapterNumber}`);
        if (pausedRecoveryMetadata.checkpointStoragePath) {
          deleteCheckpointFromStorage(pausedRecoveryMetadata.checkpointStoragePath).catch(console.warn);
        }
        setPausedRecoveryMetadata(null);
      } else if (retentionExpired) {
        console.log(`[STORAGE AUDIT] Checkpoint retention expired | chapter: ${pausedRecoveryMetadata.failedChapterNumber}`);
        if (pausedRecoveryMetadata.checkpointStoragePath) {
          deleteCheckpointFromStorage(pausedRecoveryMetadata.checkpointStoragePath).catch(console.warn);
        }
        setPausedRecoveryMetadata(null);
      }
    }
  }, [pausedRecoveryMetadata, currentChapterNum, chapters]);

  useEffect(() => {
    if (status === AppStatus.DASHBOARD && currentProjectId) {
      console.log('[TRACE 2] Backing out to dashboard local state:', {
        activeProject: projects.find(p => p.id === currentProjectId),
        storyBible: storyBible ? {
          creditTier: storyBible.creditTier,
          exportUnlocked: storyBible.exportUnlocked,
          paidEntitlementId: storyBible.paidEntitlementId
        } : null,
        dashboardListItem: projects.find(p => p.id === currentProjectId)
      });
    }
  }, [status, currentProjectId]);

  useEffect(() => {
    if (!user) {
      setProjects([]);
      setProjectsLoadState('idle');
      setProjectsLoadError(null);
      setCurrentProjectId(null);
      setStoryBible(null);
      setChapters([]);
      setLedger(null);
      setStatus(AppStatus.DASHBOARD);
      return;
    }
    const fetchProjects = async () => {
      setProjectsLoadState('loading');
      setProjectsLoadError(null);
      if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
        console.log(`[Diagnostic Operation] LIST starting: Projects collection | Path: "users/${user.uid}/projects" | Auth UID exists: true | UID: "${user.uid}"`);
      }
      try {
        const q = collection(db, 'users', user.uid, 'projects');
        const querySnapshot = await getDocs(q);
        if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
          console.log(`[Diagnostic Operation] LIST success: Projects collection | Path: "users/${user.uid}/projects" | Found docs count: ${querySnapshot.size}`);
        }
        const fbProjects: ProjectMetadata[] = [];
        querySnapshot.forEach((docSnap) => {
          const p = normalizeProjectFromFirestore(docSnap.id, docSnap.data(), user.uid);
          const normalizedCount = getDisplayChapterCount(p);
          if (normalizedCount > (p.chapterCount || 0)) {
             p.chapterCount = normalizedCount;
             // Background repair
             setDoc(doc(db, 'users', user.uid, 'projects', docSnap.id), { chapterCount: normalizedCount }, { merge: true }).catch(console.error);
          }
          fbProjects.push(p);
        });
        
        // Force sync local manifest to flush out any stale offline cache
        localStorage.setItem(getManifestKey(user.uid), JSON.stringify(fbProjects));
        
        setProjects(fbProjects);
        setProjectsLoadState('success');
      } catch(e: any) {
        console.error(`[Projects Load] Failed for users/${user.uid}/projects`, e?.code || 'unknown', e?.message || e);
        if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
          console.error(`[Diagnostic Operation] FAILED Projects collection | Path: "users/${user.uid}/projects" | Error Code: "${e.code}" | Message: "${e.message}"`);
        }
        let recoveredFromManifest = false;
        try {
          const manifestStr = localStorage.getItem(getManifestKey(user.uid));
          if (manifestStr) {
            const manifest = JSON.parse(manifestStr) as ProjectMetadata[];
            setProjects(manifest.map((project) => normalizeProjectFromFirestore(project.id, project as Record<string, unknown>, user.uid)));
            recoveredFromManifest = true;
            setProjectsLoadState('success');
          }
        } catch {
          // ignore manifest parse errors
        }
        if (!recoveredFromManifest) {
          setProjects([]);
          setProjectsLoadState('error');
          setProjectsLoadError(PROJECTS_LOAD_ERROR_MESSAGE);
        }
      }
    };
    fetchProjects();
  }, [user, projectsLoadAttempt]);

  const formatGenreString = (genres: WeightedGenre[] | string | undefined) => {
    if (!genres) return 'Unknown Genre';
    if (typeof genres === 'string') return genres;
    if (!Array.isArray(genres)) return 'Unknown Genre';
    
    return genres.map(g => {
        if (g.weight === 'primary') return `${g.name} (P)`;
        if (g.weight === 'secondary') return `${g.name} (S)`;
        return g.name;
    }).join(', ');
  }

  const saveCurrentProject = useCallback(async (durable: boolean = false, stateOverrides?: Partial<FullProjectState>, isCompletionSave: boolean = false): Promise<boolean> => {
    if (!currentProjectId || !ledger || !storyBible) return false;
    const fullState: FullProjectState = { 
      id: currentProjectId,
      seriesId: storyBible.seriesId || currentProjectId,
      volume: storyBible.volume || 1,
      ledger, 
      storyBible, 
      chapters, 
      currentChapterNum, 
      logs, 
      ledgerHistory,
      roadmapVersions,
      pausedRecoveryMetadata: pausedRecoveryMetadata as any,
      ...stateOverrides
    };
    try {
      if (user) {
        localStorage.setItem(`${getProjectPrefix(user.uid)}${currentProjectId}`, JSON.stringify(fullState));
      }
    } catch (e: any) {
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        console.warn("Storage quota exceeded, truncating history to fit...");
        // Truncate non-essential arrays to save space
        const slimState: FullProjectState = {
          ...fullState,
          logs: logs.slice(-50), // Keep only recent logs
          ledgerHistory: ledgerHistory.slice(-5), // Keep only recent history
          roadmapVersions: roadmapVersions.slice(-2),
        };
        try {
          if (user) localStorage.setItem(`${getProjectPrefix(user.uid)}${currentProjectId}`, JSON.stringify(slimState));
        } catch (innerErr) {
          console.error("Still quota exceeded even after truncation:", innerErr);
          // If still failing, drop them entirely
          const criticalState: FullProjectState = {
            ...slimState,
            logs: [],
            ledgerHistory: [],
            roadmapVersions: []
          };
          try {
             if (user) localStorage.setItem(`${getProjectPrefix(user.uid)}${currentProjectId}`, JSON.stringify(criticalState));
          } catch(finalErr) {
             console.error("Critical storage failure:", finalErr);
          }
        }
      } else {
        console.error("Error saving state to localStorage:", e);
      }
    }

    const effectiveChapters = stateOverrides?.chapters ?? chapters;
    const savingChapterNum = stateOverrides?.currentChapterNum ?? currentChapterNum;
    const activeProject = projects.find(p => p.id === currentProjectId);
    
    const previousCalculatedChapterCount = activeProject?.chapterCount ?? 0;
    // Do not write corrected dashboard metadata until isAuthoritative === true
    let correctedChapterCount = previousCalculatedChapterCount;
    if (chaptersAuthoritative || isCompletionSave) {
       const canonicalForSave = getCanonicalCompletionState(effectiveChapters, storyBible.targetChapterCount || 0, true);
       correctedChapterCount = canonicalForSave.completedChapterCount;
    }

    if (correctedChapterCount > previousCalculatedChapterCount) {
        console.log(`[METADATA COUNT CORRECTED]`, {
          projectId: currentProjectId,
          savingChapterNum,
          previousCalculatedChapterCount,
          correctedChapterCount
        });
        
        // Update local dashboard list immediately
        setProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, chapterCount: correctedChapterCount } : p));
    }

    const updatedProject: ProjectMetadata = {
      id: currentProjectId,
      seriesId: storyBible.seriesId || currentProjectId,
      title: storyBible.title,
      genre: formatGenreString(storyBible.genre),
      lastUpdated: new Date().toISOString(),
      chapterCount: correctedChapterCount,
      mode: storyBible.mode,
      volume: storyBible.volume || 1,
      status: statusRef.current,
      creditTier: storyBible.creditTier || 'free_preview',
      rewritesUsed: storyBible.rewritesUsed || 0,
      maxChaptersAllowed: storyBible.maxChaptersAllowed || (storyBible.creditTier === 'standard_manuscript' ? 20 : storyBible.creditTier === 'large_manuscript' ? 30 : storyBible.creditTier === 'epic_manuscript' ? 40 : 1),
      exportUnlocked: storyBible.exportUnlocked || false,
      ...(user && { userId: user.uid })
    };

    let success = true;
    if (user && durable) {
      const slimChapters = (stateOverrides?.chapters || chapters).map((c) => {
        const { content, recoveryMetadata, ...rest } = c;
        const slim = c.storagePath ? rest : { ...c };
        if ('recoveryMetadata' in slim && slim.recoveryMetadata === undefined) {
          delete (slim as { recoveryMetadata?: unknown }).recoveryMetadata;
        }
        return slim;
      });
      const slimState = {
         ledger: stateOverrides?.ledger || ledger,
         storyBible: stateOverrides?.storyBible || storyBible,
         chapters: slimChapters,
         currentChapterNum: stateOverrides?.currentChapterNum ?? currentChapterNum,
         pausedRecoveryMetadata: stateOverrides?.pausedRecoveryMetadata ?? pausedRecoveryMetadata
      };

      // Explicitly normalize StoryBible entitlement fields
      if (slimState.storyBible) {
         slimState.storyBible.creditTier = slimState.storyBible.creditTier ?? 'free_preview';
         slimState.storyBible.paidEntitlementId = slimState.storyBible.paidEntitlementId ?? null;
         slimState.storyBible.maxChaptersAllowed = slimState.storyBible.maxChaptersAllowed ?? 1;
         slimState.storyBible.exportUnlocked = slimState.storyBible.exportUnlocked ?? false;
         slimState.storyBible.rewritesUsed = slimState.storyBible.rewritesUsed ?? 0;
         slimState.storyBible.entitlementAppliedAt = slimState.storyBible.entitlementAppliedAt ?? null;
      }
      
      updatedProject.creditTier = updatedProject.creditTier ?? 'free_preview';
      updatedProject.paidEntitlementId = slimState.storyBible?.paidEntitlementId ?? null;
      updatedProject.exportUnlocked = slimState.storyBible?.exportUnlocked ?? false;
      updatedProject.maxChaptersAllowed = slimState.storyBible?.maxChaptersAllowed ?? 1;
      updatedProject.rewritesUsed = slimState.storyBible?.rewritesUsed ?? 0;
      updatedProject.entitlementAppliedAt = slimState.storyBible?.entitlementAppliedAt ?? null;
      if ((slimState.storyBible as any)?.isExplicitDowngrade) {
          (updatedProject as any).isExplicitDowngrade = true;
      }
        // freePreviewUsed should only be set after verification passes

      // Debug: Check for undefined paths
      const undefinedPathsProject = findUndefinedPaths(updatedProject);
      const undefinedPathsState = findUndefinedPaths(slimState);
      if (undefinedPathsProject.length > 0) console.warn("[Firestore Sanitizer] Undefined paths in updatedProject:", undefinedPathsProject);
      if (undefinedPathsState.length > 0) console.warn("[Firestore Sanitizer] Undefined paths in slimState:", undefinedPathsState);

      // Sanitize
      const cleanProject = sanitizeForFirestore(updatedProject);
      const cleanState = sanitizeForFirestore(slimState);

      try {
        success = await projectSaveQueue.enqueue({
           projectId: currentProjectId,
           uid: user.uid,
           cleanProject,
           cleanState,
           chapterCount: updatedProject.chapterCount,
           isCompletionSave
        });
      } catch (err: any) {
        success = false;
        const isExhausted = 
          err.message?.includes('resource-exhausted') || 
          err.message?.includes('Quota limit exceeded') || 
          err.message?.includes('quota exceeded') ||
          err.code === 'resource-exhausted';

        if (isExhausted) {
          const quotaMsg = "Firestore daily write quota has been reached. Generation is paused. Your latest checkpoint/chapter files may be saved in Storage, but metadata cannot update until quota resets or the database is upgraded.";
          console.error("[FIRESTORE QUOTA RUNTIME]", err);
          addLog('System', quotaMsg, 'error');
          setPublicationError(quotaMsg);
          setIsAutoRunning(false);
          setStatus(AppStatus.IDLE);
        } else {
          console.error("Firestore write failed:", err);
        }
      }
    }

    setProjects(prev => {
      const existing = prev.find(p => p.id === currentProjectId);
      
      const newManifest = existing 
        ? prev.map(p => p.id === currentProjectId ? { ...existing, ...updatedProject } : p) 
        : [...prev, updatedProject];
      if (user) localStorage.setItem(getManifestKey(user.uid), JSON.stringify(newManifest));
      return newManifest;
    });

    return success;
  }, [currentProjectId, ledger, storyBible, chapters, currentChapterNum, logs, ledgerHistory, roadmapVersions, user, pausedRecoveryMetadata, addLog]);

  const handleEnterNarrativeHub = useCallback(async () => {
    if (!storyBible || !ledger || !currentProjectId || !user) return;
    if (!storyBible.isBlueprintApproved || !storyBible.isRoadmapApproved) {
      setRoadmapPersistError('Approve both the Story Blueprint and Chapter Roadmap before continuing.');
      return;
    }

    const suspects = detectSuspectRoadmapEntries(storyBible.chapterPlans || []);
    const precheck = validateApprovedRoadmap(storyBible.chapterPlans || [], {
      expectedChapterCount: storyBible.targetChapterCount,
      suspectEntryIndexes: suspects.suspectEntryIndexes,
      suspectEntryReasons: suspects.suspectEntryReasons,
    });
    if (!precheck.valid) {
      setRoadmapPersistError(precheck.error);
      return;
    }

    setIsEnteringHub(true);
    setRoadmapPersistError(null);
    try {
      const pkg = buildApprovedRoadmapPackage(storyBible, storyBible.chapterPlans || []);
      const finalizedBible = storyBibleWithApprovedRoadmap(storyBible, pkg);
      logPremadeRoadmapDiagnostic('[ROADMAP APPROVAL PERSIST]', {
        rawLength: storyBible.originalUserRoadmapRaw?.length || storyBible.premadeRoadmap?.length || 0,
        parsedChapterCount: pkg.chapterPlans.length,
        expectedChapterCount: storyBible.targetChapterCount,
        parsedTitles: pkg.chapterPlans.map((plan) => plan.title),
        sourceBefore: storyBible.activeRoadmapSource,
        sourceAfter: pkg.roadmapSource,
        architectInvoked: false,
      });
      const saved = await saveCurrentProject(true, { storyBible: finalizedBible, ledger }, false);
      if (!saved) {
        setRoadmapPersistError('Failed to save the approved roadmap to the project. Staying in Blueprint Review.');
        return;
      }

      const stateSnap = await getDoc(doc(db, 'users', user.uid, 'projects', currentProjectId, 'state', 'current'));
      const persistedPlans = readChapterPlansFromSavedState(
        stateSnap.exists() ? (stateSnap.data() as { storyBible?: StoryBible }) : null,
      );
      const verify = verifyPersistedChapterPlans(persistedPlans, pkg.chapterPlans);
      if (!verify.valid) {
        setRoadmapPersistError(verify.error);
        return;
      }
      const verifySource = verifyPersistedRoadmapSource(
        stateSnap.exists() ? (stateSnap.data() as { storyBible?: StoryBible }).storyBible : undefined,
        pkg.roadmapSource,
      );
      if (!verifySource.valid) {
        setRoadmapPersistError(verifySource.error);
        return;
      }

      setStoryBible(finalizedBible);
      setStatus(AppStatus.IDLE);
      addLog('System', `Approved roadmap persisted (${pkg.chapterPlans.length} chapters, source: ${pkg.roadmapSource}). Entering narrative hub.`, 'success');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Roadmap persistence failed.';
      setRoadmapPersistError(message);
      addLog('System', message, 'error');
    } finally {
      setIsEnteringHub(false);
    }
  }, [storyBible, ledger, currentProjectId, user, saveCurrentProject, addLog]);

  useEffect(() => {
    if (status !== AppStatus.DASHBOARD && status !== AppStatus.SETUP) {
      if (isAutoRunning || status === AppStatus.SCRIBING || status === AppStatus.REWRITING) {
         // Prevent noncritical autosave during generation
         return;
      }
      // By default, auto-triggered saves are local-only
      saveCurrentProject(false);
    }
  }, [chapters.length, currentChapterNum, status, saveCurrentProject, isAutoRunning]);

  const toggleClosingMode = () => {
    setStoryBible(prev => {
      if (!prev) return prev;
      const nextMode = !prev.isClosingMode;
      addLog('System', `Narrative Finalization Protocol ${nextMode ? 'ENGAGED' : 'DISENGAGED'}`, 'warning');
      return { ...prev, isClosingMode: nextMode };
    });
  };

  // ── clearStaleLock — manual recovery for stuck/stale generation locks ──
  // Safe for both normal users (via Unlock and Retry) and admins (via Clear Stale Lock).
  // Reads the current lock state before clearing so diagnostics are logged.
  const clearStaleLock = useCallback(async (adminMode = false) => {
    if (!user || !currentProjectId) {
      addLog('System', 'Cannot clear lock: no active project or user.', 'error');
      return;
    }
    if (!adminMode && !hasResumableGenerationOperation(pausedRecoveryMetadata)) {
      addLog('System', 'No resumable writing session was found. Use Write Next Chapter or Repair Missing Chapter instead.', 'warning');
      return;
    }
    setIsClearingLock(true);
    const projectRef = doc(db, 'users', user.uid, 'projects', currentProjectId);
    try {
      const snap = await getDoc(projectRef);
      if (!snap.exists()) {
        addLog('System', 'Project document not found. Nothing to clear.', 'warning');
        return;
      }
      const data = snap.data();
      const lockTs = data.lastGenerationHeartbeatAt || data.generationStartedAt;
      const lockAge = lockTs ? Date.now() - new Date(lockTs).getTime() : Infinity;

      // Log diagnostics
      addLog('System', `[Lock Diagnostics] generationStatus=${data.generationStatus} activeRunId=${data.activeRunId ?? 'null'} activeChapterNum=${data.activeChapterNum ?? 'null'} age=${lockAge === Infinity ? 'unknown' : Math.round(lockAge / 60000) + 'min'}`, 'info', 'developer');

      if (!adminMode && data.generationStatus === 'running' && lockAge < 5 * 60 * 1000) {
        addLog('System', 'Generation appears active (lock age < 5 min). Use the admin tool to force clear.', 'warning');
        return;
      }

      await setDoc(projectRef, {
        generationStatus: 'failed',
        activeRunId: null,
        activeChapterNum: null,
        lastGenerationHeartbeatAt: null,
        lastGenerationFailureReason: adminMode ? 'admin_manual_clear' : 'user_manual_recovery'
      }, { merge: true });

      setProjects(prev => prev.map(p => p.id === currentProjectId ? {
        ...p,
        generationStatus: 'failed',
        activeRunId: null,
        activeChapterNum: null
      } : p));

      setStatus(AppStatus.IDLE);
      setIsAutoRunning(false);
      addLog('System', adminMode
        ? '[Stale Generation Lock Cleared] Project unlocked by admin. You can retry generation.'
        : 'Project unlocked. You can now retry generation.',
        'success'
      );
    } catch (e: any) {
      addLog('System', `Failed to clear lock: ${e.message}`, 'error');
    } finally {
      setIsClearingLock(false);
    }
  }, [user, currentProjectId, addLog, setStatus, setIsAutoRunning, pausedRecoveryMetadata]);

  const persistCompletedChapter = useCallback(async (

    chapterToSave: Chapter,
    nextChaptersArray: Chapter[],
    nextLedger: Ledger,
    nextChapterNum: number,
    isFinalChapter: boolean,
    isRewrite: boolean = false
  ): Promise<boolean> => {
    if (!user || !currentProjectId) return false;

    // 1. Save to Storage
    let storagePath = chapterToSave.storagePath || '';
    addLog('System', `Saving Chapter ${chapterToSave.id} to storage...`, 'action', 'developer');
    try {
      const storageData: StorageChapter = {
        chapterNumber: chapterToSave.id,
        title: chapterToSave.title,
        finalText: chapterToSave.content || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      storagePath = await saveChapterContentToStorage(user.uid, currentProjectId, storageData);
      chapterToSave.storagePath = storagePath;
      addLog('System', `Chapter ${chapterToSave.id} saved to storage.`, 'success', 'developer');
    } catch (e: any) {
      console.error("Storage upload failed", e);
      addLog('System', `Chapter ${chapterToSave.id} could not be saved to Storage. Please retry.`, 'error', 'user');
      setPublicationError(`Failed to upload chapter ${chapterToSave.id} to Firebase Storage.`);
      return false; // Storage failed, abort metadata
    }

    // Ensure nextChaptersArray has the updated storagePath
    const chaptersWithStorage = nextChaptersArray.map(c => 
      c.id === chapterToSave.id ? { ...c, storagePath } : c
    );

    // 2. Commit Explicitly to React State
    setLedger(nextLedger);
    setChapters(chaptersWithStorage);

    // 3. Commit Durably to Firestore Metadata
    try {
      const activeProjectDashboard = projects.find(p => p.id === currentProjectId);
      const firestoreRootRef = await getDoc(doc(db, 'users', user.uid, 'projects', currentProjectId));
      const firestoreRootData = firestoreRootRef.exists() ? firestoreRootRef.data() as ProjectMetadata : null;

      addLog('System', `[CHAPTER COUNT AUDIT] ${JSON.stringify({
        projectId: currentProjectId,
        expectedTotalChapters: storyBible?.targetChapterCount,
        localChaptersLength: chaptersWithStorage.length,
        activeProjectChapterCount: activeProjectDashboard?.chapterCount,
        firestoreRootChapterCount: firestoreRootData?.chapterCount,
        dashboardListChapterCount: activeProjectDashboard?.chapterCount,
        exportReadableJsonCount: "N/A", // Checked during export readiness
        storageResolvedPathCount: chaptersWithStorage.filter(c => c.storagePath).length,
        nextChapterNum: nextChapterNum
      })}`, 'info', 'developer');
    } catch (auditErr) {
      console.warn("Audit logging failed:", auditErr);
    }

    addLog('System', `[METADATA SAVE START] expectedChapterCount=${chaptersWithStorage.length}`, 'info', 'developer');
    const metaSuccess = await saveCurrentProject(true, {
      chapters: chaptersWithStorage,
      currentChapterNum: nextChapterNum,
      ledger: nextLedger
    }, true);

    if (!metaSuccess) {
      addLog('System', `[METADATA SAVE FAILED] Could not enqueue or update metadata.`, 'error', 'developer');
      const msg = `Chapter text was saved, but project metadata could not be updated. Click Retry Metadata Save to finish saving this chapter.`;
      addLog('System', msg, 'error', 'user');
      setPublicationError(msg);
      return false;
    } else {
      addLog('System', `[METADATA SAVE SUCCESS] Metadata write completed.`, 'success', 'developer');
    }

    const verification = await verifyProjectChapterCountWithRetry({
      expectedCount: chaptersWithStorage.length,
      readChapterCount: async () => {
        const projectRef = doc(db, 'users', user.uid, 'projects', currentProjectId);
        try {
          const projDoc = await getDocFromServer(projectRef);
          if (!projDoc.exists()) return undefined;
          return (projDoc.data() as ProjectMetadata).chapterCount;
        } catch (error: any) {
          if (error?.code === 'unavailable') {
            return undefined;
          }
          throw error;
        }
      },
    });

    if (!verification.ok) {
      addLog(
        'System',
        `[METADATA VERIFY FAILED] firestoreChapterCount=${verification.observedCount ?? 'unknown'} expected=${verification.expectedCount} attempts=${verification.attempts}`,
        'error',
        'developer',
      );
      const msg = `Chapter text was saved, but project metadata could not be verified after ${verification.attempts} read attempt(s). Click Retry Metadata Save to finish saving this chapter.`;
      addLog('System', msg, 'error', 'user');
      setPublicationError(msg);
      setStatus(AppStatus.IDLE);
      setIsAutoRunning(false);
      return false;
    }

    addLog(
      'System',
      `[METADATA VERIFY SUCCESS] firestoreChapterCount=${verification.observedCount} expected=${verification.expectedCount} attempts=${verification.attempts}${verification.settling ? ' (settled after retry)' : ''}`,
      'success',
      'developer',
    );

    // Legacy immediate read retained only as diagnostic; authoritative path is retry verifier above.
    try {
      const projDoc = await getDoc(doc(db, 'users', user.uid, 'projects', currentProjectId));
      if (projDoc.exists()) {
        const data = projDoc.data() as ProjectMetadata;
        if (data.chapterCount < chaptersWithStorage.length) {
          console.warn(`[Verification] Immediate read still below expected (${data.chapterCount} < ${chaptersWithStorage.length}) but retry verifier passed.`);
        }
      }
    } catch (e) {
      console.warn("[Verification] Could not read back project doc:", e);
    }

      // Verification passed: now it's safe to mark free preview used
      if (storyBible?.creditTier === 'free_preview' && chaptersWithStorage.length >= 1) {
         setDoc(doc(db, 'users', user.uid, 'projects', currentProjectId), { 
            freePreviewUsed: true,
            freePreviewUsedAt: new Date().toISOString()
         }, { merge: true }).catch(console.error);
      }
  
      return true;
    }, [user, currentProjectId, saveCurrentProject, addLog, storyBible]);

  const repairMetadataSave = useCallback(async () => {
    if (!user || !currentProjectId || !storyBible) return;
    
    addLog('System', `[REPAIR METADATA] Starting full storage scan for readable chapters...`, 'action', 'user');
    
    try {
        const validChapters: Chapter[] = [];
        let maxFoundChapterNum = 0;
        
        // Scan chapters 1 through targetChapterCount + 10 (buffer for overages)
        const scanLimit = (storyBible.targetChapterCount || 20) + 10;
        for (let i = 1; i <= scanLimit; i++) {
            const storagePath = buildChapterStoragePath(user.uid, currentProjectId, i);
            const storageData = await loadChapterContentFromStorage(storagePath);
            const prose = storageData ? resolveChapterProseText(storageData as unknown as Record<string, unknown>) : '';
            if (storageData && prose) {
                maxFoundChapterNum = i;
                validChapters.push({
                   id: storageData.chapterNumber || i,
                   title: storageData.title || `Chapter ${i}`,
                   content: prose,
                   summary: resolveChapterSummary({
                     storedSummary: storageData.summary,
                     roadmapSummary: storyBible.chapterPlans?.find((p) => p.chapterNumber === i)?.summary,
                     recoverySummary: storageData.recoverySummary,
                     existingSummary: storageData.title || `Chapter ${i}`,
                   }),
                   storagePath: storagePath
                });
            }
        }
        
        if (validChapters.length === 0) {
            addLog('System', `No readable chapter files found in Storage.`, 'error', 'user');
            return;
        }
        
        // Ensure sorted and deduplicated — never create duplicate chapter records during repair.
        const dedupedChapters = dedupeChaptersByNumber(validChapters);
        const finalCount = dedupedChapters.length;
        const isFinalChapter = maxFoundChapterNum >= (storyBible.targetChapterCount || 1);
        
        addLog('System', `[REPAIR METADATA] Scan complete. Found ${finalCount} valid chapters. Highest chapter is ${maxFoundChapterNum}.`, 'success', 'developer');
        
        setChapters(dedupedChapters);
        
        // Update dashboard list item immediately
        setProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, chapterCount: finalCount } : p));
        
        // Save to Firestore and local state
        const success = await saveCurrentProject(true, {
            chapters: dedupedChapters,
            currentChapterNum: isFinalChapter ? maxFoundChapterNum + 1 : maxFoundChapterNum + 1,
        }, isFinalChapter);
        
        if (success) {
            addLog('System', `Metadata repair successful. Project root chapterCount set to ${finalCount}.`, 'success', 'user');
            setCurrentChapterNum(isFinalChapter ? maxFoundChapterNum + 1 : maxFoundChapterNum + 1);
            setPublicationError(null);
            setIsAutoRunning(false);
            if (isFinalChapter) {
                setStatus(AppStatus.COMPLETE);
            } else {
                setStatus(AppStatus.IDLE);
            }
        } else {
            addLog('System', `Failed to save repaired metadata to Firestore.`, 'error', 'user');
        }
    } catch (err: any) {
        console.error("Repair failed:", err);
        addLog('System', `Repair failed: ${err.message}`, 'error');
    }
  }, [user, currentProjectId, storyBible, saveCurrentProject, addLog]);

  const handleRepairMemory = useCallback(() => {
    if (!ledger || !currentProjectId) return;
    const activeCanonId = storyBible?.canonId || currentProjectId;
    const safeLedger = geminiService.getFilteredLedger(ledger, activeCanonId, currentProjectId);
    
    const originalCount = (ledger.inventory?.length || 0) + Object.keys(ledger.characters || {}).length + (ledger.plot_points?.length || 0);
    const newCount = (safeLedger.inventory?.length || 0) + Object.keys(safeLedger.characters || {}).length + (safeLedger.plot_points?.length || 0);
    const removedCount = originalCount - newCount;
    
    setLedger(safeLedger);
    addLog('System', `Memory Repair complete. Removed ${removedCount} mismatched records.`, 'success');
    console.log(`[MEMORY REPAIR COMPLETE] { projectId: ${currentProjectId}, removedRecords: ${removedCount}, retainedRecords: ${newCount} }`);
  }, [ledger, currentProjectId, storyBible?.canonId, addLog]);

  const loadSequelSourceContext = useCallback(async (sourceProjectId: string): Promise<SequelSourceContext | null> => {
    if (!user || !sourceProjectId) return null;

    let sourceLedger: Ledger | null = null;
    let sourceBible: StoryBible | null = null;
    let sourceChapters: Chapter[] = [];
    let sourceVolume = 1;

    try {
      const stateDoc = await getDoc(doc(db, 'users', user.uid, 'projects', sourceProjectId, 'state', 'current'));
      if (stateDoc.exists()) {
        const data = stateDoc.data() as FullProjectState;
        sourceLedger = data.ledger;
        sourceBible = data.storyBible;
        sourceChapters = data.chapters || [];
      }
      const projDoc = await getDoc(doc(db, 'users', user.uid, 'projects', sourceProjectId));
      if (projDoc.exists()) {
        sourceVolume = (projDoc.data() as ProjectMetadata).volume || sourceBible?.volume || 1;
      }
    } catch (e) {
      console.error('Failed to load sequel source from Firestore', e);
    }

    if (!sourceLedger || !sourceBible) {
      const local = localStorage.getItem(`${getProjectPrefix(user.uid)}${sourceProjectId}`);
      if (local) {
        try {
          const parsed = JSON.parse(local) as FullProjectState;
          sourceLedger = parsed.ledger;
          sourceBible = parsed.storyBible;
          sourceChapters = parsed.chapters || [];
          sourceVolume = parsed.storyBible?.volume || sourceVolume;
        } catch (e) {
          console.error('Failed to parse local sequel source project', e);
        }
      }
    }

    if (!sourceLedger || !sourceBible) return null;
    return {
      projectId: sourceProjectId,
      volume: sourceVolume,
      ledger: sourceLedger,
      storyBible: sourceBible,
      chapters: sourceChapters,
    };
  }, [user]);

  const handleSelectProject = useCallback(async (id: string) => {
    console.log(`[PROJECT SWITCH MEMORY RESET] { fromProjectId: ${currentProjectId}, toProjectId: ${id} }`);
    
    // Clear transient runtime state before hydrating new project
    setStoryBible(undefined as any);
    setLedger(undefined as any);
    setChapters([]);
    setLedgerHistory([]);
    setRoadmapVersions([]);
    setCorrectionOrder(undefined);
    setFailedDraft(undefined);
    setRetryCount(0);
    setLogs([]);
    bibleRef.current = undefined as any;
    ledgerRef.current = undefined as any;

    let fullState: FullProjectState | null = null;
    let projectStatus = AppStatus.IDLE;

    if (user) {
      try {
        const stateDoc = await getDoc(doc(db, 'users', user.uid, 'projects', id, 'state', 'current'));
        let fbState: FullProjectState | undefined = undefined;
        if (stateDoc.exists()) {
          fbState = stateDoc.data() as FullProjectState;
        }
        
        const projectRef = doc(db, 'users', user.uid, 'projects', id);
        console.log(`[PROJECT OPEN READ PATH] ${projectRef.path}`);
        
        const projDoc = await getDoc(projectRef);
        if (fbState && projDoc.exists()) {
          const fbProj = projDoc.data() as ProjectMetadata;
          fullState = fbState;
          projectStatus = fbProj.status || AppStatus.IDLE;
          
          // Entitlement Hydration: Always let Firestore project root override stale local/list entitlement fields
          const isFbProjPaid = fbProj.creditTier === 'standard_manuscript' || fbProj.creditTier === 'large_manuscript' || fbProj.creditTier === 'epic_manuscript';
          if (isFbProjPaid) {
            fullState.storyBible.creditTier = fbProj.creditTier as any;
            fullState.storyBible.exportUnlocked = true;
            if (fbProj.maxChaptersAllowed) fullState.storyBible.maxChaptersAllowed = fbProj.maxChaptersAllowed;
            if (fbProj.paidEntitlementId) fullState.storyBible.paidEntitlementId = fbProj.paidEntitlementId;
            if (fbProj.entitlementAppliedAt) fullState.storyBible.entitlementAppliedAt = fbProj.entitlementAppliedAt;
            if (fbProj.rewriteCreditsIncluded !== undefined) fullState.storyBible.rewriteCreditsIncluded = fbProj.rewriteCreditsIncluded;
            if (fbProj.rewritesUsed !== undefined) fullState.storyBible.rewritesUsed = fbProj.rewritesUsed;
          } else {
            // Check if user has decremented balance but root doc doesn't show it (should be impossible now with compensating tx)
            if (fbState.storyBible?.paidEntitlementId && !fbProj.paidEntitlementId) {
               console.error(`[CRITICAL ENTITLEMENT MISSING AFTER CREDIT CONSUMED] Local state has ${fbState.storyBible.paidEntitlementId} but root doc is free.`);
            }
          }

            // Legacy Repair: Prevent duplicate/free leakage across volumes.
            if (fullState && fullState.storyBible && fullState.storyBible.volume > 1) {
              const sb = fullState.storyBible;
              const hasStrayEntitlement = sb.creditTier !== 'free_preview' || sb.exportUnlocked === true || !!sb.paidEntitlementId;
              const isPaidTier = sb.creditTier === 'standard_manuscript' || sb.creditTier === 'large_manuscript' || sb.creditTier === 'epic_manuscript';
              const isLegitPaid = sb.paidEntitlementId && sb.paidEntitlementId.includes(fullState.id);
              const hasAppliedAt = !!sb.entitlementAppliedAt;
              
              const isValidLegacyPaid = isPaidTier && sb.exportUnlocked;
              
              if (isValidLegacyPaid) {
                 console.log("[Entitlement Preservation] Valid paid entitlement detected; skipping legacy sanitization.");
                 if (!sb.paidEntitlementId) {
                     sb.paidEntitlementId = `legacy-${fullState.id}`;
                 }
                 if (!sb.entitlementAppliedAt) {
                     sb.entitlementAppliedAt = fbProj.lastUpdated || new Date().toISOString();
                 }
              } else if (hasStrayEntitlement && !isLegitPaid && !hasAppliedAt) {
                 console.log("Legacy Repair: Sanitizing stray inherited entitlement on Volume", sb.volume);
                 sb.creditTier = 'free_preview';
                 sb.exportUnlocked = false;
                 sb.paidEntitlementId = undefined;
                 sb.maxChaptersAllowed = 1;
                 (sb as any).isExplicitDowngrade = true;
              }
            }
        }
        console.log('[TRACE 3] Reopening project:', {
          clickedProjectId: id,
          readPath: projectRef.path,
          firestoreRoot: projDoc.exists() ? {
            creditTier: projDoc.data().creditTier,
            exportUnlocked: projDoc.data().exportUnlocked,
            paidEntitlementId: projDoc.data().paidEntitlementId
          } : null,
          finalStoryBible: fullState?.storyBible ? {
            creditTier: fullState.storyBible.creditTier,
            exportUnlocked: fullState.storyBible.exportUnlocked,
            paidEntitlementId: fullState.storyBible.paidEntitlementId
          } : null
        });

      } catch (e) {
        console.error("Firebase load error", e);
      }
    }

    if (!fullState && user) {
      const projectData = localStorage.getItem(`${getProjectPrefix(user.uid)}${id}`);
      if (projectData) {
        try {
          fullState = JSON.parse(projectData);
          const manifestStr = localStorage.getItem(getManifestKey(user.uid));
          if (manifestStr) {
            const manifest: ProjectMetadata[] = JSON.parse(manifestStr);
            const p = manifest.find((p: any) => p.id === id);
            if (p) {
              if ((p as ProjectMetadata & { ownerId?: string }).ownerId && (p as ProjectMetadata & { ownerId?: string }).ownerId !== user.uid && p.userId !== user.uid) {
                console.warn("Blocked loading project from local storage: Ownership mismatch.");
                localStorage.removeItem(`${getProjectPrefix(user.uid)}${id}`);
                return;
              }
              fullState.storyBible = { ...fullState.storyBible, title: p.title || fullState.storyBible.title, genre: p.genre || fullState.storyBible.genre };
              
              // Entitlement Hydration: Always let manifest override stale local/list entitlement fields
              const isPPaid = p.creditTier === 'standard_manuscript' || p.creditTier === 'large_manuscript' || p.creditTier === 'epic_manuscript';
              if (isPPaid) {
                fullState.storyBible.creditTier = p.creditTier as any;
                fullState.storyBible.exportUnlocked = true;
                if (p.maxChaptersAllowed) fullState.storyBible.maxChaptersAllowed = p.maxChaptersAllowed;
                if (p.paidEntitlementId) fullState.storyBible.paidEntitlementId = p.paidEntitlementId;
                if (p.entitlementAppliedAt) fullState.storyBible.entitlementAppliedAt = p.entitlementAppliedAt;
                if (p.rewriteCreditsIncluded !== undefined) fullState.storyBible.rewriteCreditsIncluded = p.rewriteCreditsIncluded;
                if (p.rewritesUsed !== undefined) fullState.storyBible.rewritesUsed = p.rewritesUsed;
              }
            }
          }
        } catch (e) {
          console.error("Error parsing local project state", e);
        }
      }
    }

    if (fullState) {
      if (fullState.storyBible) {
        if (!fullState.storyBible.creditTier) {
          fullState.storyBible.creditTier = 'free_preview';
        }
        if (fullState.storyBible.rewritesUsed === undefined) {
          fullState.storyBible.rewritesUsed = 0;
        }
        if (!fullState.storyBible.canonId) {
          fullState.storyBible.canonId = fullState.storyBible.canonId ?? fullState.storyBible.seriesId ?? (fullState as any).parentCanonId ?? (fullState as any).rootSeriesProjectId ?? id;
        }
      }

      if (fullState.ledger) {
        const canonId = fullState.storyBible?.canonId || id;
        const migrateArray = (arr: any[] | undefined) => {
          if (!arr) return [];
          return arr.map(item => {
            if (typeof item === 'string') {
              return { content: item, projectId: id, canonId, createdAt: new Date().toISOString(), canonStatus: 'active' };
            }
            return item;
          });
        };
        fullState.ledger.inventory = migrateArray(fullState.ledger.inventory);
        fullState.ledger.plot_points = migrateArray(fullState.ledger.plot_points);
        fullState.ledger.resolved_plot_points = migrateArray(fullState.ledger.resolved_plot_points);
        fullState.ledger.historical_events = migrateArray(fullState.ledger.historical_events);

        const migrateObj = (obj: any) => {
          if (!obj) return {};
          Object.keys(obj).forEach(k => {
            if (obj[k] && !obj[k].projectId) {
              obj[k].projectId = id;
              obj[k].canonId = canonId;
              obj[k].createdAt = new Date().toISOString();
              obj[k].canonStatus = 'active';
            }
          });
          return obj;
        };
        fullState.ledger.characters = migrateObj(fullState.ledger.characters);
        fullState.ledger.locations = migrateObj(fullState.ledger.locations);
      }
      setLedger(fullState.ledger);
      setStoryBible(fullState.storyBible);
      setChapters(fullState.chapters || []);
      setLedgerHistory(fullState.ledgerHistory || []);
      setRoadmapVersions(fullState.roadmapVersions || []);
      setCurrentChapterNum(fullState.currentChapterNum || 1);
      setLogs(fullState.logs || []);
      setPausedRecoveryMetadata((fullState as any).pausedRecoveryMetadata || null);
      setCurrentProjectId(id);
      setCorrectionOrder(undefined);
      setFailedDraft(undefined);
      setRetryCount(0);
      
      // Reset any in-progress status to IDLE on load, because the process was lost
      if ([
        AppStatus.PLANNING,
        AppStatus.SCRIBING,
        AppStatus.EDITING,
        AppStatus.PUBLISHING,
        AppStatus.ARCHIVING,
        AppStatus.INSPECTING,
        AppStatus.REWRITING,
        AppStatus.RETRIEVING
      ].includes(projectStatus)) {
        projectStatus = AppStatus.IDLE;
      }
      setStatus(projectStatus);
      addLog('System', `Blueprint loaded. Mode: ${fullState.storyBible?.mode} [Vol: ${fullState.storyBible?.volume}]`, 'success');

      const manifestEntry = projects.find((p) => p.id === id);
      const sourceProjectId = manifestEntry?.sourceProjectId || manifestEntry?.previousVolumeId;
      if (sourceProjectId && (projectStatus === AppStatus.SETUP || (fullState.storyBible?.volume || 1) > 1)) {
        void loadSequelSourceContext(sourceProjectId).then((ctx) => setSequelSourceContext(ctx));
      } else {
        setSequelSourceContext(null);
      }

      // Background migration for legacy chapters without storagePath
      if (user && fullState.chapters) {
        fullState.chapters.forEach(async (c) => {
          if (!c.storagePath && c.content) {
            try {
              const newPath = await migrateFirestoreChapterToStorage(user.uid, id, c);
              if (newPath) {
                setChapters(prev => prev.map(ch => ch.id === c.id ? { ...ch, storagePath: newPath } : ch));
                addLog('System', `Legacy chapter ${c.id} migrated to storage automatically.`, 'info');
              }
            } catch (e) {
              console.error(`Migration failed for chapter ${c.id}:`, e);
            }
          }
        });
      }
    } else {
      addLog('System', "Blueprint data corruption or missing.", 'error');
    }
  }, [addLog, user, projects, loadSequelSourceContext]);

  const handleDeleteProject = useCallback(async (id: string) => {
    if (user) {
      try {
        const idToken = await user.getIdToken();
        const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || (typeof window !== 'undefined' && window.location.origin) || 'http://localhost:3001';
        await fetch(`${API_BASE}/api/projects/delete-cleanup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ projectId: id }),
        });
      } catch (cleanupError) {
        console.warn('[handleDeleteProject] Generation cleanup request failed:', cleanupError);
      }
      deleteDoc(doc(db, 'users', user.uid, 'projects', id)).catch(console.error);
      deleteDoc(doc(db, 'users', user.uid, 'projects', id, 'state', 'current')).catch(console.error);
      localStorage.removeItem(`${getProjectPrefix(user.uid)}${id}`);
    }
    setProjects(prev => {
      const newProjects = prev.filter(p => p.id !== id);
      if (user) localStorage.setItem(getManifestKey(user.uid), JSON.stringify(newProjects));
      return newProjects;
    });
  }, [user]);

  const handleLoadChapter = useCallback(async (chapterId: number) => {
    if (!user || !currentProjectId) return;
    const chapter = chapters.find(c => c.id === chapterId);
    if (!chapter) return;
    
    // Patch 5 fix: Do not use storyBible.chapterCount (which is target count).
    // Use chapters.length or activeProject verified count to see if we should eagerly load it.
    const activeProject = projects.find(p => p.id === currentProjectId);
    const verifiedCount = activeProject ? activeProject.chapterCount : chapters.length;
    
    // If the requested chapterId exceeds our verified completion count and doesn't explicitly have content already
    if (chapterId > verifiedCount && !chapter.content && !chapter.storagePath) {
        addLog('System', `Cannot load Chapter ${chapterId} yet - metadata verification pending or chapter not generated.`, 'info');
        return;
    }
    
    const resolvedPath = resolveChapterStoragePath(chapter);
    
    // Migration logic
    if (!resolvedPath && chapter.content) {
       addLog('System', `Migrating legacy chapter ${chapterId} to Storage...`, 'action');
       try {
           const path = await migrateFirestoreChapterToStorage(user.uid, currentProjectId, chapter);
           if (path) {
             setChapters(prev => prev.map(c => c.id === chapterId ? { ...c, storagePath: path } : c));
             addLog('System', `Chapter ${chapterId} migrated successfully.`, 'success');
           }
       } catch (e) {
           console.error("Migration failed:", e);
       }
       return; 
    }

    if (resolvedPath && !chapter.content) {
      addLog('System', `Loading Chapter ${chapterId} from Storage...`, 'action');
      try {
        const data = await loadChapterFromStorage(resolvedPath);
        const resolvedSummary = resolveChapterSummary({
          storedSummary: (data as any).summary,
          roadmapSummary: storyBible?.chapterPlans?.find((p) => p.chapterNumber === chapterId)?.summary,
          recoverySummary: (chapter as any).recoverySummary,
          existingSummary: chapter.summary,
        });
        if (data && data.content) {
          setChapterLoadErrors((prev) => {
            const next = { ...prev };
            delete next[chapterId];
            return next;
          });
          setChapters(prev => prev.map(c => {
            if (c.id !== chapterId) return c;
            const recoveryMeta = (data as { recoveryMetadata?: unknown }).recoveryMetadata ?? c.recoveryMetadata;
            return {
              ...c,
              content: resolveChapterProseText(data as unknown as Record<string, unknown>),
              summary: resolvedSummary,
              ...(recoveryMeta !== undefined ? { recoveryMetadata: recoveryMeta } : {}),
            };
          }));
        } else {
          const message = 'This chapter exists, but its manuscript file could not be loaded.';
          setChapterLoadErrors((prev) => ({ ...prev, [chapterId]: message }));
          addLog('System', message, 'error');
        }
      } catch (e) {
        const storageFailure = getLastStorageLoadError();
        const message = 'This chapter exists, but its manuscript file could not be loaded.';
        setChapterLoadErrors((prev) => ({ ...prev, [chapterId]: message }));
        console.error(e);
        addLog(
          'System',
          message,
          'error',
          showAgentDebug ? 'developer' : 'user',
          showAgentDebug ? storageFailure?.technicalDetail || storageFailure?.message : undefined,
        );
      }
    }
  }, [user, currentProjectId, chapters, addLog, storyBible, showAgentDebug]);

  const handleInitialize = useCallback(async (seed: StorySeed) => {
    // 8. Prevent duplicate extraction/state write concurrent runs
    if (initializingRef.current) {
      console.warn("[App] handleInitialize already running. Blocking duplicate call.");
      return;
    }
    initializingRef.current = true;
    setIsInitializing(true);
    setLastSeed(seed);
    setExtractionError(null);

    // Diagnostic Log 1: After setup form submit
    console.log("[DIAGNOSTIC LOG 1: After setup form submit]", {
      customRoadmapLength: seed.premadeRoadmap?.length || 0,
      customRoadmapPrefix: seed.premadeRoadmap ? seed.premadeRoadmap.substring(0, 120) : "",
      containsChapterMarkers: !!seed.premadeRoadmap && /^(?:chapter|ch|segment|part|vol|volume|\d+\b|#+)/mi.test(seed.premadeRoadmap),
      hasTitle: !!seed.title,
      genreCount: seed.genre?.length || 0,
      mainConflictLength: seed.premise?.surface?.length || 0,
      deeperTensionLength: seed.premise?.tension?.length || 0,
      permanentChangeLength: seed.premise?.change?.length || 0,
      mainCharactersLength: seed.characters?.length || 0,
      worldLoreLength: seed.worldLore?.length || 0,
      writingStyleLength: seed.style?.length || 0
    });

    addLog('System', `Establishing Narrative Universe: ${seed.title}...`, 'action');
    try {
      // Diagnostic Log 2: Before calling blueprint extraction
      console.log("[DIAGNOSTIC LOG 2: Before calling blueprint extraction]", {
        hasTitle: !!seed.title,
        genreCount: seed.genre?.length || 0,
        mainConflictLength: seed.premise?.surface?.length || 0,
        deeperTensionLength: seed.premise?.tension?.length || 0,
        permanentChangeLength: seed.premise?.change?.length || 0,
        mainCharactersLength: seed.characters?.length || 0,
        worldLoreLength: seed.worldLore?.length || 0,
        writingStyleLength: seed.style?.length || 0,
        customRoadmapLength: seed.premadeRoadmap?.length || 0
      });

      const initialBlueprint = await geminiService.extractStoryBlueprint(seed);

      // Normalization logic for possible AI backend field alias responses
      const normalizeBlueprintReviewData = (raw: any) => {
        let charData = raw.characterCards || raw.characters || raw.cast || raw.characterDossiers || {};
        let factionData = raw.factions || raw.factionCards || raw.factionsAndGroups || raw.groups || {};
        let worldData = raw.worldRules || raw.worldLore || raw.loreCards || raw.rules || raw.locations || {};
        let lockData = raw.canonLocks || raw.hardCanonLocks || raw.locks || [];
        let styleData = raw.writingStyleRules || raw.styleRules || raw.style || {};
        let roadmapData = raw.chapterRoadmap || raw.chapterPlans || raw.roadmap || [];

        // 1. Normalize characters to name-keyed object map
        const charObj: Record<string, any> = {};
        if (Array.isArray(charData)) {
          charData.forEach((c: any) => {
            if (c && typeof c === 'object') {
              const nameKey = c.name || c.id || `Character_${Math.random().toString(36).substr(2, 6)}`;
              charObj[nameKey] = {
                name: nameKey,
                age: c.age || "needs confirmation",
                role: c.role || "needs confirmation",
                faction: c.faction || c.factionOrGroup || "needs confirmation",
                motivation: c.motivation || "needs confirmation",
                fear: c.fear || c.fearOrWound || "needs confirmation",
                relationship: c.relationship || c.relationshipNotes || "needs confirmation",
                location: c.location || c.currentLocation || "needs confirmation",
                abilities: c.abilities || "None",
                limitations: c.limitations || "None",
                canonLocks: ensureArray(c.canonLocks || c.canon_locks),
                forbiddenChanges: ensureArray(c.forbiddenChanges || c.forbidden_changes)
              };
            }
          });
        } else if (charData && typeof charData === 'object') {
          Object.entries(charData).forEach(([key, val]: [string, any]) => {
            if (val && typeof val === 'object') {
              charObj[key] = {
                name: val.name || key,
                age: val.age || "needs confirmation",
                role: val.role || "needs confirmation",
                faction: val.faction || val.factionOrGroup || "needs confirmation",
                motivation: val.motivation || "needs confirmation",
                fear: val.fear || val.fearOrWound || "needs confirmation",
                relationship: val.relationship || val.relationshipNotes || "needs confirmation",
                location: val.location || val.currentLocation || "needs confirmation",
                abilities: val.abilities || "None",
                limitations: val.limitations || "None",
                canonLocks: ensureArray(val.canonLocks || val.canon_locks),
                forbiddenChanges: ensureArray(val.forbiddenChanges || val.forbidden_changes)
              };
            } else if (typeof val === 'string') {
              charObj[key] = {
                name: key,
                age: "needs confirmation",
                role: "needs confirmation",
                faction: "needs confirmation",
                motivation: val,
                fear: "needs confirmation",
                relationship: "needs confirmation",
                location: "needs confirmation",
                abilities: "None",
                limitations: "None",
                canonLocks: [],
                forbiddenChanges: []
              };
            }
          });
        }

        // 2. Normalize factions to name-keyed object map
        const factionObj: Record<string, any> = {};
        if (Array.isArray(factionData)) {
          factionData.forEach((f: any) => {
            if (f && typeof f === 'object') {
              const nameKey = f.name || f.id || `Faction_${Math.random().toString(36).substr(2, 6)}`;
              factionObj[nameKey] = {
                name: nameKey,
                goal: f.goal || "needs confirmation",
                public_belief: f.publicBelief || f.public_belief || "needs confirmation",
                hidden_truth: f.hiddenTruth || f.hidden_truth || "needs confirmation",
                leader: f.leader || "needs confirmation",
                methods: f.methods || "needs confirmation",
                relationship_to_protagonist: f.relationshipToProtagonist || f.relationship_to_protagonist || "needs confirmation",
                canonLocks: ensureArray(f.canonLocks || f.canon_locks)
              };
            }
          });
        } else if (factionData && typeof factionData === 'object') {
          Object.entries(factionData).forEach(([key, val]: [string, any]) => {
            if (val && typeof val === 'object') {
              factionObj[key] = {
                name: val.name || key,
                goal: val.goal || "needs confirmation",
                public_belief: val.public_belief || val.publicBelief || "needs confirmation",
                hidden_truth: val.hidden_truth || val.hiddenTruth || "needs confirmation",
                leader: val.leader || "needs confirmation",
                methods: val.methods || "needs confirmation",
                relationship_to_protagonist: val.relationship_to_protagonist || val.relationshipToProtagonist || "needs confirmation",
                canonLocks: ensureArray(val.canonLocks || val.canon_locks)
              };
            } else if (typeof val === 'string') {
              factionObj[key] = {
                name: key,
                goal: val,
                public_belief: "needs confirmation",
                hidden_truth: "needs confirmation",
                leader: "needs confirmation",
                methods: "needs confirmation",
                relationship_to_protagonist: "needs confirmation",
                canonLocks: []
              };
            }
          });
        }

        // 3. Normalize world rules to name-keyed object map
        const worldObj: Record<string, any> = {};
        if (Array.isArray(worldData)) {
          worldData.forEach((w: any) => {
            if (w && typeof w === 'object') {
              const nameKey = w.name || w.title || w.id || `Rule_${Math.random().toString(36).substr(2, 6)}`;
              worldObj[nameKey] = {
                title: nameKey,
                category: w.category || "General",
                content: w.description || w.content || w.desc || "needs confirmation",
                limits: w.limits || "",
                forbiddenContradictions: ensureArray(w.forbiddenContradictions || w.forbidden_contradictions)
              };
            }
          });
        } else if (worldData && typeof worldData === 'object') {
          Object.entries(worldData).forEach(([key, val]: [string, any]) => {
            if (val && typeof val === 'object') {
              worldObj[key] = {
                title: val.title || val.name || key,
                category: val.category || "General",
                content: val.content || val.description || val.desc || "needs confirmation",
                limits: val.limits || "",
                forbiddenContradictions: ensureArray(val.forbiddenContradictions || val.forbidden_contradictions)
              };
            } else if (typeof val === 'string') {
              worldObj[key] = {
                title: key,
                category: "General",
                content: val,
                limits: "",
                forbiddenContradictions: []
              };
            }
          });
        }

        // 4. Normalize canonLocks safely from strings or arrays using defensive helper
        const locksArr = ensureArray(lockData);

        // 5. Normalize writingStyleRules
        const styleObj = {
          toneAndProse: styleData.toneAndProse || styleData.tone_style || styleData.tone || "",
          dialogueStyle: styleData.dialogueStyle || styleData.dialogue_style || styleData.dialogue || "",
          forbiddenWordsAndPhrases: ensureArray(styleData.forbiddenWordsAndPhrases || styleData.forbidden_words || styleData.forbiddenWords),
          prosePrecisionConstraints: styleData.prosePrecisionConstraints || styleData.constraints || ""
        };

        return {
          characters: charObj,
          factions: factionObj,
          worldRules: worldObj,
          canonLocks: locksArr,
          styleRules: styleObj,
          chapterPlans: roadmapData,
          coreStory: raw.coreStory || {}
        };
      };

      const normalized = normalizeBlueprintReviewData(initialBlueprint);

      // Diagnostic Log: Normalized counts as required
      console.log("[DIAGNOSTIC LOG: Story Blueprint Normalized]", {
        normalizedCharacterCardsCount: Object.keys(normalized.characters).length,
        normalizedFactionsCount: Object.keys(normalized.factions).length,
        normalizedWorldRulesCount: Object.keys(normalized.worldRules).length,
        normalizedCanonLocksCount: normalized.canonLocks.length,
        normalizedFieldNames: Object.keys(normalized)
      });

      // 9. Entity detection heuristic alert for retry option
      const premiseText = `${normalized.coreStory?.mainConflict || ''} ${normalized.coreStory?.deeperTension || ''} ${normalized.coreStory?.permanentChange || ''}`;
      const hasEntitiesHeuristic = /[A-Z][a-z]+/g.test(premiseText);
      if (Object.keys(normalized.characters).length === 0 && hasEntitiesHeuristic) {
        setExtractionError("WARNING: Named characters were detected in the premise DNA, but 0 Character Cards were extracted. This indicates a high probability of extraction failure.");
      } else {
        setExtractionError(null);
      }
      
      // Parse character ages cleanly if present, or mark as "needs confirmation". Also generate hard age canon locks.
      const parsedCharacters: Record<string, any> = {};
      const generatedAgeCanonLocks: string[] = [];

      if (normalized.characters) {
        Object.entries(normalized.characters).forEach(([name, data]: [string, any]) => {
          const rawAge = data.age || "needs confirmation";
          const isNumericAge = !isNaN(Number(rawAge));
          
          parsedCharacters[name] = {
            ...data,
            name: data.name || name,
            age: rawAge,
            role: data.role || "needs confirmation",
            faction: data.faction || "needs confirmation",
            motivation: data.motivation || "needs confirmation",
            fear: data.fear || "needs confirmation",
            relationship: data.relationship || "needs confirmation",
            location: data.location || "needs confirmation",
            canonLocks: ensureArray(data.canonLocks),
            forbiddenChanges: ensureArray(data.forbiddenChanges)
          };

          if (isNumericAge) {
            generatedAgeCanonLocks.push(`${name} must remain ${rawAge} and must not be described as younger or older.`);
          }
        });
      }

      // 7. try/catch block enclosing the construction and state setting/navigation block.
      let newBible: StoryBible;
      let newId = Math.random().toString(36).substr(2, 9);
      try {
        // Build the final locks list from normalized array and parsed hard age locks
        const finalLocksArr = [...normalized.canonLocks, ...generatedAgeCanonLocks];

        let initialChapterPlans = Array.isArray(normalized.chapterPlans) ? normalized.chapterPlans : [];
        let activeRoadmapSource = undefined as StoryBible['activeRoadmapSource'];
        let originalUserRoadmapRaw = seed.premadeRoadmap?.trim() || undefined;
        let originalUserRoadmapParsed = undefined as StoryBible['originalUserRoadmapParsed'];
        let roadmapParseWarnings = undefined as StoryBible['roadmapParseWarnings'];

        if (originalUserRoadmapRaw) {
          logPremadeRoadmapDiagnostic('[PREMADE ROADMAP INPUT]', {
            rawLength: originalUserRoadmapRaw.length,
            parsedChapterCount: 0,
            expectedChapterCount: seed.targetChapters,
            parsedTitles: [],
          });
          const parseResult = parsePremadeRoadmap(originalUserRoadmapRaw, seed.targetChapters);
          logPremadeRoadmapDiagnostic('[PREMADE ROADMAP PARSE]', {
            rawLength: originalUserRoadmapRaw.length,
            parsedChapterCount: parseResult.plans.length,
            expectedChapterCount: seed.targetChapters,
            parsedTitles: parseResult.parsedTitles,
            sourceAfter: parseResult.status === 'failure' ? undefined : 'USER_ROADMAP',
            architectInvoked: false,
            parseFailureReason: parseResult.parseFailureReason,
          });
          if (parseResult.status !== 'failure') {
            initialChapterPlans = tagPlansWithSource(parseResult.plans, 'USER_ROADMAP');
            activeRoadmapSource = 'USER_ROADMAP';
            originalUserRoadmapParsed = initialChapterPlans;
            roadmapParseWarnings = parseResult.warnings;
          } else {
            initialChapterPlans = [];
            roadmapParseWarnings = [parseResult.parseFailureReason || 'Premade roadmap parse failed.'];
          }
        }

                newBible = {
          title: seed.title,
          genre: seed.genre,
          contentBoundaries: seed.contentBoundaries,
          premise: seed.premise,
          style_guide: seed.style,
          mode: seed.mode,
          targetChapterCount: initialChapterPlans.length || seed.targetChapters,
          chapterLength: seed.chapterLength,
          isClosingMode: false,
          volume: 1,
          chapterPlans: initialChapterPlans,
          premadeRoadmap: seed.premadeRoadmap,
          activeRoadmapSource,
          originalUserRoadmapRaw,
          originalUserRoadmapParsed,
          roadmapParseWarnings,
          isBlueprintApproved: false,
          isRoadmapApproved: false,
          styleRules: normalized.styleRules || {},
          canonLocks: finalLocksArr,
          // Arrays explicitly attached to storyBible as well for defensive rendering
          characterCards: Object.values(parsedCharacters),
          factions: Object.values(normalized.factions),
          worldRules: Object.values(normalized.worldRules),
          creditTier: 'free_preview',
          rewritesUsed: 0,
          maxChaptersAllowed: 1,
          exportUnlocked: false,
          continuityEngineVersion: CONTINUITY_ENGINE_VERSION,
          // Blueprint Sufficiency features
          requireBlueprintApproval: ENABLE_BLUEPRINT_SUFFICIENCY,
          blueprintPipelineVersion: BLUEPRINT_PIPELINE_VERSION,
          storyStructure: initialBlueprint.storyStructure || {},
          blueprintSourceSnapshot: {
             originalIdea: seed.premise?.surface || '',
             title: seed.title || '',
             genre: seed.genre?.map(g => g.name).join(', ') || '',
             userCharacterNotes: seed.characters || '',
             userWorldNotes: seed.worldLore || '',
             userConflictNotes: seed.premise?.tension || '',
             userEndingNotes: seed.premise?.change || '',
             customRoadmap: seed.premadeRoadmap || '',
             originalUserFields: {},
             createdAt: new Date().toISOString()
          }
        } as any;

        // 5. Add one final pre-navigation log
        console.log("[DIAGNOSTIC LOG: Pre-navigation state]", {
          finalCharacterCardsIsArray: Array.isArray((newBible as any).characterCards),
          finalFactionsIsArray: Array.isArray((newBible as any).factions),
          finalWorldRulesIsArray: Array.isArray((newBible as any).worldRules),
          finalCanonLocksIsArray: Array.isArray(newBible.canonLocks),
          finalCanonLocksCount: newBible.canonLocks.length
        });
      } catch (err: any) {
        throw new Error(`Failed to construct Story Bible blueprint object: ${err.message}`);
      }

      // Execute React state updates and status transitions
      setLedger({ 
        characters: parsedCharacters, 
        inventory: [],
        locations: normalized.worldRules, 
        current_time: "Day 1",
        plot_points: [],
        resolved_plot_points: [],
        momentum: "Neutral",
        arc_stage: "Beginning",
        last_updated: new Date().toISOString(),
        factions: normalized.factions,
        worldRules: normalized.worldRules
      } as any);
      setStoryBible(newBible);
      setChapters([]);
      setCurrentChapterNum(1);
      setLogs([{
        id: 'init',
        timestamp: new Date().toLocaleTimeString(),
        agent: 'System',
        message: `${seed.mode} Orchestration Engine Offline Status: READY.`,
        type: 'success'
      }]);
      setCurrentProjectId(newId);
      setCorrectionOrder(undefined);
      setFailedDraft(undefined);
      setRetryCount(0);
      
      // 6. Transition to Blueprint Review screen
      setStatus(AppStatus.BLUEPRINT_REVIEW);
      addLog('System', `Story Blueprint Draft constructed. Awaiting review.`, 'success');
    } catch (error: any) {
      console.error("Story Blueprint initialization error:", error);
      setExtractionError(`Story Blueprint Generation Failed: ${error.message}`);
      addLog('System', `Initialization fault: ${error.message}`, 'error');
      setStatus(AppStatus.SETUP);
    } finally {
      setIsInitializing(false);
      initializingRef.current = false;
    }
  }, [addLog]);

  const openRewriteModal = useCallback((chapterId: number) => {
    if (status !== AppStatus.IDLE && status !== AppStatus.COMPLETE) {
      console.warn("[App] Cannot rewrite: system is busy");
      addLog('System', "Manual rewrite blocked: System is currently busy.", 'warning');
      return;
    }
    setRewriteModal({ isOpen: true, chapterId });
    setRewriteInstruction("");
  }, [status, addLog]);

  const confirmRewrite = useCallback(async () => {
    if (!currentProjectId || !user) return;
    
    // Check if project is unlocked
    const isUnlocked = storyBible ? (storyBible.creditTier === 'free_preview' ? false : true) : true;
    const verifiedChapters = storyBible ? storyBible.chapterCount || chapters.length : 0;

    const success = await runGenerationLifecycle({
      projectId: currentProjectId,
      uid: user.uid,
      chapterNum: rewriteModal.chapterId || 1,
      operation: 'rewrite',
      reserveTokens,
      consumeTokenReservation,
      releaseTokenReservation,
      isProjectUnlocked: () => isUnlocked,
      verifiedChapterCount: verifiedChapters,
      addLog,
      setStatus,
      setIsAutoRunning,
      setPaywallModal,
      generationCallback: async () => {

    const chapterId = rewriteModal.chapterId;
    const instruction = rewriteInstruction;
    
    setRewriteModal({ isOpen: false, chapterId: null });
    setRewriteInstruction("");

    if (!chapterId || !instruction) {
      console.log("[App] Rewrite cancelled or invalid");
      return false;
    }

    if (!ledger || !storyBible) {
      console.warn("[App] Cannot rewrite: ledger or storyBible is missing");
      return false;
    }

    const targetChapter = chapters.find(c => c.id === chapterId);
    if (!targetChapter) {
      console.error(`[App] Chapter ${chapterId} not found in state`);
      return false;
    }

    const prevChapter = chapters.find(c => c.id === chapterId - 1);
    const prevSummary = prevChapter ? prevChapter.summary : "Beginning of the story.";
    const plan = storyBible.chapterPlans?.find(p => p?.chapterNumber === chapterId);

    setStatus(AppStatus.REWRITING);
    addLog(AgentRole.REWRITER, `Local rewrite initiated for Chapter ${chapterId}.`, 'action');

    try {
      const currentRewritesUsed = storyBible.rewritesUsed || 0;
      const creditTier = storyBible.creditTier || 'free_preview';
      const includedLimit = creditTier === 'standard_manuscript' ? 5 : creditTier === 'large_manuscript' ? 10 : creditTier === 'epic_manuscript' ? 15 : 0;

      let rewriteCreditsDeduction = 0;
      if (currentRewritesUsed >= includedLimit) {
        // Exceeded included allowance. We need to check if user has rewriteCredits.
        const userRewriteCredits = userData?.rewriteCredits || 0;
        if (userRewriteCredits <= 0) {
          addLog(AgentRole.REWRITER, "Rewrite blocked: included rewrites exhausted and no Rewrite Pack credits available.", 'error');
          setPaywallModal({ isOpen: true, reason: 'rewrite' });
          setStatus(AppStatus.IDLE);
          return false;
        }
        rewriteCreditsDeduction = 1;
      }

      console.log("[App] Calling geminiService.rewriteChapter...");
      const result = await geminiService.rewriteChapter(targetChapter, ledger, instruction, storyBible, prevSummary, plan);
      console.log("[App] Rewrite result received:", result);
      
      if (result.status === 'PASS') {
        const rewrittenChapter = { ...targetChapter, content: result.content || targetChapter.content };
        const nextChapters = chapters.map(c => c.id === chapterId ? rewrittenChapter : c);

        const success = await persistCompletedChapter(
           rewrittenChapter,
           nextChapters,
           ledger,
           currentChapterNum, // Don't advance chapter num on rewrite
           chapterId >= (storyBible.targetChapterCount || 1),
           true
        );

        if (success) {
           const nextRewritesUsed = currentRewritesUsed + 1;
           setStoryBible(prev => prev ? { ...prev, rewritesUsed: nextRewritesUsed } : null);
           
           if (rewriteCreditsDeduction > 0 && userData) {
             const newRewriteCredits = Math.max(0, (userData.rewriteCredits || 0) - rewriteCreditsDeduction);
             await updateUserData({ rewriteCredits: newRewriteCredits });
             addLog(AgentRole.REWRITER, `Deducted 1 credit from Rewrite Pack (Remaining balance: ${newRewriteCredits})`, 'info');
           }

           addLog(AgentRole.REWRITER, `Rewrite successful. Canon Delta: ${result.canon_delta}`, 'success');
           if (result.editor_notes) {
             addLog(AgentRole.EDITOR, `Note: ${result.editor_notes}`, 'info');
           }
        } else {
           addLog(AgentRole.REWRITER, `Rewrite completed but failed to save properly.`, 'error');
        }
      } else {
        console.warn("[App] Rewrite rejected by AI:", result.failure_reason);
        addLog(AgentRole.REWRITER, `Rewrite REJECTED: ${result.failure_reason}`, 'error');
      }
    } catch (error: any) {
      console.error("[App] Rewrite Exception:", error);
      addLog('System', `Rewrite Exception: ${error.message}`, 'error');
    } finally {
      setStatus(AppStatus.IDLE);
    }
  
        return true; // Reached the end successfully
      }
    });

  }, [
    currentProjectId, user, chapters, ledger, storyBible, rewriteModal.chapterId, rewriteInstruction,
    reserveTokens, consumeTokenReservation, releaseTokenReservation,
    addLog, setStatus, setIsAutoRunning, setPaywallModal, runGenerationLifecycle
  ]); 


  const handleExportManuscript = useCallback(() => {
    if (!chapters.length) return;
    
    const title = storyBible?.title || 'Manuscript';
    let textContent = '';
    
    if (storyBible?.volumeTitle || storyBible?.seriesTitle) {
      if (storyBible.volumeTitle) textContent += `${storyBible.volumeTitle}\n`;
      if (storyBible.seriesTitle) textContent += `Book ${storyBible.volumeNumber || storyBible.volume || 1} of ${storyBible.seriesTitle}\n`;
      if (storyBible.authorName) textContent += `By ${storyBible.authorName}\n`;
      textContent += `\n----------------------------------\n\n`;
    } else {
      textContent = `${title.toUpperCase()} [VOLUME ${storyBible?.volume || 1}]\nMODE: ${storyBible?.mode}\n----------------------------------\n\n`;
    }

    (chapters || []).forEach(c => {
      // Safe export source order
      const exportContent = c.editedText ?? (c as any).editedContent ?? c.finalText ?? c.content;
      textContent += `CHAPTER ${c.id}: ${c.title.toUpperCase()}\n\n${exportContent}\n\n[End of Segment]\n\n`;
    });

    if (storyBible) {
      textContent += `\n\n==================================\n`;
      textContent += `STORY BLUEPRINT\n`;
      textContent += `==================================\n\n`;
      textContent += JSON.stringify(storyBible, null, 2);
    }

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(storyBible?.volumeTitle || title).replace(/\s+/g, '_')}_Vol${storyBible?.volumeNumber || storyBible?.volume || 1}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }, [chapters, storyBible]);

  const handleExportProjectState = useCallback(() => {
    if (!ledger || !storyBible) return;
    const fullState: FullProjectState = { ledger, storyBible, chapters, currentChapterNum, logs };
    const blob = new Blob([JSON.stringify(fullState, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${storyBible.title.replace(/\s+/g, '_')}_Archive_Vol${storyBible.volume}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [ledger, storyBible, chapters, currentChapterNum, logs]);

  const runOrchestrationLoop = useCallback(async (isEmergencyReset?: boolean) => {
    if (isEmergencyReset) {
       // emergency reset path
    }

    if (!currentProjectId || !user) return;
    if (generationInFlightRef.current) return;
    if (Date.now() < generationCooldownUntil) return;
    
    const runId = resolveGenerationRunId(pendingGenerationRunIdRef.current);
    pendingGenerationRunIdRef.current = runId;
    generationInFlightRef.current = true;

    try {
    // Check if project is unlocked
    const isUnlocked = bibleRef.current ? (bibleRef.current.creditTier === 'free_preview' ? false : true) : true;
    const verifiedChapters = bibleRef.current ? bibleRef.current.chapterCount || chapters.length : 0;

    const initialProjectData = bibleRef.current && ledgerRef.current ? {
      root: {
        id: currentProjectId,
        title: bibleRef.current.title,
        creditTier: bibleRef.current.creditTier || 'free_preview',
        paidEntitlementId: bibleRef.current.paidEntitlementId ?? null,
        maxChaptersAllowed: bibleRef.current.maxChaptersAllowed ?? 1,
        exportUnlocked: bibleRef.current.exportUnlocked ?? false,
        lastUpdated: new Date().toISOString(),
      },
      state: {
        ledger: ledgerRef.current,
        storyBible: bibleRef.current,
        chapters: chaptersRef.current || [],
        currentChapterNum: currentChapterNum,
      },
    } : undefined;

    const isPhysicalExtractionResume =
      pausedRecoveryMetadataRef.current?.safeResumePoint === 'PHYSICAL_CONTINUITY_EXTRACTION' &&
      pausedRecoveryMetadataRef.current?.failedChapterNumber === currentChapterNum;

    if (isPhysicalExtractionResume && pausedRecoveryMetadataRef.current?.activeRunId) {
      pendingGenerationRunIdRef.current = pausedRecoveryMetadataRef.current.activeRunId;
    }

    const success = await runGenerationLifecycle({
      projectId: currentProjectId,
      uid: user.uid,
      chapterNum: currentChapterNum,
      operation: isPhysicalExtractionResume
        ? 'manuscript_generation'
        : ((!isUnlocked && currentChapterNum === 1 && verifiedChapters === 0) ? 'free_preview' : 'manuscript_generation'),
      runId,
      reuseReservation: isPhysicalExtractionResume && pausedRecoveryMetadataRef.current?.activeRunId
        ? {
            runId: pausedRecoveryMetadataRef.current.activeRunId,
            state: 'AWAITING_PHYSICAL_EXTRACTION',
            pausedAt: pausedRecoveryMetadataRef.current.pausedAt,
            expiresAt: pausedRecoveryMetadataRef.current.expiresAt,
          }
        : undefined,
      reserveTokens,
      consumeTokenReservation,
      releaseTokenReservation,
      isProjectUnlocked: () => isUnlocked,
      verifiedChapterCount: verifiedChapters,
      addLog,
      setStatus,
      setIsAutoRunning,
      setPaywallModal,
      initialProjectData,
      targetChapterCount: bibleRef.current?.targetChapterCount || storyBible?.targetChapterCount || 20,
      manuscriptCreditBalances: userData?.manuscriptCreditBalances,
      refreshUserData,
      clearGenerationUi: () => setActiveProgress(null),
      onReservationBlocked: (error) => {
        const activeProjectId = error.activeRun?.projectId;
        const orphanedActiveRun = !!(
          activeProjectId &&
          activeProjectId !== currentProjectId &&
          !projects.some((p) => p.id === activeProjectId)
        );

        if (orphanedActiveRun && user) {
          void (async () => {
            setGenerationBlockNotice({
              ...error,
              message: 'Chronovela found an interrupted generation from a deleted project and is clearing it.',
              sourceProjectDeleted: true,
            });
            const API_BASE =
              (import.meta as any).env?.VITE_API_BASE_URL ||
              (typeof window !== 'undefined' && window.location.origin) ||
              'http://localhost:3001';
            const idToken = await user.getIdToken();
            const reconciled = await reconcileStaleActiveRun(API_BASE, idToken);
            if (reconciled.cleared) {
              addLog('System', 'Interrupted generation from deleted project cleared. Retrying once.', 'info');
              setGenerationBlockNotice(null);
              pendingGenerationRunIdRef.current = resolveGenerationRunId(pendingGenerationRunIdRef.current);
              void runOrchestrationLoop();
              return;
            }
            setGenerationBlockNotice({
              ...error,
              message: 'Another generation is already running.',
              sourceProjectDeleted: true,
            });
          })();
          return;
        }

        setGenerationBlockNotice(error);
        if (error.retryAfterSeconds) {
          setGenerationCooldownUntil(Date.now() + error.retryAfterSeconds * 1000);
        }
      },
      // generationCallback receives the lifecycle's canonical runId — do NOT create a new lock
      generationCallback: async (currentRunId: string) => {

    clearFourBeatChapterSession('generation_start');

    if (statusRef.current !== AppStatus.IDLE && statusRef.current !== AppStatus.REWRITING) {
      console.log("runOrchestrationLoop aborted: status is", statusRef.current);
      return false;
    }
    if (!ledgerRef.current || !bibleRef.current) {
      console.log("runOrchestrationLoop aborted: ledger or bible is null", { ledger: ledgerRef.current, bible: bibleRef.current });
      return false;
    }

    const generationWorkspace = getManuscriptWorkspaceState(
      chaptersRef.current,
      bibleRef.current.targetChapterCount || 0,
      true,
    );
    if (generationWorkspace.hasInternalGap && generationWorkspace.firstMissingChapterNumber) {
      const gapMessage = formatInternalGapGenerationMessage(generationWorkspace.firstMissingChapterNumber);
      addLog('System', gapMessage, 'error');
      setMissingChapterModalOpen(true);
      setIsAutoRunning(false);
      setStatus(AppStatus.IDLE);
      return false;
    }
    if (!generationWorkspace.canGenerateNextSequentialChapter) {
      if (generationWorkspace.manuscriptIsComplete) {
        addLog('System', 'Manuscript is complete. Review or export instead of generating a new chapter.', 'warning');
      } else if (generationWorkspace.duplicateChapterNumbers.length > 0) {
        addLog('System', 'Duplicate chapter numbers detected. Repair manuscript integrity before generating.', 'error');
        setProjectIntegrityModalOpen(true);
      } else {
        addLog('System', 'No sequential chapter is available for normal generation.', 'warning');
      }
      setIsAutoRunning(false);
      setStatus(AppStatus.IDLE);
      return false;
    }

    // Pre-Scribe Contamination Guard
    const activeCanonId = bibleRef.current.canonId || currentProjectId;
    let hasContamination = false;
    
    const checkArray = (arr: any[] | undefined) => {
      if (!arr) return false;
      for (const item of arr) {
        if (typeof item === 'object' && item.canonId && item.canonId !== activeCanonId) {
          return true;
        }
      }
      return false;
    };
    
    const checkObj = (obj: any) => {
      if (!obj) return false;
      for (const key of Object.keys(obj)) {
        const item = obj[key];
        if (item && typeof item === 'object' && item.canonId && item.canonId !== activeCanonId) {
          return true;
        }
      }
      return false;
    };
    
    if (
      checkArray(ledgerRef.current.inventory) ||
      checkArray(ledgerRef.current.plot_points) ||
      checkArray(ledgerRef.current.resolved_plot_points) ||
      checkArray(ledgerRef.current.historical_events) ||
      checkObj(ledgerRef.current.characters) ||
      checkObj(ledgerRef.current.locations)
    ) {
      hasContamination = true;
    }
    
    if (hasContamination) {
      addLog('System', "Story memory contains records from another project. Repair memory before generating.", 'error');
      setIsAutoRunning(false);
      setStatus(AppStatus.ERROR);
      return false;
    }

    if (currentChapterNum > bibleRef.current.targetChapterCount) {
      addLog('System', "Target chapter count reached. Sequence satisfied.", 'success');
      setIsAutoRunning(false);
      setStatus(AppStatus.COMPLETE);
      return false;
    }

    // Paywall/Entitlement Checks
    const activeProject = projects.find(p => p.id === currentProjectId);
    const verifiedCount = activeProject ? getVerifiedChapterCount(activeProject, chapters) : chapters.length;
    const isUnlocked = isProjectUnlocked(bibleRef.current);

    if (!isUnlocked && verifiedCount >= 1) {
      addLog('System', 'Write Next Chapter locked: Free Preview limit of 1 chapter reached. Please apply a Book Credit.', 'error');
      setPaywallModal({ isOpen: true, reason: 'generation' });
      setIsAutoRunning(false);
      setStatus(AppStatus.IDLE);
      return false;
    }

    // Auth check
    if (!user) {
      addLog('System', 'Authentication required. Please sign in.', 'error');
      setIsAutoRunning(false);
      return false;
    }

    // Sync local state with the lifecycle's runId (lifecycle already wrote to Firestore)
    const ts = new Date().toISOString();
    setProjects(prev => prev.map(p => p.id === currentProjectId ? {
      ...p,
      generationStatus: 'running',
      generationStartedAt: ts,
      activeRunId: currentRunId,
      activeChapterNum: currentChapterNum
    } : p));
    // NOTE: Do NOT write to Firestore here — lifecycle already acquired the lock with currentRunId.

    if (!bibleRef.current.isClosingMode && (bibleRef.current.targetChapterCount - currentChapterNum) <= 1) {
      toggleClosingMode();
    }

    // Paid tier limits check
    if (isUnlocked) {
      const tier = bibleRef.current.creditTier || 'free_preview';
      const limit = tier === 'standard_manuscript' ? 20 : tier === 'large_manuscript' ? 30 : tier === 'epic_manuscript' ? 40 : 1;
      if (currentChapterNum > limit) {
        addLog('System', `Volume chapter limit reached (${limit} segments).`, 'warning');
        setIsAutoRunning(false);
        setStatus(AppStatus.COMPLETE);
        return false;
      }
    }

    let editorRetries = 0;
    let lastShrinkStage: 'Editor' | 'PublicationEditor' | null = null;
    let finalDraft: Chapter | null = null;
    let editorFeedback: string | undefined = undefined;
    const currentCorrectionOrder = correctionRef.current;
    const bible = bibleRef.current;
    const note = directorsNote; // Capture current note
    const currentRetryCount = retryCountRef.current;

    // Timing helper
    const runAgentWithTiming = async <T,>(
      agentRole: AgentRole | 'System',
      agentName: string,
      action: () => Promise<T>
    ): Promise<T> => {
      addLog(agentRole, "Agent timeout configured: 240000ms", 'info', 'developer');
      const startedAt = new Date().toISOString();
      const startMs = Date.now();

      // Initialize the active progress panel
      setActiveProgress({
        chapterNum: currentChapterNum,
        agentName,
        elapsedSecs: 0,
        reassuranceMsg: `Still working — ${agentName} is initializing Chapter ${currentChapterNum}...`,
        requestId: geminiService.getLastRequestId()
      });

      let elapsedSecs = 0;
      const elapsedInterval = setInterval(() => {
        elapsedSecs++;
        setActiveProgress(prev => {
          if (!prev) return null;
          return {
            ...prev,
            elapsedSecs,
            requestId: prev.requestId || geminiService.getLastRequestId()
          };
        });
      }, 1000);

      const heartbeatInterval = setInterval(() => {
        let reassuranceMsg = `Still working — ${agentName} is processing Chapter ${currentChapterNum}.`;
        if (agentName === 'Scribe') {
          reassuranceMsg = "Still writing — the Scribe is drafting the chapter from the current plan and Story Memory.";
        } else if (agentName === 'Inspector') {
          reassuranceMsg = "Still checking continuity — the Inspector is comparing the draft against the chapter plan and Story Memory.";
        } else if (agentName === 'Editor') {
          reassuranceMsg = "Still polishing — the Editor is tightening pacing, style, and readability.";
        } else if (agentName === 'PublicationEditor') {
          reassuranceMsg = "Still finalizing — the Publication Editor is making lore-safe surgical edits. This can take a few minutes on dense chapters.";
        } else if (agentName === 'Archivist') {
          reassuranceMsg = "Still updating Story Memory — the Archivist is recording new canon facts from this chapter.";
        }

        addLog(agentRole, reassuranceMsg, 'info');
        setActiveProgress(prev => {
          if (!prev) return null;
          return {
            ...prev,
            reassuranceMsg,
            requestId: prev.requestId || geminiService.getLastRequestId()
          };
        });
      }, 45000);

      try {
        const result = await action();
        clearInterval(elapsedInterval);
        clearInterval(heartbeatInterval);
        setActiveProgress(null);

        const completedAt = new Date().toISOString();
        const durationMs = Date.now() - startMs;
        const reqId = geminiService.getLastRequestId() || 'N/A';
        console.log(`[Timing] ${agentName} | Chapter ${currentChapterNum} | Started: ${startedAt} | Completed: ${completedAt} | Duration: ${durationMs}ms | Request ID: ${reqId}`);
        addLog(agentRole, `[Timing] ${agentName} completed in ${durationMs}ms | Request ID: ${reqId}`, 'info');
        return result;
      } catch (err: any) {
        clearInterval(elapsedInterval);
        clearInterval(heartbeatInterval);
        setActiveProgress(null);

        const completedAt = new Date().toISOString();
        const durationMs = Date.now() - startMs;
        const reqId = geminiService.getLastRequestId() || 'N/A';
        console.error(`[Timing ERROR] ${agentName} failed after ${durationMs}ms | Request ID: ${reqId} | Error: ${err.message}`);
        throw err;
      }
    };
    try {
      const isRewrite = !!currentCorrectionOrder;

      let softState: SoftStateBrief | null = null;
      if (!isRewrite && currentChapterNum > 1 && chaptersRef.current.length > 0) {
        setStatus(AppStatus.RETRIEVING);
        
        let contextSuccess = false;
        if (USE_DETERMINISTIC_CONTEXT) {
          addLog('System', `Preparing story memory for Chapter ${currentChapterNum}...`, 'action', 'user');
          addLog('System', `Deterministically compiling lookback context for Chapter ${currentChapterNum}...`, 'action', 'developer');
          try {
            const compiled = compileDeterministicContext(
              bible,
              ledgerRef.current!,
              currentChapterNum,
              chaptersRef.current,
              (bible as any)?.seriesCanon
            );
            
            if (compiled && compiled.recentSensoryCarryover && compiled.activeEmotionalResidue) {
              softState = compiled;
              contextSuccess = true;
              addLog('System', `Story memory prepared.`, 'success', 'user');
              addLog('System', `LOOKBACK_AUDIT: Successfully compiled deterministic context for Scribe. Bypassing LLM lookback.`, 'success', 'developer');
            } else {
              addLog('System', `LOOKBACK_AUDIT: Deterministic context returned insufficient data.`, 'warning', 'developer');
            }
          } catch (e: any) {
            console.error("Deterministic lookback compiler error:", e);
            addLog('System', `LOOKBACK_AUDIT: Deterministic compiler failed: ${e.message}`, 'warning', 'developer');
          }
        }

        if (!contextSuccess && FALLBACK_TO_LLM_SOFT_STATE) {
          addLog('System', `Retrieving lookback cues...`, 'action', 'user');
          addLog('System', `LOOKBACK_AUDIT: Falling back to LLM soft state lookback...`, 'action', 'developer');
          try {
            softState = await runAgentWithTiming('System', 'SoftState', () =>
              geminiService.retrieveSoftState(bible, ledgerRef.current!, currentChapterNum, chaptersRef.current)
            );
            if (softState) {
              addLog('System', `Story memory prepared.`, 'success', 'user');
              addLog('System', `LOOKBACK_AUDIT: Retrieved atmospheric cues via fallback LLM.`, 'success', 'developer');
            }
          } catch (e: any) {
            console.error("Soft State LLM fallback retrieval failed:", e);
            addLog('System', `LOOKBACK_AUDIT: Fallback LLM lookback failed. Proceeding without context.`, 'warning', 'developer');
          }
        }
      }

      let authoritativeContinuitySnapshot: AuthoritativeContinuitySnapshot | undefined;
      if (bible.continuityEngineVersion === 'AUTHORITATIVE_MERGED_V2') {
        authoritativeContinuitySnapshot = buildAuthoritativeContinuitySnapshot({
          ledger: ledgerRef.current!,
          storyBible: bible,
          chapterNumber: currentChapterNum,
          plan: bible.chapterPlans?.find((plan) => plan.chapterNumber === currentChapterNum),
          softState,
          previousChapters: chaptersRef.current,
        });
      }

      let shouldGenerateProse = true;
      const metadata = pausedRecoveryMetadataRef.current;
      if (metadata && metadata.failedChapterNumber === currentChapterNum) {
        if (metadata.safeResumePoint === 'PublicationEditor' && metadata.editedDraftAvailable) {
          addLog('System', `Resuming Chapter ${currentChapterNum} from PublicationEditor checkpoint...`, 'action');
          let loadedDraftText: string | null = null;
          if (metadata.checkpointStoragePath) {
            try {
              loadedDraftText = await loadCheckpointFromStorage(metadata.checkpointStoragePath);
            } catch (err: any) {
              console.error("Error restoring draft from checkpoint Storage:", err);
            }
          }
          if (loadedDraftText) {
            finalDraft = {
              id: currentChapterNum,
              title: bible.chapterPlans?.find(p => p.chapterNumber === currentChapterNum)?.title || `Chapter ${currentChapterNum}`,
              content: loadedDraftText,
              summary: 'Restored from checkpoint.'
            };
            shouldGenerateProse = false;
            addLog('System', `Polished checkpoint draft successfully loaded. Bypassing Scribe, Inspector, and Editor.`, 'success');
          } else {
            addLog('System', `Checkpoint draft text could not be loaded from Storage. Falling back to Scribe regeneration.`, 'warning');
          }
        } else if (
          metadata.safeResumePoint === 'Archivist' ||
          metadata.safeResumePoint === 'SaveChapter' ||
          metadata.safeResumePoint === 'PHYSICAL_CONTINUITY_EXTRACTION'
        ) {
          addLog('System', `Resuming Chapter ${currentChapterNum} from ${metadata.safeResumePoint} checkpoint.`, 'action');
          let loadedDraftText: string | null = null;
          if (metadata.checkpointStoragePath) {
            try {
              loadedDraftText = await loadCheckpointFromStorage(metadata.checkpointStoragePath);
            } catch (err: any) {
              console.error("Error restoring final draft from checkpoint Storage:", err);
            }
          }
          if (loadedDraftText) {
            finalDraft = {
              id: currentChapterNum,
              title: bible.chapterPlans?.find(p => p.chapterNumber === currentChapterNum)?.title || `Chapter ${currentChapterNum}`,
              content: loadedDraftText,
              summary: 'Restored polished final draft from checkpoint.'
            };
            shouldGenerateProse = false;
            addLog('System', `Final polished draft successfully loaded from checkpoint storage path. Bypassing Scribe, Inspector, Editor, and PublicationEditor.`, 'success');
          } else {
            addLog('System', `Checkpoint final draft could not be loaded. Regenerating from Scribe.`, 'warning');
          }
        }
      }
      const resolveTargetWordCount = (length?: string): number => {
        if (length === 'MICRO') return 800;
        if (length === 'EXTENDED') return 4000;
        return 2500;
      };

      if (shouldGenerateProse) {
        while (editorRetries <= MAX_EDITOR_RETRIES) {
          // PATCH: Re-read bibleRef on every while-loop iteration so any plan repairs
          // from the previous iteration (or from repairChapterPlanAgainstCanon) are
          // visible to all code in this iteration, including the correction loop.
          const bible = bibleRef.current;

          if (currentRetryCount >= 8) {
            addLog(AgentRole.INSPECTOR, `ULTIMATE DEADLOCK: System unable to resolve chapter after 8 attempts. Auto-run paused for human intervention.`, 'error');
            setIsAutoRunning(false);
            setStatus(AppStatus.IDLE);
            return false;
          }

          let currentDraft: Chapter | null = null;
          const prevSummary = chaptersRef.current.length > 0 ? chaptersRef.current[chaptersRef.current.length-1].summary : "The story begins.";
          
          const targetWordCount = resolveTargetWordCount(bible.chapterLength);
          const TARGET_CHAPTER_WORDS = targetWordCount;
          const chapterWordTargets = resolveChapterWordTargets(bible.chapterLength);
          const MIN_ACCEPTABLE_FINAL_WORDS = chapterWordTargets.minimumAcceptedWords;
          const MIN_EXPANDABLE_DRAFT_WORDS = Math.round(targetWordCount * 0.28);
          const EMPTY_DRAFT_WORD_FLOOR = Math.round(targetWordCount * 0.10);
          const UNDER_TARGET_EXPANSION_THRESHOLD = 0.80;

          const chapterPlanIndex = bible.chapterPlans?.findIndex((p) => p?.chapterNumber === currentChapterNum) ?? -1;
          if (chapterPlanIndex >= 0 && bible.chapterPlans) {
            const contractAssessment = assessChapterContractSufficiency(bible.chapterPlans[chapterPlanIndex]);
            if (!contractAssessment.sufficient) {
              const enriched = enrichPlanWithFourBeatContract(
                bible.chapterPlans[chapterPlanIndex],
                currentChapterNum,
                bible.chapterLength,
              );
              bible.chapterPlans[chapterPlanIndex] = enriched.plan;
              addLog(
                'System',
                `Four-beat chapter contract prepared (${enriched.contract.beats.map((b) => b.function).join(' → ')}).`,
                'warning',
                'developer',
              );
            }
          }

          let scribeRetryCount = 0;
          let scribeOriginalWordCount = 0;
          let scribeRetryTriggered = false;
          let resumedFromRecovery = false;

          if (metadata && metadata.safeResumePoint === 'INSPECTOR_VALIDATION' && metadata.checkpointStoragePath) {
            addLog('System', `Resuming Chapter ${currentChapterNum} validation from inspector-input checkpoint...`, 'action');
            try {
              const loadedCheckpoint = await loadRecoveryCheckpointFromStorage<InspectorInputCheckpoint>(
                metadata.checkpointStoragePath,
              );
              if (loadedCheckpoint?.draft?.content) {
                const resumeHash = hashRecoveryContext(authoritativeContinuitySnapshot);
                const roadmapHash = hashRecoveryContext(bibleRef.current.chapterPlans);
                const resumeValidation = validateInspectorCheckpointResume({
                  checkpoint: loadedCheckpoint,
                  projectId: currentProjectId!,
                  authoritativeSnapshotHash: resumeHash,
                  roadmapHash,
                });
                if (!resumeValidation.ok) {
                  addLog('System', `Validation checkpoint could not be resumed safely (${resumeValidation.reason}).`, 'error');
                } else {
                  currentDraft = loadedCheckpoint.draft;
                  resumedFromRecovery = true;
                  scribeOriginalWordCount = loadedCheckpoint.draft.content.split(/\s+/).filter(Boolean).length;
                  addLog('System', 'Inspector-input checkpoint loaded. Retrying validation without rerunning Scribe.', 'success');
                }
              }
            } catch (err: any) {
              console.error('Error restoring inspector-input checkpoint:', err);
            }
          } else if (metadata && (metadata.safeResumePoint === 'EditorRepair' || metadata.safeResumePoint === 'PublicationRepair') && metadata.checkpointStoragePath) {
             addLog('System', `Resuming Chapter ${currentChapterNum} from length guard recovery checkpoint (${metadata.safeResumePoint})...`, 'action');
             let loadedDraftText: string | null = null;
             try {
               loadedDraftText = await loadCheckpointFromStorage(metadata.checkpointStoragePath);
             } catch (err: any) {
               console.error("Error restoring recovery draft from checkpoint Storage:", err);
             }
             if (loadedDraftText) {
               currentDraft = {
                 id: currentChapterNum,
                 title: bible.chapterPlans?.find(p => p.chapterNumber === currentChapterNum)?.title || `Chapter ${currentChapterNum}`,
                 content: loadedDraftText,
                 summary: 'Restored from length guard recovery checkpoint.'
               };
               resumedFromRecovery = true;
               scribeOriginalWordCount = loadedDraftText.split(/\s+/).filter(Boolean).length;
               addLog('System', `Recovery checkpoint draft successfully loaded. Bypassing Scribe and Inspector.`, 'success');
             }
          }


          // ── Step 0: ChapterPlan Pre-Validation — DISABLED ──────────────────
          // Disabled: repairChapterPlanAgainstCanon burns an extra AI call before
          // every chapter attempt and was causing deadlocks on AI-generated roadmap
          // details treated as hard canon. ChapterPlan is guidance, not locked truth.
          // The Inspector will catch actual canon violations after Scribe drafts.

          // ── Inline Inspector Correction Loop ──
          // Wrap Scribe + Inspector in a bounded correction loop.
          // On Inspector FAIL we retry Scribe with a strong correction order,
          // without exiting generationCallback. Only fall through to the deadlock
          // path after MAX_CORRECTION_ATTEMPTS failures.

          let inlineCorrection: string | undefined = currentCorrectionOrder || undefined;
          let inspectorPassed = false;
          let lastValidation: any = null;
          let fourBeatSession: FourBeatChapterSession | null = null;
          let inspectorCallsUsed = 0;

          const terminateFourBeatInspectorPath = async (
            reason: string,
            userMessage: string,
            recoveryMetadata?: PausedRecoveryMetadata,
          ) => {
            clearFourBeatChapterSession(reason, inspectorCallsUsed);
            fourBeatSession = null;
            if (recoveryMetadata) {
              setPausedRecoveryMetadata(recoveryMetadata);
              await saveCurrentProject(true, { pausedRecoveryMetadata: recoveryMetadata });
            }
            addLog(AgentRole.INSPECTOR, userMessage, 'error', 'user');
            addLog(
              AgentRole.INSPECTOR,
              `[FOUR BEAT TERMINAL BLOCK] reason=${reason} inspectorCallsUsed=${inspectorCallsUsed}`,
              'error',
              'developer',
            );
            if (currentDraft?.content) {
              setFailedDraft(currentDraft.content);
            }
            setStatus(AppStatus.IDLE);
            setIsAutoRunning(false);
            return false;
          };

          const buildInspectorRecoveryMetadata = (
            checkpointPath: string | undefined,
            errorType: string,
            userFacingMessage: string,
            failureCategory: InspectorFailureCategory,
          ): PausedRecoveryMetadata => ({
            failedChapterNumber: currentChapterNum,
            failedAgentName: 'Inspector',
            lastCompletedAgentName: 'Scribe',
            retryAvailable: failureCategory !== 'TERMINAL_CANON_BLOCK',
            safeResumePoint: 'INSPECTOR_VALIDATION',
            draftAvailable: true,
            editedDraftAvailable: false,
            checkpointStoragePath: checkpointPath,
            checkpointCreatedAt: new Date().toISOString(),
            recoveryStatus: 'paused',
            activeRunId: currentRunId,
            projectId: currentProjectId || undefined,
            errorType,
            failureCategory,
            userFacingMessage,
          });
          // Per-attempt telemetry — tracked fresh every corrAttempt
          let attemptScribeWords = 0;
          let attemptExpandedWords = 0;


          for (let corrAttempt = 0; corrAttempt <= MAX_CORRECTION_ATTEMPTS; corrAttempt++) {

            // ── Scribe (or Director on retry ≥6) ──
            // Skip Scribe at corrAttempt=0 if a checkpoint draft was already loaded
            const skipScribe = corrAttempt === 0 && resumedFromRecovery && currentDraft !== null;

            if (!skipScribe) {
            setStatus(isRewrite ? AppStatus.REWRITING : AppStatus.SCRIBING);
            if (corrAttempt > 0) {
              addLog(AgentRole.SCRIBE, `Rewriting chapter with Inspector's correction order (Attempt ${corrAttempt}/${MAX_CORRECTION_ATTEMPTS})...`, 'action', 'user');
              addLog(AgentRole.SCRIBE, `[CORRECTION LOOP] Rewriting chapter after Inspector block (corrAttempt=${corrAttempt})`, 'action', 'developer');
            } else {
              addLog(AgentRole.SCRIBE, `Writing Chapter ${currentChapterNum}...`, 'action', 'user');
              addLog(AgentRole.SCRIBE, `Generating Fragment ${currentChapterNum}/${bible.targetChapterCount} (Attempt ${currentRetryCount + 1}, Scribe Try ${scribeRetryCount + 1})...`, 'action', 'developer');
            }

            if (currentRetryCount >= 6 && corrAttempt === 0) {
              // Director override path (existing logic)
              setStatus(AppStatus.REWRITING);
              addLog(AgentRole.REWRITER, `AI Director is resolving a continuity deadlock...`, 'action', 'user');
              addLog(AgentRole.REWRITER, `AI Director stepping in to manually fix the deadlocked draft (Attempt ${currentRetryCount + 1})...`, 'action', 'developer');
              currentDraft = await runAgentWithTiming(AgentRole.REWRITER, 'Rewriter', () =>
                geminiService.rewriteDeadlockedDraft(
                  failedDraftRef.current || "",
                  inlineCorrection || "",
                  ledgerRef.current,
                  bible.chapterPlans?.find(p => p.chapterNumber === currentChapterNum) || { chapterNumber: currentChapterNum, title: `Chapter ${currentChapterNum}`, summary: 'Advance the plot.', requiredBeats: [], status: 'planned', id: 'fallback', updatedAt: new Date().toISOString() },
                  prevSummary,
                  bible,
                  softState
                )
              );
            } else {
              // PATCH: Use bibleRef.current (not the stale bible snapshot) so plan
              // repairs from repairChapterPlanAgainstCanon are visible to Scribe.
              // On correction retries, inject the previous failed draft as a negative
              // example so Scribe knows what to avoid.
              // Scale effective retryCount by corrAttempt so temperature degrades
              // correctly across correction attempts (mirrors old engine behavior).
              const effectiveRetryCount = currentRetryCount + corrAttempt;
              const scribeFailedDraft = corrAttempt > 0
                ? (currentDraft?.content ?? failedDraftRef.current)
                : failedDraftRef.current;
              let scribeResponse;
              try {
                scribeResponse = await runAgentWithTiming(AgentRole.SCRIBE, 'Scribe', () =>
                  geminiService.generateChapter(
                    bibleRef.current,
                    ledgerRef.current,
                    currentChapterNum,
                    prevSummary,
                    inlineCorrection,
                    editorFeedback,
                    note,
                    effectiveRetryCount,
                    scribeFailedDraft,
                    softState,
                    latestStateDeltaToNarrativeStateDelta(ledgerRef.current?.latestStateDelta),
                    undefined,
                    undefined,
                    {
                      onUserProgress: (message) => addLog('System', message, 'info', 'user'),
                      onDeveloperDiagnostic: (diagnostic) =>
                        addLog('System', `[FOUR BEAT DIAG] ${JSON.stringify(diagnostic)}`, 'info', 'developer'),
                      onFourBeatSession: (session) => {
                        fourBeatSession = session;
                      },
                      fourBeatRunContext: {
                        projectId: currentProjectId!,
                        runId: currentRunId,
                      },
                      authoritativeContinuitySnapshot,
                    },
                  )
                );
              } catch (scribeError: any) {
                if (
                  scribeError instanceof FourBeatStructuralFailureError ||
                  scribeError instanceof FourBeatCallBudgetExhaustedError
                ) {
                  clearFourBeatChapterSession(
                    scribeError instanceof FourBeatCallBudgetExhaustedError
                      ? 'budget_exhausted'
                      : 'structural_failure',
                  );
                  fourBeatSession = null;
                  addLog(
                    'System',
                    'One chapter section could not be completed safely. The chapter was not saved.',
                    'error',
                    'user',
                  );
                  addLog(
                    'System',
                    scribeError instanceof FourBeatCallBudgetExhaustedError
                      ? `[FOUR BEAT CALL BUDGET EXHAUSTED] chapter=${currentChapterNum} reason=${scribeError.reason}`
                      : `[FOUR BEAT BLOCKED] chapter=${currentChapterNum} reason=${scribeError.message}`,
                    'error',
                    'developer',
                  );
                  setIsAutoRunning(false);
                  setStatus(AppStatus.IDLE);
                  return false;
                }
                if (
                  scribeError instanceof ChapterCanonPreflightError ||
                  scribeError?.message?.includes('trim is not a function')
                ) {
                  console.error(`[PREFLIGHT GENERATION FAILURE] ${JSON.stringify({
                    chapterNumber: currentChapterNum,
                    runId: currentRunId,
                    code: scribeError instanceof ChapterCanonPreflightError ? scribeError.code : 'canon_normalization',
                    message: scribeError.message,
                  })}`);
                  addLog(
                    'System',
                    `Chapter ${currentChapterNum} could not start because story memory could not be prepared safely. You can retry after the project reloads.`,
                    'error',
                    'user',
                  );
                  addLog(
                    'System',
                    `[PREFLIGHT GENERATION FAILURE] chapter=${currentChapterNum} reason=${scribeError.message}`,
                    'error',
                    'developer',
                  );
                  clearFourBeatChapterSession('preflight_failure');
                  fourBeatSession = null;
                  setIsAutoRunning(false);
                  setStatus(AppStatus.IDLE);
                  return false;
                }
                throw scribeError;
              }

              const parsedExtraction = geminiService.extractProseDefensively(scribeResponse);
              console.log("[Scribe Prose Extraction Metadata]", parsedExtraction.shapeInfo);
              addLog('System', `Prose shape analyzed.`, 'info', 'user');
              addLog('System', `[Scribe Extract Metadata] ${parsedExtraction.shapeInfo}`, 'info', 'developer');

              currentDraft = {
                ...scribeResponse,
                content: parsedExtraction.text
              };
            }

            const scribeWordCount = (currentDraft.content || '').split(/\s+/).filter(Boolean).length;
            // Per-attempt telemetry: always reset for this corrAttempt
            attemptScribeWords = scribeWordCount;
            if (corrAttempt === 0 && scribeRetryCount === 0) {
              scribeOriginalWordCount = scribeWordCount;
            }

            addLog('System', `Scribe draft analysis complete.`, 'info', 'user');
            addLog('System', `[CORRECTION LOOP] Attempt ${corrAttempt}/${MAX_CORRECTION_ATTEMPTS} — Scribe draft: ${scribeWordCount} words.`, 'info', 'developer');

            // ── Pre-Inspector word count guards ──
            if (scribeWordCount < EMPTY_DRAFT_WORD_FLOOR) {
              addLog('System', `This chapter needs more development before it can be saved.`, 'error', 'user');
              addLog('System', `[GUARD WARNING] Scribe output under empty word floor (${scribeWordCount} words). Required floor: ${EMPTY_DRAFT_WORD_FLOOR} words.`, 'error', 'developer');
              if (scribeRetryCount < 1) {
                scribeRetryCount++;
                scribeRetryTriggered = true;
                addLog('System', `Retrying Chapter ${currentChapterNum} immediately...`, 'warning', 'user');
                addLog('System', `Retrying Scribe attempt 1/1 immediately...`, 'warning', 'developer');
                continue;
              } else {
                addLog('System', `Draft generation failed. Please review setup.`, 'error', 'user');
                addLog('System', `Scribe retry attempts exhausted. Failing safely.`, 'error', 'developer');
                setFailedDraft(currentDraft.content);
                setRetryCount(prev => prev + 1);
                setIsAutoRunning(false);
                setStatus(AppStatus.IDLE);
                return false;
              }
            }

            if (isIncompleteInitialDraft(scribeWordCount, chapterWordTargets)) {
              addLog(
                'System',
                `Draft is substantially under target (${scribeWordCount} words; minimum ${MIN_ACCEPTABLE_FINAL_WORDS}). A major rebuild will be required if validation passes.`,
                'warning',
                'user',
              );
              addLog(
                'System',
                `[GUARD WARNING] Incomplete initial draft (${scribeWordCount} words < ${chapterWordTargets.incompleteDraftThreshold}).`,
                'warning',
                'developer',
              );
            } else if (scribeWordCount < MIN_EXPANDABLE_DRAFT_WORDS) {
              addLog('System', `Chapter draft is shorter than ideal. Targeted expansion may follow validation.`, 'warning', 'user');
              addLog('System', `[GUARD WARNING] Scribe output below expandable floor (${scribeWordCount} words). Required floor: ${MIN_EXPANDABLE_DRAFT_WORDS} words.`, 'warning', 'developer');
              if (scribeRetryCount < 1) {
                scribeRetryCount++;
                scribeRetryTriggered = true;
                addLog('System', `Retrying Chapter ${currentChapterNum} with stronger length targets...`, 'warning', 'user');
                addLog('System', `Retrying Scribe attempt 1/1 with stronger instruction...`, 'warning', 'developer');
                continue;
              } else {
                addLog('System', `Scribe retry attempts exhausted. Proceeding to expansion rescue if validation passes.`, 'warning', 'developer');
              }
            }

            } // end if (!skipScribe)

            // ── Pre-Inspector recovery checkpoint ──
            let inspectorCheckpointPath: string | undefined = metadata?.checkpointStoragePath;
            if (currentDraft?.content && user && currentProjectId && !resumedFromRecovery) {
              const authoritativeSnapshotHash = hashRecoveryContext(authoritativeContinuitySnapshot);
              const roadmapHash = hashRecoveryContext(bibleRef.current.chapterPlans);
              const checkpoint = buildInspectorInputCheckpoint({
                projectId: currentProjectId,
                chapterNumber: currentChapterNum,
                runId: currentRunId,
                draft: currentDraft,
                authoritativeSnapshotHash,
                roadmapHash,
              });
              inspectorCheckpointPath = buildInspectorInputCheckpointPath(
                user.uid,
                currentProjectId,
                currentChapterNum,
              );
              try {
                await saveRecoveryCheckpointToStorage(inspectorCheckpointPath, checkpoint as Record<string, unknown>);
                addLog('System', '[RECOVERY CHECKPOINT] Inspector-input checkpoint saved.', 'info', 'developer');
              } catch (checkpointErr) {
                console.warn('Inspector-input checkpoint save failed:', checkpointErr);
              }
            }

            // ── Inspector (runs before expansion to avoid wasting AI calls on rejected drafts) ──
            setStatus(AppStatus.INSPECTING);
            addLog(AgentRole.INSPECTOR, `Checking continuity...`, 'action', 'user');
            addLog(AgentRole.INSPECTOR, `Analyzing continuity drift...`, 'action', 'developer');
            const chapterPlan = bibleRef.current.chapterPlans?.find((p) => p.chapterNumber === currentChapterNum);
            fourBeatSession = validateFourBeatSessionScope(fourBeatSession, {
              projectId: currentProjectId!,
              chapterNumber: currentChapterNum,
              runId: currentRunId,
            });

            try {
              lastValidation = await runAgentWithTiming(AgentRole.INSPECTOR, 'Inspector', () =>
                geminiService.validateChapter(
                  currentDraft,
                  ledgerRef.current,
                  bibleRef.current,
                  note,
                  currentRetryCount,
                  chapterPlan,
                  authoritativeContinuitySnapshot,
                ),
              );
              inspectorCallsUsed = recordInspectorLogicalCall(fourBeatSession, inspectorCallsUsed);
              lastValidation = promoteRepairableInspectorVerdict(lastValidation, currentDraft, chapterPlan);
            } catch (inspectorCallError: any) {
              if (isInfrastructureInspectorError(inspectorCallError)) {
                const userMsg = buildInspectorFailureUserMessage('RECOVERABLE_INSPECTOR_FAILURE', ['INSPECTOR_INFRASTRUCTURE_FAILURE']);
                return await terminateFourBeatInspectorPath(
                  'recoverable_inspector_failure',
                  userMsg,
                  buildInspectorRecoveryMetadata(
                    inspectorCheckpointPath,
                    'INSPECTOR_INFRASTRUCTURE_FAILURE',
                    userMsg,
                    'RECOVERABLE_INSPECTOR_FAILURE',
                  ),
                );
              }
              throw inspectorCallError;
            }

            let inspectorOrchestration: Awaited<ReturnType<typeof geminiService.runInspectorWithOptionalBeatRepair>> | null = null;
            try {
              inspectorOrchestration = await geminiService.runInspectorWithOptionalBeatRepair({
                chapter: currentDraft!,
                initialValidation: lastValidation,
                session: fourBeatSession,
                inspectorCallsUsed,
                executeBeatRepair: ({ chapter, beatRepairRequest, session }) =>
                  geminiService.executeInspectorConfirmedBeatRepair({
                    chapter,
                    beatRepairRequest,
                    session,
                    storyBible: bibleRef.current,
                    ledger: ledgerRef.current,
                    plan: chapterPlan,
                    softState,
                    authoritativeContinuitySnapshot,
                    onUserProgress: (message) => addLog('System', message, 'info', 'user'),
                  }),
                rerunInspector: (chapter) =>
                  geminiService.validateChapter(
                    chapter,
                    ledgerRef.current,
                    bibleRef.current,
                    note,
                    currentRetryCount,
                    chapterPlan,
                    authoritativeContinuitySnapshot,
                  ).then((result) => promoteRepairableInspectorVerdict(result, chapter, chapterPlan)),
              });
              currentDraft = inspectorOrchestration.chapter;
              lastValidation = inspectorOrchestration.validation;
              inspectorCallsUsed = inspectorOrchestration.inspectorCallsUsed;
              syncInspectorCallCount(fourBeatSession, inspectorCallsUsed);
              inspectorPhysicalStateChangesRef.current = lastValidation.physicalStateChanges || [];
              if (inspectorOrchestration.beatRepairAttempted) {
                addLog(
                  AgentRole.INSPECTOR,
                  `[FOUR BEAT INSPECTOR REPAIR] beatIds=${inspectorOrchestration.repairedBeatIds.join(',') || 'none'} succeeded=${inspectorOrchestration.beatRepairSucceeded}`,
                  inspectorOrchestration.beatRepairSucceeded ? 'success' : 'warning',
                  'developer',
                );
              }
            } catch (inspectorRepairError: any) {
              if (inspectorRepairError instanceof FourBeatCallBudgetExhaustedError) {
                const userMsg = buildInspectorFailureUserMessage('TARGETED_REPAIR', ['TARGETED_REPAIR_BUDGET_ERROR']);
                return await terminateFourBeatInspectorPath(
                  'budget_exhausted',
                  userMsg,
                  buildInspectorRecoveryMetadata(
                    inspectorCheckpointPath,
                    'TARGETED_REPAIR_BUDGET_ERROR',
                    userMsg,
                    'TARGETED_REPAIR_FAILURE',
                  ),
                );
              }
              if (isInfrastructureInspectorError(inspectorRepairError)) {
                const userMsg = buildInspectorFailureUserMessage('RECOVERABLE_INSPECTOR_FAILURE', ['INSPECTOR_INFRASTRUCTURE_FAILURE']);
                return await terminateFourBeatInspectorPath(
                  'recoverable_inspector_failure',
                  userMsg,
                  buildInspectorRecoveryMetadata(
                    inspectorCheckpointPath,
                    'INSPECTOR_INFRASTRUCTURE_FAILURE',
                    userMsg,
                    'RECOVERABLE_INSPECTOR_FAILURE',
                  ),
                );
              }
              throw inspectorRepairError;
            }

            const decision = classifyInspectorDecision({
              validation: lastValidation,
              inspectorCallsUsed,
              repairCallsUsed: inspectorOrchestration?.beatRepairAttempted ? 1 : 0,
            });
            decision.chapterNumber = currentChapterNum;
            logInspectorDecision(decision);

            if (decision.route === 'ACCEPT' && isFourBeatInspectorPass(lastValidation)) {
              inspectorPassed = true;
              break;
            }

            if (decision.route === 'RECOVERABLE_INSPECTOR_FAILURE') {
              const userMsg = buildInspectorFailureUserMessage('RECOVERABLE_INSPECTOR_FAILURE', decision.reasonCodes);
              return await terminateFourBeatInspectorPath(
                'recoverable_inspector_failure',
                userMsg,
                buildInspectorRecoveryMetadata(
                  inspectorCheckpointPath,
                  decision.reasonCodes[0] || 'INSPECTOR_INFRASTRUCTURE_FAILURE',
                  userMsg,
                  'RECOVERABLE_INSPECTOR_FAILURE',
                ),
              );
            }

            if (!shouldUseLegacyInspectorCorrectionLoop({ session: fourBeatSession, chapter: currentDraft })) {
              const terminalReason =
                decision.route === 'TERMINAL_CANON_BLOCK'
                  ? 'inspector_terminal_canon_block'
                  : decision.route === 'TARGETED_REPAIR' && inspectorOrchestration?.beatRepairAttempted && !inspectorOrchestration.beatRepairSucceeded
                    ? 'targeted_repair_failure'
                    : 'inspector_terminal_block';
              const userMsg = buildInspectorFailureUserMessage(
                decision.route === 'TERMINAL_CANON_BLOCK'
                  ? 'TERMINAL_CANON_BLOCK'
                  : decision.route === 'TARGETED_REPAIR'
                    ? 'TARGETED_REPAIR'
                    : 'TERMINAL_CANON_BLOCK',
                decision.reasonCodes,
              );
              const failureCategory = mapInspectorTerminalReasonToFailureCategory(terminalReason, decision.route);
              return await terminateFourBeatInspectorPath(
                terminalReason,
                userMsg,
                buildInspectorRecoveryMetadata(inspectorCheckpointPath, terminalReason, userMsg, failureCategory),
              );
            }

            // ── Legacy Inspector FAIL — full-chapter correction loop (non-four-beat paths only) ──
            // Build a STRONG correction order that forces a full rewrite against canon
            const correctionIssue = lastValidation.reason || 'Continuity error detected';
            const correctionInstruction = lastValidation.correction_order || 'Ensure the narrative advances logically without referencing resolved or impossible elements.';
            const fullFeedback = [
              `=== MANDATORY CORRECTION ORDER (Attempt ${corrAttempt + 1}/${MAX_CORRECTION_ATTEMPTS}) ===`,
              ``,
              `FAILED CLAIMS DETECTED:`,
              correctionIssue,
              ``,
              `REQUIRED CORRECTIONS (apply ALL of these):`,
              correctionInstruction,
              ``,
              `CANON TRUTHS (these cannot be contradicted):`,
              bible.eraState ? `Current era: ${(bible.eraState as any).currentEra || ''} | Current system: ${(bible.eraState as any).currentSystem || ''} | Forbidden: ${((bible.eraState as any).forbiddenEraConfusions || []).join(', ')}` : '',
              ``,
              `FORBIDDEN MOVES FOR THIS REWRITE:`,
              `- Do not preserve any prior draft details that contradict this correction order.`,
              `- Do not reference resolved systems, neutralized entities, or old-era mechanics as currently active.`,
              `- Do not repeat the failed draft's approach.`,
              ``,
              `REQUIRED: Write the chapter fresh, applying every correction above from the start.`
            ].filter(Boolean).join('\n');

            addLog(AgentRole.INSPECTOR, `GATE BLOCKED: ${correctionIssue}`, 'error', 'developer');
            addLog(AgentRole.INSPECTOR, `[CORRECTION LOOP] Attempt ${corrAttempt}/${MAX_CORRECTION_ATTEMPTS} — Inspector: FAIL`, 'error', 'developer');

            if (corrAttempt < MAX_CORRECTION_ATTEMPTS) {
              // Recoverable — retry Scribe with strong correction order
              inlineCorrection = fullFeedback;
              setFailedDraft(currentDraft!.content);
              // PATCH: Persist correction order to React state at every Inspector FAIL,
              // not just at exhaustion. This ensures the outer retry escalation path
              // (retryCount 3/5/6) always carries the latest correction context.
              setCorrectionOrder(fullFeedback);
              addLog(AgentRole.INSPECTOR, `Continuity issue found. Rewriting chapter with the Inspector's correction order.`, 'warning', 'user');
              addLog(AgentRole.INSPECTOR, `[CORRECTION LOOP] Scheduling Scribe retry ${corrAttempt + 1}/${MAX_CORRECTION_ATTEMPTS} with forced correction order.`, 'warning', 'developer');
              continue;
            }

            // ── Exhausted correction attempts — fall through to deadlock escalation ──
            addLog(AgentRole.INSPECTOR, `The chapter could not be repaired automatically. The roadmap may conflict with current canon.`, 'error', 'user');
            addLog(AgentRole.INSPECTOR, `[CORRECTION LOOP] Exhausted ${MAX_CORRECTION_ATTEMPTS} correction attempts. Escalating to deadlock path.`, 'error', 'developer');

            // Mirror existing retry escalation logic
            if (currentRetryCount === 3) {
              addLog(AgentRole.ARCHITECT, `Re-planning narrative arc to avoid continuity drift...`, 'warning', 'user');
              addLog(AgentRole.ARCHITECT, `Deadlock detected. Rewriting Chapter ${currentChapterNum} plan to align with Ledger...`, 'warning', 'developer');
              try {
                const revisedPlan = await runAgentWithTiming(AgentRole.ARCHITECT, 'Architect', () =>
                  geminiService.replanCurrentChapter(
                    // PATCH: use bibleRef.current so deadlock replan also sees the
                    // latest plan state (including any prior repair pass output).
                    bibleRef.current.chapterPlans?.find(p => p.chapterNumber === currentChapterNum) || { chapterNumber: currentChapterNum, title: `Chapter ${currentChapterNum}`, summary: 'Advance the plot.', requiredBeats: [], status: 'planned', id: 'fallback', updatedAt: new Date().toISOString() },
                    ledgerRef.current,
                    lastValidation.reason || "Continuity error",
                    prevSummary
                  )
                );
                setStoryBible(prev => {
                  if (!prev) return prev;
                  const newPlans = [...(prev.chapterPlans || [])];
                  const planIndex = newPlans.findIndex(p => p?.chapterNumber === currentChapterNum);
                  if (planIndex !== -1) {
                    newPlans[planIndex] = revisedPlan;
                  }
                  return { ...prev, chapterPlans: newPlans };
                });
                addLog(AgentRole.ARCHITECT, `Chapter plan revised. Scribe will attempt with new parameters.`, 'success', 'developer');
                setFailedDraft(undefined);
                setCorrectionOrder(undefined);
              } catch (e: any) {
                console.error("Architect replan failed:", e);
                addLog(AgentRole.ARCHITECT, `Failed to rewrite plan: ${e.message || 'Unknown error'}`, 'error', 'developer');
                setCorrectionOrder(fullFeedback);
              }
            } else if (currentRetryCount === 5) {
              addLog(AgentRole.REWRITER, `Scribe failed 6 times. AI Director will take over on the next attempt.`, 'warning', 'developer');
              setCorrectionOrder(fullFeedback);
            } else if (currentRetryCount === 6) {
              addLog(AgentRole.REWRITER, `AI Director failed. Generating a Director's Note for the Scribe's next attempt.`, 'warning', 'developer');
              try {
                const newNote = await runAgentWithTiming(AgentRole.REWRITER, 'RewriterNote', () =>
                  geminiService.generateDirectorsNote(
                    currentDraft!.content || "",
                    lastValidation.reason || "Continuity error",
                    ledgerRef.current,
                    bible.chapterPlans?.find(p => p.chapterNumber === currentChapterNum) || { chapterNumber: currentChapterNum, title: `Chapter ${currentChapterNum}`, summary: 'Advance the plot.', requiredBeats: [], status: 'planned', id: 'fallback', updatedAt: new Date().toISOString() }
                  )
                );
                setDirectorsNote(newNote);
                setFailedDraft(undefined);
                setCorrectionOrder(undefined);
              } catch (e: any) {
                console.error("Director's Note generation failed:", e);
                addLog(AgentRole.REWRITER, `Failed to generate Director's Note: ${e.message || 'Unknown error'}`, 'error', 'developer');
                setCorrectionOrder(fullFeedback);
              }
            } else {
              setCorrectionOrder(fullFeedback);
            }

            if (currentRetryCount !== 3 && currentRetryCount !== 6) {
              setFailedDraft(currentDraft!.content);
            }
            setRetryCount(prev => prev + 1);

            // ── Safe lock cleanup before returning ──
            // Lifecycle's finally block handles Firestore lock cleanup; just update UI here
            addLog('System', `[CORRECTION LOOP] Returning false — lifecycle will clear lock.`, 'info', 'developer');
            setStatus(AppStatus.IDLE);
            setIsAutoRunning(false);
            return false;

          } // end correction loop

          if (!inspectorPassed) {
            // Safety net — should not normally reach here
            setRetryCount(prev => prev + 1);
            setStatus(AppStatus.IDLE);
            setIsAutoRunning(false);
            return false;
          }

          addLog(AgentRole.INSPECTOR, `Continuity check passed.`, 'success', 'user');
          addLog(AgentRole.INSPECTOR, `Progression validated.`, 'success', 'developer');

          // ── Expansion pass (runs AFTER Inspector pass to avoid expanding rejected drafts) ──
          let expansionAttemptCount = 0;
          let currentDraftWordCount = (currentDraft!.content || '').split(/\s+/).filter(Boolean).length;
          let expansionFailed = false;

          while (ENABLE_UNDER_TARGET_EXPANSION && currentDraftWordCount < MIN_ACCEPTABLE_FINAL_WORDS && expansionAttemptCount < 2) {
            expansionAttemptCount++;
            const preExpansionDraft = currentDraft;
            const preExpansionWordCount = currentDraftWordCount;
            
            const activeBible = bibleRef.current;
            const currentPlanForExpansion = activeBible.chapterPlans?.find(p => p?.chapterNumber === currentChapterNum);
            const expansionPlan = buildChapterExpansionPlan(
              preExpansionDraft!.content || '',
              currentPlanForExpansion,
              activeBible.chapterLength,
            );

            addLog(
              'System',
              expansionPlan.deficitStrategy === 'MAJOR'
                ? `Rebuilding under-length chapter (${preExpansionWordCount} words; need ${expansionPlan.requiredAdditionalWords} more)...`
                : `Expanding the chapter with targeted scene insertions (Pass ${expansionAttemptCount}/2)...`,
              'warning',
              'user',
            );
            addLog(
              'System',
              `[EXPANSION START] chapterNum=${currentChapterNum} strategy=${expansionPlan.deficitStrategy} inputWordCount=${preExpansionWordCount} minimumGrowth=${expansionPlan.minimumUsefulGrowth}`,
              'warning',
              'developer',
            );

            const preGateResult = runAcceptanceChecks(currentDraft, currentPlanForExpansion, ledgerRef.current!, currentChapterNum);

            try {
              const expansionResult = await runAgentWithTiming(AgentRole.SCRIBE, 'Scribe', () =>
                geminiService.expandChapter(
                  activeBible,
                  ledgerRef.current!,
                  currentChapterNum,
                  preExpansionDraft!.content || '',
                  preGateResult.warnings,
                  currentPlanForExpansion,
                  softState,
                  expansionPlan,
                  {
                    onUserProgress: (message) => addLog('System', message, 'info', 'user'),
                    onDeveloperDiagnostic: (diagnostic) =>
                      addLog('System', `[SCRIBE DIAG] ${JSON.stringify(diagnostic)}`, 'info', 'developer'),
                  },
                )
              );
              
              const passResult = expansionResult.passResult;
              const expandedDraft = expansionResult.chapter;
              const progressMessage = formatExpansionProgressMessage(passResult);

              addLog(
                'System',
                `[EXPANSION METADATA] ${JSON.stringify({
                  requestedTargetWords: expansionResult.metadata.requestedTargetWords,
                  outputWords: expansionResult.metadata.outputWords,
                  finishReason: expansionResult.metadata.finishReason,
                  outputTokenCount: expansionResult.metadata.outputTokenCount,
                  truncated: expansionResult.metadata.truncated,
                  status: passResult.status,
                  addedWords: passResult.addedWords,
                })}`,
                'info',
                'developer',
              );

              const postExpansion = resolvePostExpansionDraftState(
                preExpansionDraft!,
                expandedDraft,
                passResult,
              );
              currentDraft = postExpansion.draft;
              currentDraftWordCount = postExpansion.wordCount;
              expansionFailed = postExpansion.expansionFailed;

              if (passResult.status === 'SUCCESS') {
                addLog('System', progressMessage, 'success', 'user');
                addLog('System', `Chapter expansion successful.`, 'success', 'user');
                addLog('System', `[EXPANSION SUCCESS] Expanded draft after attempt ${expansionAttemptCount} is now ${currentDraftWordCount} words (+${passResult.addedWords}).`, 'success', 'developer');
              } else if (postExpansion.keptOriginalDraft) {
                addLog('System', progressMessage, 'warning', 'user');
                addLog('System', `[EXPANSION FAIL] Kept original draft (${preExpansionWordCount} words).`, 'warning', 'developer');
              } else {
                addLog('System', progressMessage, 'warning', 'user');
                addLog('System', `[EXPANSION INSUFFICIENT] status=${passResult.status} added=${passResult.addedWords} reason=${passResult.reason || 'n/a'}`, 'warning', 'developer');
              }
            } catch (e: any) {
              console.error("Expansion pass failed:", e);
              addLog('System', `[EXPANSION ERROR] error=${e.message || e}`, 'error', 'developer');
              expansionFailed = true;
              break;
            }
          }

          if (shouldBlockPipelineAfterExpansionFailure(
            currentDraftWordCount,
            MIN_ACCEPTABLE_FINAL_WORDS,
            expansionFailed,
            expansionAttemptCount,
          )) {
             addLog('System', `Expansion failed before the chapter reached minimum length. Retry expansion or rewrite this chapter.`, 'error', 'user');
             addLog('System', `[EXPANSION BLOCKED PIPELINE] draft below minimum (${currentDraftWordCount} < ${MIN_ACCEPTABLE_FINAL_WORDS}); stopping before Editor`, 'error', 'developer');
             setFailedDraft(currentDraft!.content);
             setRetryCount(prev => prev + 1);
             setStatus(AppStatus.IDLE);
             setIsAutoRunning(false);
             return false;
          }

          // ── Acceptance Gate (post-expansion) ──
          try {
            const currentPlanForGate = bibleRef.current.chapterPlans?.find(p => p?.chapterNumber === currentChapterNum);
            const gateResult = runAcceptanceChecks(currentDraft, currentPlanForGate, ledgerRef.current!, currentChapterNum);
            addLog('System', `Reviewing chapter structure...`, 'info', 'user');
            addLog('System', `Acceptance Gate telemetry: ${gateResult.telemetry.wordCount} words, pacing density: ${gateResult.telemetry.pacingDensity}`, 'info', 'developer');
            if (gateResult.warnings.length > 0) {
              gateResult.warnings.forEach(w => addLog('System', `Acceptance Warning: ${w}`, 'warning', 'developer'));
            }
            const shouldBlock = gateResult.isBlocked || (ACCEPTANCE_GATE_BLOCKING_MODE && gateResult.isBlocked);
            if (shouldBlock) {
              addLog('System', `This chapter needs more development before it can be saved.`, 'error', 'user');
              addLog('System', `Acceptance Gate BLOCKED the draft: Empty or invalid draft prose.`, 'error', 'developer');
              setFailedDraft(currentDraft!.content);
              setRetryCount(prev => prev + 1);
              setStatus(AppStatus.IDLE);
              return false;
            }
          } catch (gateErr: any) {
            console.error("Acceptance Gate failed to execute:", gateErr);
            addLog('System', `Acceptance Gate diagnostic error: ${gateErr.message}`, 'warning', 'developer');
          }

          const inspectorApprovedWordCount = currentDraftWordCount;
          addLog('System', `Editor input word count: ${inspectorApprovedWordCount} words.`, 'info', 'developer');

          setStatus(AppStatus.EDITING);
          addLog(AgentRole.EDITOR, `Polishing the chapter...`, 'action', 'user');
          addLog(AgentRole.EDITOR, `Auditing style adherence...`, 'action', 'developer');
          const plan = bible.chapterPlans?.find(p => p?.chapterNumber === currentChapterNum);
          const editorResult: EditorResult = await runAgentWithTiming(AgentRole.EDITOR, 'Editor', () =>
            geminiService.editChapter(currentDraft!, bible, ledgerRef.current!, plan)
          );

          let editorOutputWordCount = (editorResult.polished_text || '').split(/\s+/).filter(Boolean).length;
          let editorShrinkPct = inspectorApprovedWordCount > 0 ? ((inspectorApprovedWordCount - editorOutputWordCount) / inspectorApprovedWordCount) * 100 : 0;
          addLog('System', `Editor output word count: ${editorOutputWordCount} words.`, 'info', 'developer');
          addLog('System', `Editor shrink percentage: ${editorShrinkPct.toFixed(1)}%`, 'info', 'developer');

          // Editor shrink guard: prioritize canon validity (Inspector-approved draft) over hard floor.
          // If Editor shrinks below inspectorApprovedWordCount * 0.90, attempt repair.
          // If repair still fails, fall back to Inspector-approved draft (not hard 2000 floor),
          // since the draft already passed continuity — a shorter valid chapter is better than no chapter.
          const editorShrinkFloor = inspectorApprovedWordCount * 0.90;
          if (editorOutputWordCount < editorShrinkFloor) {
            lastShrinkStage = 'Editor';
            addLog('System', `Editor output rejected: word count shrank below threshold.`, 'warning', 'user');
            addLog('System', `Editor output rejected: ${editorOutputWordCount} words vs input ${inspectorApprovedWordCount} words (floor: ${Math.round(editorShrinkFloor)} words).`, 'warning', 'developer');

            addLog(AgentRole.EDITOR, `Restoring chapter details and depth...`, 'action', 'user');
            addLog(AgentRole.EDITOR, `Triggering Editor repair pass to restore missing details...`, 'action', 'developer');
            try {
              const repairedResult = await runAgentWithTiming(AgentRole.EDITOR, 'Editor', () =>
                geminiService.repairEditChapter(currentDraft!, bible, ledgerRef.current!, plan, currentDraft!.content || '', inspectorApprovedWordCount, 0.90)
              );
              const repairedWordCount = (repairedResult.polished_text || '').split(/\s+/).filter(Boolean).length;
              if (repairedWordCount >= editorShrinkFloor) {
                editorResult.polished_text = repairedResult.polished_text;
                editorResult.status = 'APPROVED';
                editorOutputWordCount = repairedWordCount;
                editorShrinkPct = inspectorApprovedWordCount > 0 ? ((inspectorApprovedWordCount - editorOutputWordCount) / inspectorApprovedWordCount) * 100 : 0;
                addLog('System', `Editor output accepted after length repair pass (${repairedWordCount} words).`, 'success', 'developer');
              } else {
                // Repair still below floor — fall back to Inspector-approved draft (canon-valid)
                editorResult.polished_text = currentDraft!.content;
                editorResult.status = 'APPROVED';
                editorOutputWordCount = inspectorApprovedWordCount;
                editorShrinkPct = 0;
                addLog('System', `Editor repair pass still below threshold (${repairedWordCount} words). Using Inspector-approved draft.`, 'warning', 'developer');
                if (inspectorApprovedWordCount < 2000) {
                  addLog('System', `Chapter passed continuity but remained below target length after repair.`, 'warning', 'user');
                }
              }
            } catch (repairErr: any) {
              console.error("Editor repair pass failed:", repairErr);
              editorResult.polished_text = currentDraft!.content;
              editorResult.status = 'APPROVED';
              editorOutputWordCount = inspectorApprovedWordCount;
              editorShrinkPct = 0;
              addLog('System', `Editor repair pass failed. Using Inspector-approved draft.`, 'warning', 'developer');
            }
          } else {
            addLog('System', `Editor output accepted.`, 'success', 'developer');
          }

          if (editorResult.status === 'APPROVED' || editorRetries === MAX_EDITOR_RETRIES) {
            finalDraft = { ...currentDraft!, content: editorResult.polished_text || currentDraft!.content };
            addLog('System', `Polishing complete.`, 'success', 'user');
            addLog('System', `[TELEMETRY] Editor output polished draft word count: ${(finalDraft.content || '').split(/\s+/).filter(Boolean).length} words.`, 'info', 'developer');
            if (editorResult.status === 'APPROVED') {
               addLog(AgentRole.EDITOR, "Draft approved and polished.", 'success', 'developer');
            }
            break;
          } else {
            addLog(AgentRole.EDITOR, `Polishing retry initiated.`, 'warning', 'user');
            addLog(AgentRole.EDITOR, `Rejecting draft: ${editorResult.feedback || 'General tone mismatch'}`, 'warning', 'developer');
            editorFeedback = editorResult.feedback;
            editorRetries++;
          }
        }
      }

      if (!finalDraft) throw new Error("Narrative production failure.");

      let shouldPublish = true;
      if (metadata && metadata.failedChapterNumber === currentChapterNum) {
        if (metadata.safeResumePoint === 'Archivist' || metadata.safeResumePoint === 'SaveChapter' || metadata.safeResumePoint === 'PHYSICAL_CONTINUITY_EXTRACTION') {
          shouldPublish = false;
        }
      }

      if (shouldPublish) {
        setStatus(AppStatus.PUBLISHING);
        addLog(AgentRole.PUBLICATION_EDITOR, `Finalizing manuscript polish...`, 'action', 'user');
        addLog(AgentRole.PUBLICATION_EDITOR, "Performing surgical edits and lore compliance check...", 'action', 'developer');
        const publishPlan = bible.chapterPlans?.find(p => p?.chapterNumber === currentChapterNum);

        // Save temporary checkpoint draft *before* PublicationEditor (or if it timed out and is retried, we still have the checkpointDraftText!)
        if (finalDraft && finalDraft.content) {
          try {
            const checkpointPath = await saveCheckpointToStorage(user.uid, currentProjectId!, currentChapterNum, finalDraft.content);
            checkpointStoragePathRef.current = checkpointPath;
            console.log(`[STORAGE AUDIT] checkpoint created *before* PublicationEditor at: ${checkpointPath}`);
          } catch (storageErr) {
            console.error("Failed to write temporary checkpoint to Storage:", storageErr);
          }
        }

        let publishResult;
        let pubAttempts = 0;
        const finalDraftRef = finalDraft; // reference helper

        while (pubAttempts < 2) {
          pubAttempts++;
          addLog(AgentRole.PUBLICATION_EDITOR, `Finalizing manuscript polish (Pass ${pubAttempts}/2)...`, 'action', 'user');
          addLog(AgentRole.PUBLICATION_EDITOR, `PublicationEditor Attempt ${pubAttempts} started...`, 'action', 'developer');
          try {
            publishResult = await runAgentWithTiming(AgentRole.PUBLICATION_EDITOR, 'PublicationEditor', () =>
              geminiService.publishChapter(finalDraftRef!, bible, ledgerRef.current, publishPlan)
            );
            break;
          } catch (pubErr: any) {
            const isTransient = pubErr.message?.toLowerCase().includes('timeout') || 
                                pubErr.message?.toLowerCase().includes('timed out') || 
                                pubErr.message?.toLowerCase().includes('fetch') || 
                                pubErr.message?.toLowerCase().includes('network') || 
                                pubErr.status === 429 || 
                                pubErr.status >= 500;

            addLog(AgentRole.PUBLICATION_EDITOR, `Final polish failed: ${pubErr.message || 'Timeout'}`, 'warning', 'user');
            addLog(AgentRole.PUBLICATION_EDITOR, `PublicationEditor Attempt ${pubAttempts} failed/timed out: ${pubErr.message || 'Timeout'}`, 'warning', 'developer');

            if (isTransient && pubAttempts < 2) {
              addLog(AgentRole.PUBLICATION_EDITOR, `PublicationEditor transient failure, retrying safely. Chapter ${currentChapterNum} has not been marked complete.`, 'warning', 'developer');
            } else {
              addLog(AgentRole.PUBLICATION_EDITOR, `Publication Editor failed after retry attempts. Pausing safely at checkpoint.`, 'error', 'developer');
              setPausedRecoveryMetadata(prev => prev ? {
                ...prev,
                pausedAt: new Date().toISOString(),
                errorType: pubErr.name || 'PublicationError',
                userFacingMessage: pubErr.message || 'Timeout'
              } : {
                failedChapterNumber: currentChapterNum,
                failedAgentName: 'PublicationEditor',
                lastCompletedAgentName: 'Editor',
                retryAvailable: true,
                safeResumePoint: 'PublicationEditor',
                draftAvailable: true,
                editedDraftAvailable: true,
                pausedAt: new Date().toISOString(),
                errorType: pubErr.name || 'PublicationError',
                userFacingMessage: pubErr.message || 'Timeout'
              });
              setPublicationError(`Connection was interrupted. Chapter ${currentChapterNum} was not saved or marked complete. Reconnect and resume when ready.`);
              setIsAutoRunning(false);
              setStatus(AppStatus.IDLE);
              setActiveProgress(null);
              return false;
            }
          }
        }

        const editorApprovedWordCount = (finalDraftRef!.content || '').split(/\s+/).filter(Boolean).length;
        addLog('System', `PublicationEditor input word count: ${editorApprovedWordCount} words.`, 'info', 'developer');

        let pubOutputWordCount = (publishResult.patched_content || '').split(/\s+/).filter(Boolean).length;
        let pubShrinkPct = editorApprovedWordCount > 0 ? ((editorApprovedWordCount - pubOutputWordCount) / editorApprovedWordCount) * 100 : 0;
        addLog('System', `PublicationEditor output word count: ${pubOutputWordCount} words.`, 'info', 'developer');
        addLog('System', `PublicationEditor shrink percentage: ${pubShrinkPct.toFixed(1)}%`, 'info', 'developer');

        if (pubOutputWordCount < 2000 || pubOutputWordCount < editorApprovedWordCount * 0.95) {
          lastShrinkStage = 'PublicationEditor';
          addLog('System', `PublicationEditor output rejected: word count shrank below threshold.`, 'warning', 'user');
          addLog('System', `PublicationEditor output rejected: ${pubOutputWordCount} words vs input ${editorApprovedWordCount} words.`, 'warning', 'developer');

          addLog(AgentRole.PUBLICATION_EDITOR, `Restoring manuscript detail and pacing...`, 'action', 'user');
          addLog(AgentRole.PUBLICATION_EDITOR, `Triggering PublicationEditor repair pass to restore missing details...`, 'action', 'developer');
          try {
            const repairedPubResult = await runAgentWithTiming(AgentRole.PUBLICATION_EDITOR, 'PublicationEditor', () =>
              geminiService.repairPublishChapter(finalDraftRef!, bible, ledgerRef.current, publishPlan, finalDraftRef!.content || '', 2000, 0.95)
            );
            const repairedPubWordCount = countWords(repairedPubResult.patched_content || '');
            const repairDecision = evaluatePublicationRepairAcceptance(editorApprovedWordCount, repairedPubWordCount);
            if (repairDecision.accepted) {
              publishResult.patched_content = repairedPubResult.patched_content;
              pubOutputWordCount = repairedPubWordCount;
              pubShrinkPct = editorApprovedWordCount > 0 ? ((editorApprovedWordCount - pubOutputWordCount) / editorApprovedWordCount) * 100 : 0;
              addLog('System', `PublicationEditor output accepted after length repair pass (${repairedPubWordCount} words).`, 'success', 'developer');
            } else {
              if (repairDecision.reason === 'excessive_growth') {
                logPublicationRepairGrowthRejected({
                  inputWords: repairDecision.inputWords,
                  outputWords: repairDecision.outputWords,
                  growthRatio: repairDecision.growthRatio,
                });
                addLog(
                  'System',
                  `PublicationEditor repair rejected: ${repairedPubWordCount} words exceeds ${Math.round(PUBLICATION_REPAIR_MAX_GROWTH_RATIO * 100)}% growth cap (${editorApprovedWordCount} input words).`,
                  'warning',
                  'developer',
                );
              }
              publishResult.patched_content = finalDraftRef!.content;
              pubOutputWordCount = editorApprovedWordCount;
              pubShrinkPct = 0;
              addLog('System', `PublicationEditor repair pass rejected (${repairDecision.reason}). Preserving last accepted Editor draft (${editorApprovedWordCount} words).`, 'warning', 'developer');
            }
          } catch (repairErr: any) {
            console.error("PublicationEditor repair pass failed:", repairErr);
            publishResult.patched_content = finalDraftRef!.content;
            pubOutputWordCount = editorApprovedWordCount;
            pubShrinkPct = 0;
            addLog('System', `PublicationEditor repair pass failed. Skipping PublicationEditor and using original Editor-approved draft to preserve length.`, 'warning', 'developer');
          }
        } else {
          addLog('System', `PublicationEditor output accepted.`, 'success', 'developer');
        }

        finalDraft = { ...finalDraft, content: publishResult.patched_content };
        addLog('System', `Final polish complete.`, 'success', 'user');
        addLog('System', `[TELEMETRY] PublicationEditor output word count: ${(finalDraft.content || '').split(/\s+/).filter(Boolean).length} words.`, 'info', 'developer');
        addLog(AgentRole.PUBLICATION_EDITOR, `Published. ${publishResult.change_log?.length || 0} surgical fixes applied.`, 'success', 'developer');

        (publishResult.lore_compliance || []).forEach(rail => {
          if (rail.status === 'FAIL') {
            addLog(AgentRole.PUBLICATION_EDITOR, `Lore Warning [${rail.rail}]: ${rail.note}`, 'warning', 'developer');
          }
        });

        // Update the storage checkpoint path with the published polished text for safety
        if (finalDraft && finalDraft.content) {
          try {
            const checkpointPath = await saveCheckpointToStorage(user.uid, currentProjectId!, currentChapterNum, finalDraft.content);
            checkpointStoragePathRef.current = checkpointPath;
            console.log(`[STORAGE AUDIT] checkpoint updated *after* PublicationEditor at: ${checkpointPath}`);
          } catch (storageErr) {
            console.error("Failed to update temporary published checkpoint to Storage:", storageErr);
          }
        }
      }

      // ── PRE-ARCHIVIST LENGTH SAFEGUARD ──
      const forbiddenProseCheck = validateForbiddenProseForPersistence(finalDraft!.content, bible);
      if (!forbiddenProseCheck.ok) {
        addLog('System', forbiddenProseCheck.message, 'error', 'user');
        addLog('System', `Forbidden prose persistence block: ${forbiddenProseCheck.matches.map((match) => `${match.kind}:${match.term}`).join(', ')}`, 'error', 'developer');
        setFailedDraft(finalDraft!.content);
        setIsAutoRunning(false);
        setStatus(AppStatus.IDLE);
        return false;
      }

      const finalWordCount = (finalDraft!.content || '').split(/\s+/).filter(Boolean).length;
      const finalTargetWordCount = resolveTargetWordCount(bible.chapterLength);
      const finalMinAcceptable = Math.round(finalTargetWordCount * 0.80);
      addLog('System', `Final pre-Archivist word count: ${finalWordCount} words.`, 'info', 'developer');
      addLog('System', `Final pre-Archivist word count: ${finalWordCount} | Saved chapter word count: ${finalWordCount}`, 'info', 'developer');

      if (finalWordCount < finalMinAcceptable) {
        let resumePoint: 'EditorRepair' | 'PublicationRepair' | 'ManualRewrite' = 'ManualRewrite';
        if (lastShrinkStage === 'Editor') {
          resumePoint = 'EditorRepair';
        } else if (lastShrinkStage === 'PublicationEditor') {
          resumePoint = 'PublicationRepair';
        }

        const shortChapterError = `CRITICAL LENGTH GUARD: Final chapter draft word count (${finalWordCount} words) is below the minimum acceptable limit of ${finalMinAcceptable} words. safeResumePoint: ${resumePoint}. Archivist and Storage commits blocked to prevent saving broken chapter states. Please trigger a manual rewrite or expansion to continue.`;
        addLog('System', `This chapter needs more development before it can be saved.`, 'error', 'user');
        addLog('System', shortChapterError, 'error', 'developer');
        setPausedRecoveryMetadata({
          failedChapterNumber: currentChapterNum,
          failedAgentName: 'Archivist',
          lastCompletedAgentName: 'PublicationEditor',
          retryAvailable: true,
          safeResumePoint: resumePoint as any,
          draftAvailable: true,
          editedDraftAvailable: true,
          pausedAt: new Date().toISOString(),
          errorType: 'WordCountGuardError',
          userFacingMessage: shortChapterError
        } as any);
        setIsAutoRunning(false);
        setStatus(AppStatus.IDLE);
        return false;
      }

      let shouldArchive = true;
      if (metadata && metadata.failedChapterNumber === currentChapterNum) {
        if (metadata.safeResumePoint === 'SaveChapter') {
          shouldArchive = false;
        }
      }

      let updatedLedger = ledgerRef.current;
      if (shouldArchive) {
        setStatus(AppStatus.ARCHIVING);
        addLog(AgentRole.ARCHIVIST, `Updating story memory...`, 'action', 'user');
        addLog(AgentRole.ARCHIVIST, "Synchronizing ledger...", 'action', 'developer');

        // Save history before update
        setLedgerHistory(prev => [...prev, ledgerRef.current!]);
        const finalDraftForLedger = finalDraft!; // reference helper
        const inspectorChangesForExtraction = filterInspectorPhysicalStateChangesForFinalProse(
          (metadata?.safeResumePoint === 'PHYSICAL_CONTINUITY_EXTRACTION'
            ? metadata.inspectorPhysicalStateChanges
            : undefined) || inspectorPhysicalStateChangesRef.current || [],
          finalDraftForLedger.content || '',
          currentChapterNum,
        );
        try {
          const archivistOptions =
            metadata?.safeResumePoint === 'PHYSICAL_CONTINUITY_EXTRACTION' && metadata.archivistRawSnapshot
              ? {
                  physicalExtractionOnly: true,
                  archivistRawSnapshot: metadata.archivistRawSnapshot,
                  inspectorPhysicalStateChanges: inspectorChangesForExtraction,
                }
              : {
                  inspectorPhysicalStateChanges: inspectorChangesForExtraction,
                };
          updatedLedger = await runAgentWithTiming(AgentRole.ARCHIVIST, 'Archivist', () =>
            geminiService.updateLedger(finalDraftForLedger, ledgerRef.current, bible.mode, bible, archivistOptions)
          );

          // Now update checkpoint ref to SaveChapter state
          if (finalDraft && finalDraft.content) {
            try {
              const savePromise = saveCheckpointToStorage(user.uid, currentProjectId!, currentChapterNum, finalDraft.content);
              const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error('Checkpoint upload timed out (60s)')), 60000);
              });
              const checkpointPath = await Promise.race([savePromise, timeoutPromise]);
              checkpointStoragePathRef.current = checkpointPath;
              console.log(`[STORAGE AUDIT] checkpoint updated *after* Archivist at: ${checkpointPath}`);
            } catch (storageErr: any) {
              if (storageErr.message?.includes('timed out') || storageErr.message?.includes('ERR_NAME_NOT_RESOLVED')) {
                console.warn("[STORAGE AUDIT] Transient network failure saving temporary checkpoint. Continuing generation since this is non-critical.", storageErr);
              } else {
                console.error("Failed to update temporary archived checkpoint to Storage:", storageErr);
              }
            }
          }
        } catch (err: any) {
          console.error("Archivist execution failed", err);
          if (err instanceof PhysicalContinuityExtractionError || err?.name === 'PhysicalContinuityExtractionError') {
            logPhysicalContinuityExtractionBlocked(err.details);
            setLedgerHistory((prev) => (prev.length ? prev.slice(0, -1) : prev));
            const userFacingMessage =
              'The chapter was written, but Story Memory could not safely preserve an important physical continuity detail. The chapter was checkpointed and was not committed. Retry the Story Memory update.';
            addLog(AgentRole.ARCHIVIST, userFacingMessage, 'error', 'user');
            addLog(AgentRole.ARCHIVIST, `Physical continuity extraction blocked after bounded correction.`, 'error', 'developer');
            const recoveryMetadata: PausedRecoveryMetadata = {
              failedChapterNumber: currentChapterNum,
              failedAgentName: 'Archivist',
              lastCompletedAgentName: 'PublicationEditor',
              retryAvailable: true,
              safeResumePoint: 'PHYSICAL_CONTINUITY_EXTRACTION',
              draftAvailable: true,
              editedDraftAvailable: true,
              checkpointStoragePath: checkpointStoragePathRef.current,
              archivistRawSnapshot: err.archivistRawSnapshot,
              inspectorPhysicalStateChanges: filterInspectorPhysicalStateChangesForFinalProse(
                inspectorPhysicalStateChangesRef.current || [],
                finalDraft?.content || '',
                currentChapterNum,
              ),
              activeRunId: currentRunId,
              reservationRecoveryState: 'AWAITING_PHYSICAL_EXTRACTION',
              reservationRecoveryRunId: currentRunId,
              pausedAt: new Date().toISOString(),
              expiresAt: buildPhysicalRecoveryExpiresAt(),
              projectId: currentProjectId || undefined,
              errorType: err.code || 'PhysicalContinuityExtractionError',
              userFacingMessage,
            };
            setPausedRecoveryMetadata(recoveryMetadata);
            await saveCurrentProject(true, { pausedRecoveryMetadata: recoveryMetadata });
            setIsAutoRunning(false);
            setStatus(AppStatus.IDLE);
            return { success: false, reservationDisposition: 'retain_awaiting_physical' as const };
          }
          addLog(AgentRole.ARCHIVIST, `Story memory update paused.`, 'error', 'user');
          addLog(AgentRole.ARCHIVIST, `Archivist synchronization failed: ${err.message || err}. Pausing safely.`, 'error', 'developer');
          setPausedRecoveryMetadata(prev => ({
            failedChapterNumber: currentChapterNum,
            failedAgentName: 'Archivist',
            lastCompletedAgentName: 'PublicationEditor',
            retryAvailable: true,
            safeResumePoint: 'Archivist',
            draftAvailable: true,
            editedDraftAvailable: true,
            checkpointStoragePath: prev?.checkpointStoragePath,
            pausedAt: new Date().toISOString(),
            errorType: err.name || 'ArchivistError',
            userFacingMessage: err.message || 'Archivist synchronization failed'
          } as any));
          setIsAutoRunning(false);
          setStatus(AppStatus.IDLE);
          return false;
        }
      }

      // Resolve canonical title using shared service
      const roadmapPlan = bible.chapterPlans?.find(p => p?.chapterNumber === currentChapterNum);
      const resolvedTitle = resolveChapterTitle(finalDraft!, roadmapPlan, currentChapterNum);
      finalDraft!.title = resolvedTitle;
      (finalDraft as any).chapterTitle = resolvedTitle;

      // ── PHASE 1 & 2: DETERMINISTIC CONTINUITY TELEMETRY & ACCEPTANCE GATE ──
      addLog('System', `Reviewing chapter structure...`, 'action', 'user');
      addLog('System', `Executing deterministic continuity acceptance check...`, 'action', 'developer');
      try {
        const acceptanceResult = runAcceptanceChecks(finalDraft!, roadmapPlan, ledgerRef.current!, currentChapterNum);
        
        // Log telemetry metrics cleanly
        console.log(`[Telemetry Audit] Chapter ${currentChapterNum} | Word Count: ${acceptanceResult.telemetry.wordCount} | Pacing Dialogue Density: ${(acceptanceResult.telemetry.pacingDensity * 100).toFixed(1)}% | Scenes: ${acceptanceResult.telemetry.sceneCount}`);
        addLog('System', `Structure check complete.`, 'info', 'user');
        addLog('System', `Prose Telemetry: ${acceptanceResult.telemetry.wordCount} words, ${(acceptanceResult.telemetry.pacingDensity * 100).toFixed(0)}% dialogue density.`, 'info', 'developer');

        // Log warnings to user UI and Advanced Log panel
        if (acceptanceResult.warnings.length > 0) {
          acceptanceResult.warnings.forEach(warnMsg => {
            console.warn(`[Acceptance Warn]`, warnMsg);
            addLog('System', warnMsg, 'warning', 'developer');
          });
        } else {
          addLog('System', `Acceptance check passed. Zero continuity drifts detected.`, 'success', 'developer');
        }

        if (acceptanceResult.isBlocked) {
          throw new Error("Blocking validation failed: prose is empty.");
        }
      } catch (checkErr: any) {
        console.error("Acceptance Check execution failed", checkErr);
        addLog('System', `Structure check interrupted.`, 'error', 'user');
        addLog('System', `Acceptance check halted loop: ${checkErr.message || checkErr}`, 'error', 'developer');
        setIsAutoRunning(false);
        setStatus(AppStatus.IDLE);
        return false;
      }

      // Save via Unified Completion Helper
      addLog('System', `Saving chapter...`, 'action', 'user');
      const nextChapters = [...chapters.filter(c => c.id !== currentChapterNum), finalDraft!].sort((a,b) => a.id - b.id);
      
      const success = await persistCompletedChapter(
         finalDraft!, 
         nextChapters, 
         updatedLedger, 
         currentChapterNum + 1, 
         currentChapterNum >= bible.targetChapterCount, 
         false
      );

      if (!success) {
         // Storage or Firestore failed. We do not advance completion state.
         setIsAutoRunning(false);
         setStatus(AppStatus.IDLE);
         return false;
      }

      // Clear checkpoints and recovery metadata ONLY on successful completion of ALL durable saves!
      const finalMetadata = pausedRecoveryMetadataRef.current;
      if (finalMetadata?.checkpointStoragePath) {
        await deleteCheckpointFromStorage(finalMetadata.checkpointStoragePath).catch(console.warn);
      }
      if (user && currentProjectId) {
        const inspectorInputPath = buildInspectorInputCheckpointPath(user.uid, currentProjectId, currentChapterNum);
        await deleteCheckpointFromStorage(inspectorInputPath).catch(console.warn);
      }
      setPausedRecoveryMetadata(null);
      setCheckpointDraftText(null);
      
      setCurrentChapterNum(prev => {
        const next = prev + 1;
        setRetryCount(0); // Reset retry count for new chapter
        if (next > bible.targetChapterCount) {
           setStatus(AppStatus.COMPLETE);
           setIsAutoRunning(false);
        } else {
           setStatus(AppStatus.IDLE);
        }
        return next;
      });
      setCorrectionOrder(undefined);
      setFailedDraft(undefined);
    } catch (error: any) {
      const isResourceExhausted = 
        error.message?.includes('resource-exhausted') || 
        error.message?.includes('Quota limit exceeded') || 
        error.message?.includes('quota exceeded') ||
        error.code === 'resource-exhausted' ||
        error.name === 'FirebaseError' && error.code?.includes('exhausted');

      if (isResourceExhausted) {
        const quotaMsg = "Firestore daily write quota has been reached. Generation is paused. Your latest checkpoint/chapter files may be saved in Storage, but metadata cannot update until quota resets or the database is upgraded.";
        console.error("[FIRESTORE QUOTA FAILURE]", quotaMsg);
        addLog('System', quotaMsg, 'error');
        setPublicationError(quotaMsg);
        setIsAutoRunning(false);
      } else if (error instanceof RateLimitError || error.name === 'RateLimitError') {
        const secs = error.retryAfterSeconds || 60;
        const msg = `Generation paused because the rate limit was reached. Please wait ${secs} seconds and resume.`;
        addLog('System', msg, 'warning');
        setPublicationError(msg);
      } else {
        const isNetworkOrAuth = 
          error.message?.toLowerCase().includes('auth/network-request-failed') ||
          error.message?.toLowerCase().includes('network-request-failed') ||
          error.message?.toLowerCase().includes('failed to fetch') ||
          error.message?.toLowerCase().includes('network error');
          
        if (isNetworkOrAuth) {
          const msg = `Connection was interrupted. Chapter ${currentChapterNum} was not saved or marked complete. Reconnect and resume when ready.`;
          addLog('System', msg, 'error');
          setPublicationError(msg);
        } else if (error.message?.includes('Generation service temporarily failed')) {
          addLog('System', error.message, 'error');
          setPublicationError(error.message);
        } else {
          addLog('System', `Process Exception: ${error.message}`, 'error');
          setPublicationError(`Process Exception: ${error.message}`);
        }
      }
      setIsAutoRunning(false);
      setStatus(AppStatus.IDLE);
      setActiveProgress(null);
      clearFourBeatChapterSession('generation_failure');
      setTimeout(() => {
        saveCurrentProject(true);
      }, 0);
      return false;
    } finally {
      // Local state sync: lifecycle will update Firestore; we just keep the UI in sync
      setProjects(prev => prev.map(p => p.id === currentProjectId && p.activeRunId === currentRunId ? {
        ...p,
        generationStatus: statusRef.current === AppStatus.COMPLETE ? 'idle' : 'failed',
        activeRunId: null
      } : p));
    }

    clearFourBeatChapterSession('generation_success');
    return true;
      }
    });

    if (success && isAutoRunning) {
       // Loop continues natively in App.tsx via useEffect, but we can also trigger here if needed.
       // The original relied on a useEffect to re-trigger, so we don't strictly need setTimeout here unless we want to.
       // The original had a useEffect that did: setTimeout(runOrchestrationLoop, 2000) when isAutoRunning && status === IDLE.
    }
    } finally {
      generationInFlightRef.current = false;
      if (!isAutoRunning) {
        pendingGenerationRunIdRef.current = null;
      }
    }
  }, [
    currentProjectId, user, currentChapterNum, isAutoRunning, reserveTokens, consumeTokenReservation, releaseTokenReservation,
    addLog, setStatus, setIsAutoRunning, setPaywallModal, runGenerationLifecycle, projects, chapters, generationCooldownUntil
  ]); 


  useEffect(() => {
    let t: any;
    if (isAutoRunning && status === AppStatus.IDLE && !generationInFlightRef.current) {
       t = setTimeout(runOrchestrationLoop, 2000);
    }
    return () => clearTimeout(t);
  }, [isAutoRunning, status, runOrchestrationLoop]);

  const handleCreateNextChapter = useCallback(() => {
    if (generationInFlightRef.current || paywallModalOpenRef.current || Date.now() < generationCooldownUntil) return;
    pendingGenerationRunIdRef.current = resolveGenerationRunId(null);
    void runOrchestrationLoop();
  }, [runOrchestrationLoop, generationCooldownUntil]);

  const handleUpdateLedgerFromNote = useCallback(async () => {
    if (!directorsNote || !ledger) return;
    setStatus(AppStatus.ARCHIVING);
    addLog(AgentRole.ARCHIVIST, "Re-calculating ledger from Director's Note...", 'action');
    try {
      // Save history before update
      setLedgerHistory(prev => [...prev, ledger]);
      const updatedLedger = await geminiService.updateLedgerFromNote(directorsNote, ledger);
      setLedger(updatedLedger);
      addLog(AgentRole.ARCHIVIST, "Ledger synchronized with new direction.", 'success');
    } catch (error: any) {
      addLog('System', `Ledger Update Exception: ${error.message}`, 'error');
    } finally {
      setStatus(AppStatus.IDLE);
    }
  }, [directorsNote, ledger, addLog]);

  const reconcileFinalManuscriptToCanon = async () => {
    try {
      setIsInitializing(true);
      addLog('System', 'Loading final manuscript text to reconcile edits...', 'action');
      
      let fullManuscript = '';
      for (const chap of chapters) {
        let loadedText = chap.content || '';
        try {
          const path = resolveChapterStoragePath(chap);
          if (path) {
            const data = await loadChapterContentFromStorage(path);
            if (data) {
              loadedText = data.editedText || data.finalText || loadedText;
            }
          }
        } catch(e) {}
        fullManuscript += `\\n\\n--- CHAPTER ${chap.chapterNumber || chap.id} ---\\n\\n${loadedText}`;
      }
      
      const fingerprintBytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(fullManuscript));
      const fingerprint = Array.from(new Uint8Array(fingerprintBytes)).map(x => x.toString(16).padStart(2, '0')).join('');

      addLog(AgentRole.ARCHIVIST, 'Reconciling Ledger and Story Blueprint from final edited manuscript...', 'action');
      const { updatedLedger, updatedBible } = await geminiService.reconcileManuscriptToCanon(fullManuscript, ledger!, storyBible!);
      
      const ts = new Date().toISOString();
      const finalBible = { ...updatedBible, hasUnsyncedEdits: false } as StoryBible;
      
      setLedger(updatedLedger);
      setStoryBible(finalBible);
      
      setProjects(prev => prev.map(p => p.id === currentProjectId ? { 
         ...p, 
         hasUnsyncedEdits: false, 
         lastCanonSyncAt: ts, 
         canonSource: 'edited', 
         finalManuscriptFingerprint: fingerprint 
      } : p));
      
      await saveCurrentProject(true, { ledger: updatedLedger, storyBible: finalBible }, false);
      
      if (user && currentProjectId) {
        setDoc(doc(db, 'users', user.uid, 'projects', currentProjectId), {
          hasUnsyncedEdits: false,
          lastCanonSyncAt: ts,
          canonSource: 'edited',
          finalManuscriptFingerprint: fingerprint
        }, { merge: true }).catch(console.error);
      }
      
      addLog('System', 'Story Memory successfully reconciled with final manuscript.', 'success');
      setShowReconciliationPrompt(false);
    } catch(err: any) {
      addLog('System', `Story Memory could not be updated from your edits. Please retry before starting the next volume. Error: ${err.message}`, 'error');
    } finally {
      setIsInitializing(false);
    }
  };

  const handleCreateSequel = useCallback(async () => {
    if (!ledger || !storyBible || !currentProjectId || !user) return;
    if (sequelBusyRef.current) return;

    if (storyBible.hasUnsyncedEdits || projects.find(p => p.id === currentProjectId)?.hasUnsyncedEdits) {
      setShowReconciliationPrompt(true);
      return;
    }

    const ctx = {
      ownerUid: user.uid,
      sourceProjectId: currentProjectId,
      sourceVolume: storyBible.volume || 1,
      seriesId: storyBible.seriesId || currentProjectId,
      ledger,
      storyBible,
      chapters,
    };

    const existing = await querySequelOperationForResume(ctx);
    if (existing && (existing.status === 'UNKNOWN_OUTCOME' || existing.status === 'FAILED' || existing.completedSteps.length > 0)) {
      setSequelOperation(existing);
      setSequelPanelOpen(true);
      addLog('System', 'An in-progress Volume setup was found. Resume to continue without restarting completed steps.', 'warning');
      return;
    }

    sequelBusyRef.current = true;
    setIsInitializing(true);
    setSequelPanelOpen(true);
    setSequelStepMessage('Starting Volume setup…');
    addLog('System', 'Initiating Sequel Workspace Flow...', 'action');

    try {
      await saveCurrentProject(true);
      addLog('System', 'Current volume frozen and preserved.', 'success');

      setSequelSourceContext({
        projectId: currentProjectId,
        volume: storyBible.volume || 1,
        ledger,
        storyBible,
        chapters,
      });

      const result = await runSequelInitialization(ctx, {
        onStep: (message) => setSequelStepMessage(message),
      });

      setSequelOperation(result.operation);
      localStorage.setItem(`${getProjectPrefix(user.uid)}${result.metadata.id}`, JSON.stringify(result.fullState));

      setProjects((prev) => {
        const withoutInitializing = prev.filter((p) => p.id !== result.metadata.id);
        const updatedManifest = [...withoutInitializing, result.metadata];
        localStorage.setItem(getManifestKey(user.uid), JSON.stringify(updatedManifest));
        return updatedManifest;
      });

      setCurrentProjectId(result.metadata.id);
      setLedger(result.fullState.ledger);
      setStoryBible(result.fullState.storyBible);
      setChapters([]);
      setCurrentChapterNum(1);
      setLogs(result.fullState.logs);
      setLedgerHistory([]);
      setStatus(AppStatus.SETUP);
      setSequelPanelOpen(false);
      addLog('System', `Volume ${result.fullState.volume} workspace ready.`, 'success');
    } catch (e: any) {
      if (e instanceof GenerationOutcomeUnknownError) {
        const op = await querySequelOperationForResume(ctx);
        setSequelOperation(op);
        addLog(
          'System',
          'Volume setup paused: the generation service timed out before returning a definitive result. Use Resume — completed steps will not rerun.',
          'warning',
        );
      } else {
        addLog('System', `Failed to create sequel: ${e.message}`, 'error');
        setSequelPanelOpen(false);
      }
    } finally {
      sequelBusyRef.current = false;
      setIsInitializing(false);
      setSequelStepMessage(null);
    }
  }, [ledger, storyBible, currentProjectId, chapters, projects, user, addLog, saveCurrentProject]);

  const handleResumeSequel = useCallback(async () => {
    if (!ledger || !storyBible || !currentProjectId || !user || sequelBusyRef.current) return;

    const ctx = {
      ownerUid: user.uid,
      sourceProjectId: currentProjectId,
      sourceVolume: storyBible.volume || 1,
      seriesId: storyBible.seriesId || currentProjectId,
      ledger,
      storyBible,
      chapters,
    };

    sequelBusyRef.current = true;
    setIsInitializing(true);
    setSequelStepMessage('Resuming Volume setup…');

    try {
      const result = await runSequelInitialization(ctx, {
        onStep: (message) => setSequelStepMessage(message),
      });
      setSequelOperation(result.operation);
      localStorage.setItem(`${getProjectPrefix(user.uid)}${result.metadata.id}`, JSON.stringify(result.fullState));
      setProjects((prev) => {
        const withoutInitializing = prev.filter((p) => p.id !== result.metadata.id);
        const updatedManifest = [...withoutInitializing, result.metadata];
        localStorage.setItem(getManifestKey(user.uid), JSON.stringify(updatedManifest));
        return updatedManifest;
      });
      setCurrentProjectId(result.metadata.id);
      setLedger(result.fullState.ledger);
      setStoryBible(result.fullState.storyBible);
      setChapters([]);
      setCurrentChapterNum(1);
      setLogs(result.fullState.logs);
      setLedgerHistory([]);
      setStatus(AppStatus.SETUP);
      setSequelPanelOpen(false);
      addLog('System', `Volume ${result.fullState.volume} workspace resumed successfully.`, 'success');
    } catch (e: any) {
      if (e instanceof GenerationOutcomeUnknownError) {
        const op = await querySequelOperationForResume(ctx);
        setSequelOperation(op);
        addLog('System', 'Resume paused again with unknown outcome. Query operation state before retrying.', 'warning');
      } else {
        addLog('System', `Sequel resume failed: ${e.message}`, 'error');
      }
    } finally {
      sequelBusyRef.current = false;
      setIsInitializing(false);
      setSequelStepMessage(null);
    }
  }, [ledger, storyBible, currentProjectId, chapters, user, addLog]);

  const handleCancelSequelSetup = useCallback(async () => {
    if (!user || !currentProjectId || !sequelOperation) return;
    await requestSequelCancel(user.uid, currentProjectId, sequelOperation.operationId);
    const refreshed = await querySequelOperationForResume({
      ownerUid: user.uid,
      sourceProjectId: currentProjectId,
      sourceVolume: storyBible?.volume || 1,
      seriesId: storyBible?.seriesId || currentProjectId,
      ledger: ledger!,
      storyBible: storyBible!,
      chapters,
    });
    setSequelOperation(refreshed);
    addLog('System', 'Volume setup cancellation recorded. No project was deleted.', 'warning');
  }, [user, currentProjectId, sequelOperation, storyBible, ledger, chapters, addLog]);

  const handleRoadmapSave = async (newPlans: any[], newTargetCount: number) => {
    if (!currentProjectId || !storyBible) return;
    const newVersion = {
      id: `ver_${Date.now()}`,
      projectId: currentProjectId,
      createdAt: new Date().toISOString(),
      reason: 'manual_edit' as const,
      chapterPlans: storyBible.chapterPlans || []
    };
    setRoadmapVersions(prev => [...prev, newVersion]);
    setStoryBible(prev => prev ? { ...prev, targetChapterCount: newTargetCount, chapterPlans: newPlans } : null);
    addLog('System', 'Roadmap locally updated and saved to project.', 'success');
    saveCurrentProject();
  };


  // --- PRODUCTION DIAGNOSTIC LOGGER ---
  const diagnosticRan = React.useRef(false);
  React.useEffect(() => {
    if (
      chaptersAuthoritative && 
      !diagnosticRan.current && 
      user && 
      isAdmin(user) && 
      import.meta.env.VITE_DEBUG_ENGINE === "true"
    ) {
      diagnosticRan.current = true;
      
      const safeChapters = chapters.map(ch => ({
        chapterNumber: ch.chapterNumber,
        recordId: ch.id,
        idType: typeof ch.id,
        title: ch.title,
        contentLength: ch.content?.length || 0,
        contentSource: ch.contentSource || 'unknown',
        storageContentVerified: ch.storageContentVerified || false,
        storagePathPresent: !!ch.storagePath,
        duplicateOfChapterNumber: null, // Basic stub, advanced duplicate logic can be added if needed
        createdAt: ch.createdAt
      }));
      
      const chapter21 = safeChapters.find(ch => ch.chapterNumber === 21 || ch.recordId === 21);
      
      console.log("[VOLUME 6 PRODUCTION DIAGNOSTIC]", JSON.stringify({
        canonicalState: {
          projectId: currentProjectId,
          targetChapterCount: storyBible?.targetChapterCount || ap?.targetChapterCount || 0,
          trustedCompletedChapterCount: ap?.trustedCompletedChapterCount || null,
          authoritativeCompletedChapterCount: canonicalState.completedChapterCount,
          uniqueChapterNumbers: canonicalState.uniqueChapterNumbers,
          missingChapterNumbers: canonicalState.missingChapterNumbers,
          duplicateChapterNumbers: canonicalState.duplicateChapterNumbers,
          overflowChapterNumbers: [], // Add if needed
          chaptersAuthoritative,
          isBookComplete: canonicalState.isBookComplete,
          hasOverflowChapters: canonicalState.hasOverflowChapters
        },
        phantomChapter21: chapter21 || null,
        allChaptersSummary: safeChapters
      }, null, 2));
    }
  }, [chaptersAuthoritative, chapters, currentProjectId, storyBible, ap, canonicalState, user]);
  // --- END DIAGNOSTIC LOGGER ---

  const handleRegenerateRemainingRoadmap = async (fromIndex: number, currentPlans: any[], targetChapterCount: number) => {
    if (!ledger || !storyBible || !currentProjectId) return;
    setIsRoadmapEditorOpen(false);
    setStatus(AppStatus.PLANNING);
    addLog(AgentRole.ARCHITECT, `Regenerating roadmap from Chapter ${fromIndex + 1}...`, 'action');
    const newVersion = {
      id: `ver_${Date.now()}`,
      projectId: currentProjectId,
      createdAt: new Date().toISOString(),
      reason: 'ai_regeneration' as const,
      chapterPlans: storyBible.chapterPlans || []
    };
    setRoadmapVersions(prev => [...prev, newVersion]);
    
    try {
      const { plans: newPlans, warnings: regenWarnings } = await geminiService.regenerateRemainingRoadmap(storyBible, ledger, currentPlans, fromIndex, targetChapterCount, (msg: string) => addLog(AgentRole.ARCHITECT, msg, 'action'));
      setStoryBible(prev => prev ? { ...prev, targetChapterCount: targetChapterCount, chapterPlans: newPlans } : null);
      addLog(AgentRole.ARCHITECT, 'Future roadmap successfully regenerated across the existing foundation.', 'success');
      saveCurrentProject();
    } catch (e: any) {
      addLog('System', `Roadmap Regeneration Failed: ${e.message}`, 'error');
    } finally {
      setStatus(AppStatus.IDLE);
    }
  };

  const handleRegenerateSingleChapter = async (index: number, currentPlans: any[]) => {
    if (!ledger || !storyBible || !currentProjectId) return;
    setIsRoadmapEditorOpen(false);
    setStatus(AppStatus.PLANNING);
    addLog(AgentRole.ARCHITECT, `Regenerating single chapter plan for Chapter ${index + 1}...`, 'action');
    const newVersion = {
      id: `ver_${Date.now()}`,
      projectId: currentProjectId,
      createdAt: new Date().toISOString(),
      reason: 'ai_regeneration' as const,
      chapterPlans: storyBible.chapterPlans || []
    };
    setRoadmapVersions(prev => [...prev, newVersion]);
    
    try {
      const newPlan = await geminiService.regenerateSingleChapterPlan(storyBible, ledger, currentPlans, index);
      const newPlans = [...currentPlans];
      newPlans[index] = newPlan;
      setStoryBible(prev => prev ? { ...prev, chapterPlans: newPlans } : null);
      addLog(AgentRole.ARCHITECT, `Chapter ${index + 1} plan regenerated.`, 'success');
      saveCurrentProject();
    } catch (e: any) {
      addLog('System', `Roadmap Chapter Regeneration Failed: ${e.message}`, 'error');
    } finally {
      setStatus(AppStatus.IDLE);
    }
  };

  const handleRollback = useCallback(() => {
    if (ledgerHistory.length === 0) return;
    
    const previousLedger = ledgerHistory[ledgerHistory.length - 1];
    setLedger(previousLedger);
    setLedgerHistory(prev => prev.slice(0, -1));
    addLog('System', "Lore-Lock: Ledger rolled back to previous state.", 'warning');
  }, [ledgerHistory, addLog]);

  const needsLegalAcceptance = user && userData && (!userData.legalAccepted || userData.legalAcceptedVersion !== CURRENT_LEGAL_VERSION);

  if (needsLegalAcceptance) {
    return (
      <div className="min-h-screen bg-cv-page text-cv-primary flex flex-col overflow-hidden relative">
         <LegalGate onAccept={updateUserData} currentLegalVersion={CURRENT_LEGAL_VERSION} />
      </div>
    );
  }

  function renderPaywallModal() {
    return (
      <PaywallModal
        isOpen={paywallModal.isOpen}
        reason={paywallModal.reason}
        highlightedTier={paywallModal.highlightedTier}
        headline={paywallModal.headline}
        subheadline={paywallModal.subheadline}
        onClose={() => setPaywallModal((prev) => ({ ...prev, isOpen: false }))}
        user={user}
        userData={userData}
        updateUserData={updateUserData}
        refreshUserData={refreshUserData}
        applyManuscriptCreditToProject={applyManuscriptCreditToProject}
        storyBible={storyBible}
        setStoryBible={setStoryBible}
        currentProjectId={currentProjectId}
        addLog={addLog}
        saveCurrentProject={saveCurrentProject}
        setStatus={setStatus}
        setIsAutoRunning={setIsAutoRunning}
      />
    );
  }

  const dashboardProjects = filterDashboardProjects(filterUserProjects(projects, user?.uid));

  if (status === AppStatus.DASHBOARD) return (
    <>
      <ProjectDashboard 
        projects={dashboardProjects}
        projectsLoadState={projectsLoadState}
        projectsLoadError={projectsLoadError}
        onRetryProjectsLoad={() => setProjectsLoadAttempt((attempt) => attempt + 1)}
        onSelect={handleSelectProject}
        onDelete={handleDeleteProject}
        onNew={() => { setStoryBible(null); setLedger(null); setStatus(AppStatus.SETUP); }}
        onUpgradePlan={() => setPaywallModal({ isOpen: true, reason: 'generation' })}
      />
      {renderPaywallModal()}
    </>
  );
  if (status === AppStatus.SETUP) {
    if (storyBible && currentProjectId) {
      return (
        <>
          <SequelSetup 
            initialBible={storyBible} 
            initialLedger={ledger!}
            sourceContext={sequelSourceContext}
            onLoadSource={loadSequelSourceContext}
            onSaveDraft={(updatedBible, updatedLedger) => {
              setStoryBible(updatedBible);
              setLedger(updatedLedger);
              void saveCurrentProject(true, { storyBible: updatedBible, ledger: updatedLedger }, false);
            }}
            onRegenerateProposal={async (draftBible, plan) => {
              const addendum = buildSequelPlanningPromptAddendum(plan, null);
              const proposed = await geminiService.regenerateSequelAiProposal(draftBible, addendum);
              return {
                ...plan,
                workflowStatus: 'PROPOSAL_GENERATED',
                aiProposal: {
                  surface: { value: proposed.surface, source: 'ai', status: 'pending' },
                  tension: { value: proposed.tension, source: 'ai', status: 'pending' },
                  change: { value: proposed.change, source: 'ai', status: 'pending' },
                  approved: false,
                  declinedExplicitly: false,
                },
              };
            }}
            onStart={async (updatedBible, updatedLedger) => {
              setStoryBible(updatedBible);
              setLedger(updatedLedger);
              setStatus(AppStatus.PLANNING);
              addLog(AgentRole.ARCHITECT, `Synthesizing Narrative Roadmap for Volume ${updatedBible.volume}...`, 'action');
              try {
                const { plans, warnings, planningState } = await geminiService.planChapters(
                  updatedBible,
                  updatedLedger,
                  (msg: string) => addLog(AgentRole.ARCHITECT, msg, 'action'),
                  {
                    existingPlanningState: updatedBible.roadmapPlanning,
                    onPlanningStateChange: async (state) => {
                      const bibleWithPlanning = { ...updatedBible, roadmapPlanning: state };
                      setStoryBible((prev) => (prev ? { ...prev, roadmapPlanning: state } : prev));
                      await saveCurrentProject(true, { storyBible: bibleWithPlanning }, false);
                    },
                  },
                );
                const finalizedBible = {
                  ...updatedBible,
                  chapterPlans: plans,
                  roadmapWarnings: warnings,
                  roadmapPlanning: planningState,
                };
                setStoryBible((prev) => (prev ? { ...prev, chapterPlans: plans, roadmapWarnings: warnings, roadmapPlanning: planningState } : null));
                addLog(AgentRole.ARCHITECT, `Roadmap finalized. ${plans.length} chapters mapped for Volume ${updatedBible.volume}.`, 'success');
                saveCurrentProject(true, { storyBible: finalizedBible }, false);
                setStatus(AppStatus.IDLE);
              } catch (error: unknown) {
                addLog('System', `Planning fault: ${toUserFacingRoadmapError(error)}`, 'error');
                setStatus(AppStatus.IDLE);
              }
            }} 
            onCancel={() => setStatus(AppStatus.DASHBOARD)} 
          />
          {renderPaywallModal()}
        </>
      );
    }
    return (
      <>
        <StorySetup onInitialize={handleInitialize} isLoading={isInitializing} hasSavedGame={false} onResume={() => setStatus(AppStatus.DASHBOARD)} />
        {renderPaywallModal()}
      </>
    );
  }

  if (status === AppStatus.BLUEPRINT_REVIEW) {
    return (
      <>
        <BlueprintReview 
          storyBible={storyBible!}
          ledger={ledger!}
          onUpdate={(updatedBible, updatedLedger) => {
            const syncedBible = { ...updatedBible };
            if (updatedLedger.characters) {
              (syncedBible as any).characterCards = Object.values(updatedLedger.characters);
            }
            if ((updatedLedger as any).factions) {
              (syncedBible as any).factions = Object.values((updatedLedger as any).factions);
            }
            if ((updatedLedger as any).worldRules) {
              (syncedBible as any).worldRules = Object.values((updatedLedger as any).worldRules);
            }
            setStoryBible(syncedBible);
            setLedger(updatedLedger);
          }}
          onApproveBlueprint={() => {
            setStoryBible(prev => prev ? { ...prev, isBlueprintApproved: !prev.isBlueprintApproved } : null);
            addLog('System', storyBible?.isBlueprintApproved ? 'Story Blueprint unapproved.' : 'Story Blueprint approved.', 'warning');
          }}
          onApproveRoadmap={() => {
            const currentlyApproved = storyBible?.isRoadmapApproved;
            setStoryBible(prev => {
              if (!prev) return null;
              const updatedPlans = (prev.chapterPlans || []).map(p => {
                if (!p) return p;
                const resolvedVal = p.title || (p as any).chapterTitle || (p as any).name || (p as any).heading || `Chapter ${p.chapterNumber}`;
                return {
                  ...p,
                  title: resolvedVal,
                  chapterTitle: resolvedVal
                };
              });
              return {
                ...prev,
                chapterPlans: updatedPlans,
                isRoadmapApproved: !prev.isRoadmapApproved
              };
            });
            addLog('System', currentlyApproved ? 'Chapter Roadmap unapproved.' : 'Chapter Roadmap approved.', 'warning');
          }}
          onEnterNarrativeHub={handleEnterNarrativeHub}
          isEnteringHub={isEnteringHub}
          roadmapPersistError={roadmapPersistError}
          onGenerateRoadmap={async () => {
            setIsInitializing(true);
            const isRegenerate = Boolean(storyBible?.chapterPlans?.length);
            const nextSource = sourceForArchitectInvocation(storyBible?.activeRoadmapSource, isRegenerate);
            logPremadeRoadmapDiagnostic('[ROADMAP SOURCE CHANGE]', {
              rawLength: storyBible?.originalUserRoadmapRaw?.length || storyBible?.premadeRoadmap?.length || 0,
              parsedChapterCount: storyBible?.chapterPlans?.length || 0,
              expectedChapterCount: storyBible?.targetChapterCount,
              parsedTitles: (storyBible?.chapterPlans || []).map((plan) => plan.title),
              sourceBefore: storyBible?.activeRoadmapSource,
              sourceAfter: nextSource,
              architectInvoked: true,
            });
            addLog(AgentRole.ARCHITECT, `Synthesizing Narrative Roadmap for ${storyBible?.targetChapterCount} Chapters...`, 'action');
            try {
              const { plans, warnings, planningState } = await geminiService.planChapters(
                storyBible!,
                ledger!,
                (msg: string) => addLog(AgentRole.ARCHITECT, msg, 'action'),
                {
                  existingPlanningState: storyBible?.roadmapPlanning,
                  onPlanningStateChange: async (state) => {
                    const bibleWithPlanning = { ...(storyBible as StoryBible), roadmapPlanning: state };
                    setStoryBible((prev) => (prev ? { ...prev, roadmapPlanning: state } : prev));
                    await saveCurrentProject(true, { storyBible: bibleWithPlanning }, false);
                  },
                },
              );
              const taggedPlans = tagPlansWithSource(plans, nextSource);
              setStoryBible((prev) =>
                prev
                  ? {
                      ...prev,
                      chapterPlans: taggedPlans,
                      roadmapWarnings: warnings,
                      roadmapPlanning: planningState,
                      activeRoadmapSource: nextSource,
                      originalUserRoadmapRaw: prev.originalUserRoadmapRaw ?? prev.premadeRoadmap,
                      originalUserRoadmapParsed: prev.originalUserRoadmapParsed,
                      isRoadmapApproved: false,
                    }
                  : null,
              );
              addLog(AgentRole.ARCHITECT, `Roadmap finalized. ${plans.length} chapters mapped.`, 'success');
            } catch (error: unknown) {
              addLog('System', `Planning fault: ${toUserFacingRoadmapError(error)}`, 'error');
            } finally {
              setIsInitializing(false);
            }
          }}
          onBackToSetup={() => setStatus(AppStatus.SETUP)}
          isGeneratingRoadmap={isInitializing}
          extractionError={extractionError}
          onRetryExtraction={lastSeed ? () => handleInitialize(lastSeed) : undefined}
        />
        {renderPaywallModal()}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-cv-page text-cv-primary flex flex-col overflow-hidden pb-24 md:pb-0">
      {status === AppStatus.COMPLETE && (
        <div className="fixed inset-0 z-[100] bg-cv-page/90 backdrop-blur-xl flex items-center justify-center p-6">
          <div className="max-w-xl w-full bg-cv-panel border border-cv-interactive/30 rounded-3xl p-10 text-center shadow-[0_0_100px_rgba(59,130,246,0.2)] animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 bg-cv-success/20 text-cv-gold rounded-full flex items-center justify-center mx-auto mb-6 border border-cv-interactive/50 shadow-inner">
               <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-3xl font-black text-white uppercase tracking-tighter mb-2">Volume {storyBible?.volume} Satisfied</h2>
            <p className="text-cv-muted font-mono text-xs uppercase tracking-widest mb-8">Narrative cycle complete. Ready for sequential evolution?</p>
            
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setStatus(AppStatus.IDLE)}
                className="py-4 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary font-black text-xs uppercase tracking-widest rounded-xl transition-all border border-cv-border-strong"
              >
                Stay & Review
              </button>
              <button 
                onClick={handleCreateSequel}
                disabled={isInitializing}
                className="py-4 bg-cv-success hover:bg-[#2EB67C] text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-xl shadow-black/40 flex items-center justify-center gap-3"
              >
                {isInitializing ? 'Synthesizing...' : `Begin Volume ${storyBible ? storyBible.volume + 1 : 'Next'}`}
              </button>
            </div>
            <button 
              onClick={() => { saveCurrentProject(); setStatus(AppStatus.DASHBOARD); }}
              className="mt-6 text-[10px] text-cv-muted font-black uppercase hover:text-white transition-colors"
            >
              Back to Library
            </button>
          </div>
        </div>
      )}

      <header className="bg-cv-panel/80 backdrop-blur-md border-b border-cv-border p-4 flex justify-between items-center shadow-2xl z-20">
        <div className="flex items-center gap-6">
          <button onClick={() => { saveCurrentProject(); setIsAutoRunning(false); setStatus(AppStatus.DASHBOARD); }} className="p-2 text-cv-muted hover:text-white bg-cv-elevated rounded-lg transition-colors border border-cv-border-strong">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          </button>
          <div className="flex flex-col">
            <h1 className="text-xl font-black text-white flex items-center gap-2 tracking-tighter">
              <span className="text-cv-interactive hidden md:inline">Chronovela</span> {storyBible?.title}
              <span className="text-[10px] text-cv-gold bg-cv-interactive/15 px-2 py-0.5 rounded border border-cv-interactive/30 ml-2">VOL {storyBible?.volume}</span>
            </h1>
            <div className="flex items-center gap-2">
               <span className="text-[9px] font-mono font-black text-cv-gold bg-cv-interactive/15 px-1.5 py-0.5 rounded border border-cv-border-strong uppercase tracking-widest">{storyBible?.mode}</span>
               <span className="text-[9px] font-mono font-bold text-cv-muted uppercase">{completedChapters === null ? "..." : completedChapters} / {storyBible?.targetChapterCount} SEGMENTS</span>
               <button
                 type="button"
                 onClick={() => setProjectIntegrityModalOpen(true)}
                 className={`text-[9px] font-black uppercase tracking-widest ml-1 ${
                   integrityIssueLabel.startsWith('Integrity issue')
                     ? 'text-cv-warning/90 hover:text-cv-warning underline decoration-amber-500/40'
                     : 'text-cv-muted hover:text-cv-secondary'
                 }`}
               >
                 {integrityIssueLabel}
               </button>
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2">
          {isComplete ? (
            <>
              {/* PRIMARY ACTION: Export Manuscript */}
              <button 
                onClick={() => {
                  if (!isProjectUnlocked(storyBible)) {
                    setPaywallModal({ isOpen: true, reason: 'export' });
                  } else {
                    setIsPublicationModalOpen(true);
                  }
                }} 
                disabled={chapters.length === 0} 
                className="flex items-center gap-2 px-5 py-1.5 text-xs font-black rounded bg-cv-success border border-cv-success/40 text-white hover:bg-[#2EB67C] shadow-lg shadow-black/30 transition-all disabled:opacity-30"
              >
                <ExportIcon /> EXPORT MANUSCRIPT
              </button>

              <button 
                onClick={() => setIsManuscriptEditorOpen(true)}
                disabled={chapters.length === 0} 
                className="flex items-center gap-2 px-5 py-1.5 text-xs font-black rounded bg-cv-elevated border border-cv-border-strong text-white hover:bg-cv-elevated shadow-lg shadow-black/20 transition-all disabled:opacity-30"
              >
                REVIEW MANUSCRIPT
              </button>

              {/* SECONDARY ACTION: Start Volume X */}
              <button 
                onClick={() => {
                  const stdBalance = userData?.standardCredits || 0;
                  const lngBalance = userData?.longCredits || 0;
                  const epcBalance = userData?.epicCredits || 0;
                  if (stdBalance <= 0 && lngBalance <= 0 && epcBalance <= 0) {
                    setPaywallModal({ isOpen: true, reason: 'sequel' });
                  } else {
                    handleCreateSequel();
                  }
                }} 
                disabled={isInitializing || sequelPanelOpen}
                className="flex items-center gap-2 px-4 py-1.5 text-xs font-black rounded bg-cv-elevated border border-cv-border-strong text-cv-primary hover:bg-cv-elevated transition-all"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                {isInitializing ? 'SYNTHESIZING...' : `START VOLUME ${storyBible ? storyBible.volume + 1 : 'NEXT'}`}
              </button>
            </>
          ) : (
            <>
              {/* Write Next Chapter or Unlock or Repair */}
              <div className="flex flex-col items-center gap-2">
                {generationBlockNotice && (
                  <div className="max-w-md rounded-lg border border-amber-500/40 bg-amber-950/30 px-3 py-2 text-[11px] text-amber-100 text-center">
                    <p>{generationBlockNotice.message}</p>
                    {generationBlockNotice.sourceProjectDeleted ? (
                      <p className="mt-1 text-amber-200/80">This lock came from a deleted project, not your current book.</p>
                    ) : null}
                    {generationBlockNotice.retryAfterSeconds ? (
                      <p className="mt-1 text-amber-200/80">Retry after {generationBlockNotice.retryAfterSeconds}s.</p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => setGenerationBlockNotice(null)}
                        className="px-2 py-1 rounded bg-cv-elevated text-cv-secondary text-[10px] uppercase tracking-wide"
                      >
                        Dismiss
                      </button>
                      {generationBlockNotice.activeRun?.projectId &&
                      projects.some((p) => p.id === generationBlockNotice.activeRun?.projectId) ? (
                        <button
                          type="button"
                          onClick={() => {
                            const targetId = generationBlockNotice.activeRun!.projectId;
                            setGenerationBlockNotice(null);
                            void handleSelectProject(targetId);
                          }}
                          className="px-2 py-1 rounded bg-cv-elevated text-cv-secondary text-[10px] uppercase tracking-wide"
                        >
                          Open Active Project
                        </button>
                      ) : null}
                      {generationBlockNotice.activeRunId ? (
                        <button
                          type="button"
                          onClick={() =>
                            addLog(
                              'System',
                              `Active run ${generationBlockNotice.activeRunId} (${generationBlockNotice.activeRun?.operation || 'unknown'})`,
                              'info',
                              'developer',
                            )
                          }
                          className="px-2 py-1 rounded bg-cv-elevated text-cv-secondary text-[10px] uppercase tracking-wide"
                        >
                          View Generation Status
                        </button>
                      ) : null}
                      {generationBlockNotice.retryAfterSeconds ? (
                        <button
                          type="button"
                          disabled={Date.now() < generationCooldownUntil}
                          onClick={() => void handleCreateNextChapter()}
                          className="px-2 py-1 rounded bg-amber-700/50 text-amber-50 text-[10px] uppercase tracking-wide disabled:opacity-50"
                        >
                          Retry
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                {workspaceActionState.kind === "WRITE_NEXT" || workspaceActionState.kind === "GENERATION_RUNNING" ? (
                  <button
                    onClick={() => void handleCreateNextChapter()}
                    disabled={workspaceActionState.disabled || generationInFlightRef.current || Date.now() < generationCooldownUntil}
                    className="px-5 py-1.5 bg-cv-success hover:bg-[#2EB67C] text-white rounded font-black text-xs uppercase tracking-widest disabled:opacity-30 shadow-lg shadow-black/30 min-w-[140px]"
                  >
                    {workspaceActionState.buttonLabel}
                  </button>
                ) : workspaceActionState.kind === "BOOK_CREDIT_REQUIRED" || workspaceActionState.kind === "BUY_CREDIT_REQUIRED" ? (
                  <button
                    onClick={() => setPaywallModal({ isOpen: true, reason: 'generation' })}
                    className="flex items-center gap-2 px-5 py-1.5 border border-amber-500/60 bg-amber-950/40 text-cv-warning hover:bg-amber-900/50 hover:text-amber-100 rounded font-black text-xs uppercase tracking-widest shadow-lg shadow-amber-900/20 transition-all min-w-[140px] justify-center"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0" />
                    </svg>
                    {workspaceActionState.buttonLabel}
                  </button>
                ) : workspaceActionState.kind === "CLEAR_FAILED_LOCK" ? (
                  <button
                    onClick={() => clearStaleLock(false)}
                    disabled={workspaceActionState.disabled}
                    className={`flex items-center gap-2 px-5 py-1.5 border border-amber-500/30 bg-cv-elevated text-cv-warning/90 hover:bg-cv-elevated hover:text-amber-100 rounded tracking-wide shadow-md transition-all min-w-[140px] justify-center ${workspaceActionState.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <svg className={`w-4 h-4 text-amber-500/70 ${workspaceActionState.disabled ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {workspaceActionState.disabled ? (
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      ) : (
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      )}
                    </svg>
                    {workspaceActionState.buttonLabel}
                  </button>
                ) : workspaceActionState.kind === "MISSING_CHAPTER" ? (
                  <button
                    onClick={() => setMissingChapterModalOpen(true)}
                    className="flex items-center gap-2 px-5 py-1.5 border border-cv-danger/60 bg-red-950/40 text-cv-danger hover:bg-red-900/50 hover:text-red-100 rounded font-black text-xs uppercase tracking-widest shadow-lg shadow-red-900/20 transition-all min-w-[140px] justify-center"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {workspaceActionState.buttonLabel}
                  </button>
                ) : workspaceActionState.kind === "PROJECT_REPAIR_REQUIRED" ? (
                  <button
                    onClick={() => setProjectIntegrityModalOpen(true)}
                    className="flex items-center gap-2 px-5 py-1.5 border border-cv-danger/60 bg-red-950/40 text-cv-danger hover:bg-red-900/50 hover:text-red-100 rounded font-black text-xs uppercase tracking-widest shadow-lg shadow-red-900/20 transition-all min-w-[140px] justify-center"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {workspaceActionState.buttonLabel}
                  </button>
                ) : null}

                {workspaceActionState.subLabel && (
                  <span className={`text-[9px] font-medium mt-1 tracking-wide text-center max-w-[180px] ${workspaceActionState.kind === "CLEAR_FAILED_LOCK" ? "text-cv-muted normal-case" : "font-bold text-amber-500/80 uppercase tracking-widest"}`}>
                    {workspaceActionState.subLabel}
                  </span>
                )}
              </div>

              {/* Auto-writing Toggle */}
              <button 
                onClick={() => {
                  if (!isProjectUnlocked(storyBible)) {
                    setPaywallModal({ isOpen: true, reason: 'autowrite' });
                  } else {
                    setIsAutoRunning(!isAutoRunning);
                  }
                }} 
                disabled={status === AppStatus.COMPLETE} 
                className={`px-4 py-1.5 text-xs font-black rounded flex items-center gap-2 border transition-all ${isAutoRunning ? 'bg-cv-success border-cv-interactive text-white' : 'bg-cv-elevated border-cv-border-strong text-cv-muted'}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${isAutoRunning ? 'bg-white animate-pulse' : 'bg-cv-disabled'}`}></div>
                {isAutoRunning ? 'Auto-writing On' : 'Auto-writing Off'}
              </button>

              {/* Export Manuscript */}
              <button 
                onClick={() => {
                  if (!isProjectUnlocked(storyBible)) {
                    setPaywallModal({ isOpen: true, reason: 'export' });
                  } else {
                    setIsPublicationModalOpen(true);
                  }
                }} 
                disabled={chapters.length === 0} 
                className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-black rounded border bg-cv-elevated border-cv-border-strong text-cv-muted hover:text-white disabled:opacity-30 transition-all"
              >
                <ExportIcon /> EXPORT MANUSCRIPT
              </button>

              <button 
                onClick={() => setIsManuscriptEditorOpen(true)}
                disabled={chapters.length === 0} 
                className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-black rounded border bg-cv-elevated border-cv-border-strong text-white hover:bg-cv-elevated disabled:opacity-30 transition-all"
              >
                REVIEW MANUSCRIPT
              </button>
            </>
          )}

          {/* More Actions / Advanced Controls Dropdown */}
          <div className="relative">
            <button 
              onClick={() => setShowAdvancedControls(!showAdvancedControls)} 
              className="px-3 py-1.5 text-xs font-black rounded flex items-center gap-2 border bg-cv-elevated border-cv-border-strong text-cv-muted hover:text-white transition-all"
            >
              ⚙️ More Actions
            </button>
            {showAdvancedControls && (
              <div className="absolute right-0 mt-2 w-56 bg-cv-panel border border-cv-border rounded-lg shadow-2xl p-2 z-50 flex flex-col gap-1 text-left">
                {status !== AppStatus.IDLE && (
                  <button 
                    onClick={() => { 
                      setIsAutoRunning(false); 
                      setStatus(AppStatus.IDLE); 
                      setActiveProgress(null); 
                      setShowAdvancedControls(false);
                    }}
                    className="w-full text-left px-3 py-2 text-[10px] font-black uppercase text-cv-danger hover:bg-red-950/20 rounded transition-all"
                  >
                    🚨 Emergency Reset
                  </button>
                )}

                {/* Admin-only: Clear Stale Generation Lock */}
                {user && isAdmin(user) && (
                  <button
                    onClick={async () => {
                      setShowAdvancedControls(false);
                      await clearStaleLock(true);
                    }}
                    className="w-full text-left px-3 py-2 text-[10px] font-black uppercase text-cv-warning hover:bg-orange-950/20 rounded transition-all"
                  >
                    🔓 Clear Stale Generation Lock
                  </button>
                )}

                <a href="/help" className="w-full text-left px-3 py-2 text-[10px] font-black uppercase text-cv-secondary hover:bg-cv-elevated rounded transition-all block">
                  Help Center
                </a>

                <button 
                  onClick={() => { 
                    toggleClosingMode(); 
                    setShowAdvancedControls(false); 
                  }} 
                  className={`w-full text-left px-3 py-2 text-[10px] font-black uppercase rounded transition-all ${storyBible?.isClosingMode ? 'text-cv-warning hover:bg-orange-950/20' : 'text-cv-muted hover:bg-cv-elevated'}`}
                >
                  {storyBible?.isClosingMode ? '⏹️ Disable Closing Mode' : 'Force Wrap-Up'}
                </button>

                <button 
                  onClick={() => {
                    if (window.confirm("Are you sure you want to completely regenerate the roadmap? All unlocked chapter plans will be wiped.")) {
                      handleRegenerateRemainingRoadmap(0, storyBible?.chapterPlans || [], totalChapters);
                    }
                    setShowAdvancedControls(false);
                  }} 
                  className="w-full text-left px-3 py-2 text-[10px] font-black uppercase text-pink-400 hover:bg-pink-950/20 rounded transition-all"
                >
                  🔄 Regenerate Roadmap
                </button>

                <button 
                  onClick={() => {
                    setIsRoadmapEditorOpen(true);
                    setShowAdvancedControls(false);
                  }}
                  className="w-full text-left px-3 py-2 text-[10px] font-black uppercase text-cv-teal hover:bg-purple-950/20 rounded transition-all"
                >
                  🗺️ Edit Roadmap
                </button>

                <button 
                  onClick={() => {
                    handleExportProjectState();
                    setShowAdvancedControls(false);
                  }}
                  className="w-full text-left px-3 py-2 text-[10px] font-black uppercase text-cv-muted hover:bg-cv-elevated rounded transition-all"
                >
                  💾 Archive
                </button>

                <div className="h-px bg-cv-elevated my-1"></div>

                <button 
                  onClick={() => {
                    setPaywallModal({ isOpen: true, reason: 'generation' });
                    setShowAdvancedControls(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-[10px] font-black uppercase text-cv-warning hover:bg-amber-950/20 rounded transition-all"
                >
                  💳 Developer Billing Tools
                </button>

                <button 
                  onClick={() => { 
                    setShowAgentDebug(!showAgentDebug); 
                    setShowAdvancedControls(false); 
                  }} 
                  className="w-full text-left px-3 py-1.5 text-[10px] font-black uppercase text-cv-muted hover:bg-cv-elevated rounded transition-all"
                >
                  {showAgentDebug ? '🙈 Hide Developer Agent Status' : '🕵️ Developer Agent Status'}
                </button>

                <button 
                  onClick={() => { 
                    setShowAdvancedLogs(!showAdvancedLogs); 
                    setShowAdvancedControls(false); 
                  }} 
                  className="w-full text-left px-3 py-1.5 text-[10px] font-black uppercase text-cv-muted hover:bg-cv-elevated rounded transition-all"
                >
                  {showAdvancedLogs ? '🙈 Hide Advanced System Logs' : '📋 Advanced System Logs'}
                </button>
              </div>
            )}
          </div>

          {user ? (
            <div className="relative group ml-2">
              <button className="flex items-center gap-3 px-3 py-1.5 bg-cv-panel border border-cv-border rounded-lg hover:border-cv-border-strong transition-colors" type="button">
                <span className="text-xs text-cv-secondary font-medium tracking-wide flex items-center gap-1">
                  <span>Free Preview</span>
                  <span className="text-cv-disabled">·</span>
                  <span className="text-cv-muted">Standard <span className="text-cv-gold font-bold">{userData?.standardCredits || 0}</span></span>
                  <span className="text-cv-disabled">·</span>
                  <span className="text-cv-muted">Long <span className="text-cv-teal font-bold">{userData?.longCredits || 0}</span></span>
                  <span className="text-cv-disabled">·</span>
                  <span className="text-cv-muted">Epic <span className="text-cv-warning font-bold">{userData?.epicCredits || 0}</span></span>
                  <span className="text-cv-disabled">·</span>
                  <span className="text-cv-muted">Rewrite <span className="text-cv-success font-bold">{userData?.rewriteCredits || 0}</span></span>
                </span>
                <svg className="w-3 h-3 text-cv-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <div className="absolute right-0 mt-2 w-64 bg-cv-panel border border-cv-border-strong rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden text-left">
                <div className="p-4 border-b border-cv-border bg-cv-page/50">
                  <p className="text-xs text-cv-muted font-mono mb-1">SIGNED IN AS</p>
                  <p className="text-sm font-medium text-cv-secondary truncate">{user.email}</p>
                </div>
                <div className="p-2 space-y-1">
                  <div className="px-3 py-2 flex justify-between items-center text-sm">
                    <span className="text-cv-muted">Plan</span>
                    <span className="text-cv-primary font-medium capitalize">Free Preview</span>
                  </div>
                  <div className="px-3 py-2 flex justify-between items-center text-sm">
                    <span className="text-cv-muted">Book Credits</span>
                    <span className="text-cv-gold font-bold">{
                      (userData?.standardCredits || 0) + 
                      (userData?.longCredits || 0) + 
                      (userData?.epicCredits || 0)
                    }</span>
                  </div>
                </div>
                <div className="p-2 border-t border-cv-border">
                  <button onClick={() => setPaywallModal({ isOpen: true, reason: 'generation' })} className="w-full text-left px-3 py-2 text-sm text-cv-teal hover:bg-cv-gold/10 rounded transition-colors font-medium">
                    Upgrade Plan
                  </button>
                  {typeof window !== 'undefined' && (window as any).aistudio && (
                    <button
                      onClick={async () => {
                        try {
                          await (window as any).aistudio.openSelectKey();
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-cv-muted hover:bg-cv-elevated hover:text-cv-primary rounded transition-colors"
                    >
                      Developer Settings (API Key)
                    </button>
                  )}
                  <button onClick={logout} className="w-full text-left px-3 py-2 text-sm text-cv-danger hover:bg-red-500/10 rounded transition-colors mt-1">
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button onClick={login} className="px-4 py-2 bg-cv-elevated hover:bg-cv-elevated text-white rounded font-bold uppercase tracking-widest text-xs transition-colors border border-cv-border-strong ml-2">
               Sign In
            </button>
          )}

        </div>
      </header>

      {pausedRecoveryMetadata && (() => {
        const recoveryAction = resolveRecoveryAction(pausedRecoveryMetadata);
        return (
        <div className="bg-orange-950/80 border-b border-orange-500/50 p-4 shadow-2xl flex items-center justify-between z-30 animate-in fade-in duration-300">
          <div className="flex items-center gap-3">
            <span className="text-xl">⚠️</span>
            <div className="flex flex-col">
              <h4 className="font-black text-orange-200 text-xs uppercase tracking-tighter">Chapter Generation Interrupted</h4>
              <p className="text-[11px] text-orange-300/80 font-mono leading-relaxed mt-0.5">
                Chapter {pausedRecoveryMetadata.failedChapterNumber} paused at {pausedRecoveryMetadata.failedAgentName}. {recoveryAction.explanation}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {recoveryAction.allowed ? (
              <button
                onClick={() => runOrchestrationLoop()}
                className="px-4 py-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded font-black text-[10px] uppercase tracking-widest transition-all shadow-md"
              >
                {recoveryAction.label}
              </button>
            ) : (
              <button
                onClick={() => {
                  const message = pausedRecoveryMetadata.userFacingMessage || 'This chapter has a confirmed canon block.';
                  addLog('System', message, 'warning', 'user');
                  if (failedDraft) {
                    addLog('System', 'Review the preserved draft in the manuscript panel.', 'info', 'user');
                  }
                }}
                className="px-4 py-1.5 bg-orange-800 hover:bg-orange-700 text-white rounded font-black text-[10px] uppercase tracking-widest transition-all shadow-md"
              >
                Review Block
              </button>
            )}
            <button
              onClick={() => {
                addLog('System', 'Recovery checkpoint preserved. Return to project when ready to resume.', 'info', 'user');
                setStatus(AppStatus.IDLE);
                setIsAutoRunning(false);
              }}
              className="px-3 py-1.5 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary rounded font-black text-[10px] uppercase tracking-widest transition-all border border-cv-border-strong"
            >
              Return to Project
            </button>
            <button
              onClick={async () => {
                if (window.confirm("Are you sure you want to regenerate this chapter from scratch? The current checkpoint draft will be discarded.")) {
                  let deletedStorage = false;
                  if (pausedRecoveryMetadata.checkpointStoragePath) {
                    await deleteCheckpointFromStorage(pausedRecoveryMetadata.checkpointStoragePath).then(() => { deletedStorage = true; }).catch(console.warn);
                  }
                  try {
                    await releaseRecoveryReservationIfNeeded(pausedRecoveryMetadata);
                    setPausedRecoveryMetadata(null);
                    setCheckpointDraftText(null);
                    addLog('System', `Interrupted chapter recovery checkpoint cleared. Restarting Chapter ${currentChapterNum} fresh.`, 'warning');
                    setTimeout(() => runOrchestrationLoop(), 500);
                  } catch (firestoreErr) {
                    const msg = deletedStorage 
                      ? "Checkpoint file removed, but Firestore metadata could not update due to quota. Try again after quota reset."
                      : "Could not clear checkpoint due to Firestore quota.";
                    addLog('System', msg, 'error');
                    alert(msg);
                  }
                }
              }}
              className="px-4 py-1.5 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary rounded font-black text-[10px] uppercase tracking-widest transition-all border border-cv-border-strong"
            >
              Regenerate Chapter
            </button>
            <button
              onClick={async () => {
                if (window.confirm("Clear this recovery checkpoint? The state will revert to idle.")) {
                  let deletedStorage = false;
                  if (pausedRecoveryMetadata.checkpointStoragePath) {
                    await deleteCheckpointFromStorage(pausedRecoveryMetadata.checkpointStoragePath).then(() => { deletedStorage = true; }).catch(console.warn);
                  }
                  try {
                    await releaseRecoveryReservationIfNeeded(pausedRecoveryMetadata);
                    setPausedRecoveryMetadata(null);
                    setCheckpointDraftText(null);
                    addLog('System', `Interrupted chapter recovery checkpoint discarded. Status returned to idle.`, 'info');
                  } catch (firestoreErr) {
                    const msg = deletedStorage 
                      ? "Checkpoint file removed, but Firestore metadata could not update due to quota. Try again after quota reset."
                      : "Could not clear checkpoint due to Firestore quota.";
                    addLog('System', msg, 'error');
                    alert(msg);
                  }
                }
              }}
              className="px-3 py-1.5 bg-orange-950/20 text-cv-warning hover:bg-orange-950/50 rounded font-black text-[10px] uppercase tracking-widest transition-all border border-orange-500/20"
            >
              Discard Draft
            </button>
          </div>
        </div>
        );
      })()}

      {publicationError && publicationError.includes('Retry Metadata Save') && (
        <div className="bg-red-950/80 border-b border-cv-danger/50 p-4 shadow-2xl flex items-center justify-between z-30 animate-in fade-in duration-300">
          <div className="flex items-center gap-3">
            <span className="text-xl">🚨</span>
            <div className="flex flex-col">
              <h4 className="font-black text-red-200 text-xs uppercase tracking-tighter">Metadata Verification Failed</h4>
              <p className="text-[11px] text-cv-danger/80 font-mono leading-relaxed mt-0.5">
                {publicationError}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={repairMetadataSave}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded font-black text-[10px] uppercase tracking-widest transition-all shadow-md"
            >
              Retry Metadata Save
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden">
        <div className="w-full lg:w-[400px] shrink-0 flex flex-col lg:border-r border-cv-border p-4 gap-4 bg-cv-page/50 overflow-visible lg:overflow-hidden z-10">
          {/* Current Chapter Progress Panel & Visual Step Tracker */}
          <div className="bg-cv-panel border border-cv-interactive/20 rounded-2xl p-4 flex flex-col gap-4 shadow-xl">
            {isComplete ? (
              <>
                <div>
                  <div className="text-[10px] font-mono font-black text-cv-success uppercase tracking-widest mb-1">
                    🎉 Book Complete
                  </div>
                  <h2 className="text-lg font-black text-white leading-tight">
                    {completedChapters === null ? "Reconciling chapter count..." : `${completedChapters} of ${totalChapters} chapters finished`}
                  </h2>
                  <p className="text-xs text-cv-muted mt-2">
                    Your manuscript is ready to review, export, or continue into the next volume.
                  </p>
                </div>

                {/* Static Checklist */}
                <div className="flex flex-col gap-2 border-y border-cv-border py-3 text-[11px] font-mono">
                  <div className="flex items-center gap-2 text-cv-success font-bold">
                    <span>✓</span>
                    <span>All chapters written</span>
                  </div>
                  <div className="flex items-center gap-2 text-cv-success font-bold">
                    <span>✓</span>
                    <span>Story memory updated</span>
                  </div>
                  <div className="flex items-center gap-2 text-cv-success font-bold">
                    <span>✓</span>
                    <span>Manuscript ready for export</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="text-[10px] font-mono font-black text-cv-gold uppercase tracking-widest mb-1">
                    {status !== AppStatus.IDLE
                      ? '⚡ Narrative Engine Active'
                      : workspaceState.manuscriptIsComplete
                        ? 'Manuscript Complete'
                        : workspaceState.hasInternalGap
                          ? 'Manuscript Repair Required'
                          : workspaceState.canGenerateNextSequentialChapter
                            ? 'Ready for Next Chapter'
                            : '🟢 Manuscript Workspace'}
                  </div>
                  <h2 className="text-lg font-black text-white leading-tight">
                    {status !== AppStatus.IDLE
                      ? `Writing Chapter ${Math.min(currentChapterNum, totalChapters)} of ${totalChapters}`
                      : workspaceState.manuscriptIsComplete
                        ? `${totalChapters} of ${totalChapters} chapters`
                        : workspaceState.hasInternalGap && workspaceState.firstMissingChapterNumber
                          ? `Chapter ${workspaceState.firstMissingChapterNumber} is missing`
                          : workspaceState.canGenerateNextSequentialChapter && workspaceState.nextSequentialChapterNumber
                            ? `Ready for Chapter ${workspaceState.nextSequentialChapterNumber}`
                            : workspaceActionState.subLabel || 'Manuscript workspace'}
                  </h2>
                  {workspaceState.hasInternalGap && workspaceState.firstMissingChapterNumber && status === AppStatus.IDLE && (
                    <div className="text-xs text-cv-muted mt-1 leading-relaxed">
                      Your manuscript already reaches Chapter {workspaceState.highestExistingChapterNumber}, but Chapter {workspaceState.firstMissingChapterNumber} is missing. Repair the gap before continuing or exporting.
                    </div>
                  )}
                  {workspaceState.canGenerateNextSequentialChapter && workspaceState.nextSequentialChapterNumber && storyBible?.chapterPlans?.[workspaceState.nextSequentialChapterNumber - 1]?.title && status === AppStatus.IDLE && (
                    <div className="text-xs text-cv-muted italic mt-0.5 truncate">
                      "{storyBible.chapterPlans[workspaceState.nextSequentialChapterNumber - 1].title}"
                    </div>
                  )}
                </div>

                {/* Visual Step Tracker */}
                <div className="flex flex-col gap-2 border-y border-cv-border py-3">
                  {[
                    { label: 'Draft', key: [AppStatus.SCRIBING, AppStatus.REWRITING], order: 1 },
                    { label: 'Continuity Check', key: [AppStatus.INSPECTING], order: 2 },
                    { label: 'Style Edit', key: [AppStatus.EDITING], order: 3 },
                    { label: 'Final Polish', key: [AppStatus.PUBLISHING], order: 4 },
                    { label: 'Story Memory', key: [AppStatus.ARCHIVING], order: 5 },
                    { label: 'Save Chapter', key: [], order: 6 }
                  ].map((step) => {
                    let stepStatus: 'complete' | 'active' | 'pending' | 'failed' = 'pending';
                    
                    const currentOrder = 
                      status === AppStatus.SCRIBING || status === AppStatus.REWRITING ? 1 :
                      status === AppStatus.INSPECTING ? 2 :
                      status === AppStatus.EDITING ? 3 :
                      status === AppStatus.PUBLISHING ? 4 :
                      status === AppStatus.ARCHIVING ? 5 :
                      status === AppStatus.IDLE ? 0 : 0;
                    
                    if (status === AppStatus.COMPLETE) {
                      stepStatus = 'complete';
                    } else if (status === AppStatus.IDLE) {
                      stepStatus = 'pending';
                    } else if (step.key.includes(status)) {
                      stepStatus = 'active';
                    } else if (step.order < currentOrder) {
                      stepStatus = 'complete';
                    } else if (publicationError && step.order === 4) {
                      stepStatus = 'failed';
                    } else {
                      stepStatus = 'pending';
                    }

                    return (
                      <div key={step.label} className="flex items-center justify-between text-[11px] font-mono">
                        <span className={`font-bold ${stepStatus === 'active' ? 'text-cv-gold' : stepStatus === 'complete' ? 'text-cv-muted' : 'text-cv-disabled'}`}>
                          {step.label}
                        </span>
                        <span className={`text-[9px] uppercase font-black px-1.5 py-0.5 rounded ${
                          stepStatus === 'complete' ? 'bg-cv-elevated text-cv-muted border border-cv-border/50' :
                          stepStatus === 'active' ? 'bg-cv-success/20 text-cv-gold border border-cv-interactive/30 animate-pulse' :
                          stepStatus === 'failed' ? 'bg-red-900/20 text-cv-danger border border-cv-danger/30 animate-pulse' :
                          'bg-cv-page/20 text-cv-disabled'
                        }`}>
                          {stepStatus}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Friendly Status Message */}
                <div className="text-xs leading-relaxed text-cv-secondary">
                  {status === AppStatus.IDLE ? (
                    <span>Setup ready. Click <strong>Write Next Chapter</strong> to start writing the next chapter.</span>
                  ) : status === AppStatus.SCRIBING ? (
                    <span>Authoring the initial raw draft based on the chapter roadmap and canon guide...</span>
                  ) : status === AppStatus.INSPECTING ? (
                    <span>Scanning character states, timeline events, and canon details for absolute consistency...</span>
                  ) : status === AppStatus.EDITING ? (
                    <span>Aligning the prose style with your custom style instructions and tone guide...</span>
                  ) : status === AppStatus.PUBLISHING ? (
                    <span>Still finalizing the polished chapter — this can take a few minutes on dense chapters.</span>
                  ) : status === AppStatus.ARCHIVING ? (
                    <span>Synching new events, characters, and changes back into the persistent Story Memory...</span>
                  ) : (
                    <span>Narrative engine is processing. Please wait while the volume takes shape.</span>
                  )}
                </div>
              </>
            )}

            {/* Advanced details with Request ID */}
            {activeProgress && showAgentDebug && (
              <div className="text-[10px] font-mono text-cv-muted flex justify-between items-center border-t border-cv-border pt-2">
                <span>Timer: {activeProgress.elapsedSecs}s</span>
                {activeProgress.requestId && activeProgress.requestId !== 'N/A' && (
                  <span className="truncate max-w-[150px]">ID: {activeProgress.requestId}</span>
                )}
              </div>
            )}
          </div>

          {/* Collapsible Developer Agent Status */}
          {(showAgentDebug || (user && isAdmin(user)) || pausedRecoveryMetadata || status === AppStatus.ERROR) && (
            <div className="border border-cv-border rounded-xl overflow-hidden bg-cv-panel/20 hidden lg:block">
              <button 
                onClick={() => setShowAgentDebug(!showAgentDebug)} 
                className="w-full px-3 py-2 flex items-center justify-between text-[11px] font-black text-cv-muted uppercase tracking-widest hover:text-white bg-cv-panel/50 transition-all border-b border-cv-border"
              >
                <span>🕵️ Developer Agent Status</span>
                <span>{showAgentDebug ? '▼' : '▶'}</span>
              </button>
              {showAgentDebug && (
                <div className="p-3 grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto custom-scrollbar animate-in fade-in duration-200 bg-cv-page/40">
                   <AgentCard role={AgentRole.ARCHITECT} isActive={status === AppStatus.PLANNING} icon={<ArchitectIcon />} />
                   <AgentCard role={AgentRole.SCRIBE} isActive={status === AppStatus.SCRIBING || status === AppStatus.REWRITING} icon={<ScribeIcon />} />
                   <AgentCard role={AgentRole.EDITOR} isActive={status === AppStatus.EDITING} icon={<EditorIcon />} />
                   <AgentCard role={AgentRole.INSPECTOR} isActive={status === AppStatus.INSPECTING} icon={<InspectorIcon />} />
                   <AgentCard role={AgentRole.PUBLICATION_EDITOR} isActive={status === AppStatus.PUBLISHING} icon={<PublicationIcon />} />
                   <AgentCard role={AgentRole.ARCHIVIST} isActive={status === AppStatus.ARCHIVING} icon={<ArchivistIcon />} />
                   {status === AppStatus.REWRITING && (
                     <div className="col-span-2">
                       <AgentCard role={AgentRole.REWRITER} isActive={status === AppStatus.REWRITING} icon={<RewriterIcon />} />
                     </div>
                   )}
                </div>
              )}
            </div>
          )}

          <div className="bg-cv-panel/80 border border-cv-interactive/20 rounded-xl p-3 flex flex-col gap-2 shadow-inner">
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] font-black text-cv-gold uppercase tracking-widest flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Director's Note
              </h3>
              <div className="flex items-center gap-2">
                {directorsNote && (
                  <>
                    <button 
                      onClick={handleUpdateLedgerFromNote}
                      disabled={status !== AppStatus.IDLE}
                      className="text-[9px] text-cv-gold hover:text-cv-interactive uppercase font-bold bg-cv-interactive/15 px-2 py-0.5 rounded border border-cv-interactive/30 disabled:opacity-30"
                    >
                      Apply to Ledger
                    </button>
                    <button onClick={() => setDirectorsNote('')} className="text-[9px] text-cv-muted hover:text-white uppercase font-bold">Clear</button>
                  </>
                )}
              </div>
            </div>
            <textarea 
              value={directorsNote}
              onChange={(e) => setDirectorsNote(e.target.value)}
              placeholder="Inject narrative direction (e.g., 'Make it more violent', 'Save the messenger')..."
              className="bg-cv-page/50 border border-cv-border rounded-lg p-2 text-[11px] text-cv-secondary font-mono focus:outline-none focus:border-cv-interactive/50 min-h-[80px] resize-none placeholder:text-cv-muted"
            />
          </div>

          {correctionOrder && (
            <div className="bg-red-950/20 border border-cv-danger/30 rounded-xl p-3 flex flex-col gap-2 animate-pulse">
              <h3 className="text-[10px] font-black text-cv-danger uppercase tracking-widest flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                Inspector's Correction Order
              </h3>
              <div className="text-[11px] text-red-200/80 font-mono leading-relaxed bg-black/40 p-2 rounded border border-cv-danger/10 max-h-[150px] overflow-auto custom-scrollbar">
                {correctionOrder}
              </div>
              <button 
                onClick={() => setCorrectionOrder(undefined)}
                className="text-[9px] text-cv-danger hover:text-white uppercase font-bold self-end"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* User Progress Feed */}
          <div className="border border-cv-border rounded-xl overflow-hidden bg-cv-panel/20 hidden lg:block">
            <div className="px-3 py-2 border-b border-cv-border bg-cv-panel/50 flex items-center justify-between">
              <span className="text-[11px] font-black text-cv-gold uppercase tracking-widest flex items-center gap-2">
                📖 Creative Progress Feed
              </span>
            </div>
            <div className="p-1 bg-cv-page/40">
              <LogPanel logs={logs} filterAudience="user" />
            </div>
          </div>

          {/* Collapsible Advanced Logs */}
          <div className="border border-cv-border rounded-xl overflow-hidden bg-cv-panel/20 mt-auto hidden lg:block">
            <button 
              onClick={() => setShowAdvancedLogs(!showAdvancedLogs)} 
              className="w-full px-3 py-2 flex items-center justify-between text-[11px] font-black text-cv-muted uppercase tracking-widest hover:text-white bg-cv-panel/50 transition-all border-b border-cv-border"
            >
              <div className="flex items-center gap-2">
                <span>📋 Advanced System Logs</span>
                {user && isAdmin(user) && (
                  <span className="px-1.5 py-0.2 text-[8px] bg-cv-interactive/20 text-cv-interactive rounded border border-cv-interactive/50 font-bold uppercase tracking-wider">
                    Admin
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {logs.filter(l => l.type === 'error').length > 0 && (
                  <span className="px-1.5 py-0.5 text-[9px] bg-red-600 text-white rounded font-bold">
                    {logs.filter(l => l.type === 'error').length}
                  </span>
                )}
                <span>{showAdvancedLogs ? '▼' : '▶'}</span>
              </div>
            </button>
            {showAdvancedLogs && (
              <div className="p-1 animate-in fade-in duration-200 bg-cv-page/40">
                <LogPanel logs={logs} filterAudience="developer" />
              </div>
            )}
          </div>
          <div className="flex-1 min-h-0">
             <div className="h-full flex flex-col gap-2">
                <div className="grid grid-cols-2 gap-2 text-[9px] font-mono uppercase tracking-tighter">
                   <div className="bg-cv-panel/50 p-2 rounded border border-cv-border">
                      <div className="text-cv-muted mb-0.5">Stage</div>
                      <div className="text-cv-gold font-black truncate">{ledger?.arc_stage}</div>
                   </div>
                   <div className="bg-cv-panel/50 p-2 rounded border border-cv-border">
                      <div className="text-cv-muted mb-0.5">Momentum</div>
                      <div className="text-green-400 font-black truncate">{ledger?.momentum}</div>
                   </div>
                </div>
                {ledger && (
                  <LedgerView 
                    ledger={ledger} 
                    onRollback={handleRollback} 
                    canRollback={ledgerHistory.length > 0 && status === AppStatus.IDLE}
                    activeCanonId={storyBible?.canonId || currentProjectId || ''}
                    activeProjectId={currentProjectId || ''}
                    onRepair={handleRepairMemory}
                  />
                )}
             </div>
          </div>
        </div>
        <div className="flex-1 p-4 lg:p-8 bg-cv-page overflow-visible lg:overflow-hidden relative min-h-[50vh]">
          <StoryFeed 
            chapters={chapters} 
            currentChapterId={currentChapterNum} 
            mode={storyBible?.mode || StoryMode.NOVEL} 
            onRewrite={openRewriteModal}
            onLoadChapter={handleLoadChapter}
            isRewriting={status !== AppStatus.IDLE && status !== AppStatus.COMPLETE}
            status={status}
            activeProgress={activeProgress}
            chapterPlans={storyBible?.chapterPlans || []}
            isBookComplete={isComplete}
            showAgentDebug={showAgentDebug}
            chapterLoadErrors={chapterLoadErrors}
          />
          {correctionOrder && status === AppStatus.IDLE && (
            <div className="absolute bottom-6 left-6 right-6 bg-red-950/90 border border-cv-danger/50 p-5 rounded-lg shadow-2xl animate-bounce">
              <div className="flex items-start gap-4">
                <div className="text-cv-danger font-black text-lg">!</div>
                <div className="flex-1">
                  <h4 className="font-black text-red-100 uppercase tracking-tighter text-xs">Correction Required</h4>
                  <p className="text-[10px] text-red-200/80 font-mono leading-relaxed mt-1">{correctionOrder}</p>
                </div>
                <button onClick={() => setCorrectionOrder(undefined)} className="text-cv-danger text-xs font-bold uppercase hover:text-white">Dismiss</button>
              </div>
            </div>
          )}
          {publicationError && (
            <div className="absolute bottom-6 left-6 right-6 bg-orange-950/90 border border-orange-500/50 p-5 rounded-lg shadow-2xl z-50 animate-in fade-in duration-300">
              <div className="flex items-start gap-4">
                <div className="text-cv-warning font-black text-lg">⚠️</div>
                <div className="flex-1">
                  <h4 className="font-black text-orange-100 uppercase tracking-tighter text-xs">Publication Editor Error</h4>
                  <p className="text-[10px] text-orange-200/80 font-mono leading-relaxed mt-1">{publicationError}</p>
                </div>
                <div className="flex gap-2">
                  {publicationError.includes('Retry Metadata Save') ? (
                    <button 
                      onClick={() => {
                        setPublicationError(null);
                        repairMetadataSave();
                      }} 
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-black uppercase rounded transition-colors"
                    >
                      Retry Metadata Save
                    </button>
                  ) : (
                    <button 
                      onClick={() => {
                        setPublicationError(null);
                        runOrchestrationLoop();
                      }} 
                      className="px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-black uppercase rounded transition-colors"
                    >
                      Retry Chapter {currentChapterNum}
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      setPublicationError(null);
                      setIsAutoRunning(false);
                      setStatus(AppStatus.IDLE);
                    }} 
                    className="px-3 py-1.5 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary text-xs font-black uppercase rounded border border-cv-border-strong transition-colors"
                  >
                    Reset to Idle
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Action Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-cv-panel border-t border-cv-border p-3 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] z-40 flex items-center justify-between gap-2">
        <button
          onClick={() => {
            if (isAutoRunning) {
              setIsAutoRunning(false);
            } else {
              handleCreateNextChapter();
            }
          }}
          disabled={isInitializing || isComplete || (!isAutoRunning && chapters.length >= (storyBible?.targetChapterCount || 100))}
          className={`flex-1 py-3 px-4 rounded-lg font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
            isAutoRunning 
              ? 'bg-orange-600 text-white shadow-lg shadow-orange-900/40'
              : 'bg-cv-success hover:bg-[#2EB67C] text-white shadow-lg shadow-black/40'
          } disabled:opacity-50 disabled:shadow-none`}
        >
          {isAutoRunning ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Pause
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Write
            </>
          )}
        </button>
        
        <button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="p-3 bg-cv-elevated text-cv-secondary rounded-lg border border-cv-border-strong flex flex-col items-center justify-center gap-1 min-w-[64px]"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          <span className="text-[9px] font-black uppercase">Chaps</span>
        </button>

        {isComplete && storyBible?.exportUnlocked && (
          <button
            onClick={() => setIsPublicationModalOpen(true)}
            className="p-3 bg-cv-success text-white rounded-lg border border-cv-interactive shadow-lg shadow-black/30 flex flex-col items-center justify-center gap-1 min-w-[64px]"
          >
            <ExportIcon />
            <span className="text-[9px] font-black uppercase">Export</span>
          </button>
        )}

        <button
          onClick={() => setShowMobileMenu(true)}
          className="p-3 bg-cv-elevated text-cv-secondary rounded-lg border border-cv-border-strong flex flex-col items-center justify-center gap-1 min-w-[64px]"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
          </svg>
          <span className="text-[9px] font-black uppercase">Menu</span>
        </button>
      </div>

      {/* Mobile Menu Bottom Sheet */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm p-4 md:hidden">
          <div className="bg-cv-panel border border-cv-border-strong rounded-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl flex flex-col mb-20 animate-in slide-in-from-bottom-4">
            <div className="p-4 border-b border-cv-border flex justify-between items-center bg-cv-page rounded-t-2xl sticky top-0 z-10">
              <h2 className="text-lg font-black text-white uppercase tracking-wider">Options</h2>
              <button onClick={() => setShowMobileMenu(false)} className="text-cv-muted hover:text-white p-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <div className="p-4 space-y-2 flex flex-col">
              <button onClick={() => { setShowMobileMenu(false); setPaywallModal({ isOpen: true, reason: 'generation' }); }} className="w-full text-left p-4 bg-cv-elevated rounded-xl font-bold text-sm text-cv-teal border border-cv-border-strong">
                Upgrade Plan
              </button>
              
              <button onClick={() => { setShowMobileMenu(false); setIsRoadmapEditorOpen(true); }} className="w-full text-left p-4 bg-cv-elevated rounded-xl font-bold text-sm text-cv-gold border border-cv-border-strong">
                Edit Roadmap
              </button>

              <button 
                onClick={async () => {
                  setShowMobileMenu(false);
                  if (window.confirm("Archive this project? It will be removed from your dashboard.")) {
                    try {
                      if (currentProjectId) {
                        handleDeleteProject(currentProjectId);
                        addLog('System', `Archived project ${currentProjectId}.`, 'success');
                      }
                      setStatus(AppStatus.DASHBOARD);
                      setCurrentProjectId(null);
                    } catch (e: any) {
                      console.error("Archive failed:", e);
                      alert("Failed to archive project.");
                    }
                  }
                }} 
                className="w-full text-left p-4 bg-cv-elevated rounded-xl font-bold text-sm text-cv-danger border border-cv-border-strong mt-4"
              >
                Archive Project
              </button>
              
              <div className="pt-4 mt-4 border-t border-cv-border">
                <a href="/help" className="w-full text-left p-4 bg-cv-elevated rounded-xl font-bold text-sm text-cv-secondary border border-cv-border-strong block mb-4">
                  Help Center
                </a>
                
                <h3 className="text-xs font-mono font-bold text-cv-muted uppercase mb-2">Advanced / Debug</h3>
                
                <button onClick={() => { setShowMobileMenu(false); setShowAdvancedLogs(true); }} className="w-full text-left p-3 text-xs font-bold text-cv-muted bg-cv-elevated/50 rounded-lg border border-cv-border-strong mb-2">
                  System Logs
                </button>
                
                {user && (user.uid === 'ADMIN_TEST_UID' || user.email === 'skaldhaven@gmail.com') && (
                  <>
                    <button onClick={() => { setShowMobileMenu(false); setPaywallModal({ isOpen: true, reason: 'generation' }); }} className="w-full text-left p-3 text-xs font-bold text-amber-500 bg-cv-elevated/50 rounded-lg border border-cv-border-strong mb-2">
                      Developer Billing Tools
                    </button>
                    <button onClick={() => { setShowMobileMenu(false); setShowAgentDebug(true); }} className="w-full text-left p-3 text-xs font-bold text-cv-muted bg-cv-elevated/50 rounded-lg border border-cv-border-strong mb-2">
                      Agent Status
                    </button>
                  </>
                )}

                <button 
                  onClick={() => {
                    setShowMobileMenu(false);
                    if (window.confirm("EMERGENCY RESET: This will cancel any active generation and reset the orchestration loop. Only use if the app is frozen. Continue?")) {
                      setIsAutoRunning(false);
                      setPausedRecoveryMetadata(null);
                      runOrchestrationLoop(true); 
                    }
                  }} 
                  className="w-full text-left p-3 text-xs font-bold text-orange-500 bg-orange-950/20 rounded-lg border border-orange-500/20 mt-4"
                >
                  Emergency Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rewriteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-cv-panel border border-cv-border-strong rounded-xl p-6 w-[500px] max-w-[90vw] shadow-2xl flex flex-col gap-4">
            <h2 className="text-lg font-black text-white uppercase tracking-wider flex items-center gap-2">
              <RewriterIcon />
              Rewrite Protocol
            </h2>
            <p className="text-sm text-cv-muted">
              State your reason and instructions for this localized edit on Chapter {rewriteModal.chapterId}.
            </p>
            <textarea
              value={rewriteInstruction}
              onChange={(e) => setRewriteInstruction(e.target.value)}
              placeholder="e.g., 'Make the dialogue more aggressive', 'Add more description of the environment'..."
              className="bg-cv-page border border-cv-border rounded-lg p-3 text-sm text-cv-secondary font-mono focus:outline-none focus:border-cv-interactive/50 min-h-[120px] resize-none"
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-2">
              <button
                onClick={() => {
                  setRewriteModal({ isOpen: false, chapterId: null });
                  setRewriteInstruction("");
                }}
                className="px-4 py-2 rounded text-xs font-bold uppercase text-cv-muted hover:text-white hover:bg-cv-elevated transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRewrite}
                disabled={!rewriteInstruction.trim()}
                className="px-4 py-2 rounded text-xs font-black uppercase bg-purple-600 text-white hover:bg-cv-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-purple-900/20"
              >
                Execute Rewrite
              </button>
            </div>
          </div>
        </div>
      )}

      {storyBible && (
        <RoadmapEditor 
            isOpen={isRoadmapEditorOpen}
            onClose={() => setIsRoadmapEditorOpen(false)}
            plans={storyBible.chapterPlans || []}
            warnings={storyBible.roadmapWarnings || []}
          targetChapterCount={storyBible.targetChapterCount}
          chapters={chapters}
          onSave={handleRoadmapSave}
          onRegenerateRemaining={handleRegenerateRemainingRoadmap}
          onRegenerateChapter={handleRegenerateSingleChapter}
        />
      )}

      {isPublicationModalOpen && storyBible && currentProjectId && (
        <PublicationExport 
          uid={user?.uid!} 
          projectId={currentProjectId} 
          storyBible={storyBible} 
          chapters={chapters} 
          onUpdateChapters={setChapters}
          onRepairRequested={repairMetadataSave}
          onClose={() => setIsPublicationModalOpen(false)} 
        />
      )}

      {isManuscriptEditorOpen && storyBible && currentProjectId && (
        <ManuscriptEditor
          uid={user?.uid!}
          projectId={currentProjectId}
          storyBible={storyBible}
          chapters={chapters}
          onUpdateChapters={(updatedChapters) => {
            setChapters(updatedChapters);
            setStoryBible(prev => prev ? { ...prev, hasUnsyncedEdits: true } as StoryBible : null);
            setProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, hasUnsyncedEdits: true } : p));
            saveCurrentProject(true, { 
               chapters: updatedChapters, 
               storyBible: storyBible ? { ...storyBible, hasUnsyncedEdits: true } as StoryBible : undefined
            }, false);
            if (user && currentProjectId) {
              setDoc(doc(db, 'users', user.uid, 'projects', currentProjectId), { hasUnsyncedEdits: true }, { merge: true }).catch(console.error);
            }
          }}
          onClose={() => setIsManuscriptEditorOpen(false)}
          onExport={() => {
            const project = projects.find(p => p.id === currentProjectId);
            const actualLoadedCount = chapters.length;
            const summaryCount = project?.chapterCount ?? 0;
            
            if (publicationError?.includes('Retry Metadata Save')) {
              alert("Metadata mismatch. Please click 'Retry Metadata Save' to repair your project before exporting.");
              return;
            }
            if (project && actualLoadedCount < summaryCount) {
              alert("Metadata mismatch. The actual loaded chapters are fewer than expected.");
              return;
            }
            if (project && summaryCount < actualLoadedCount) {
              alert("Your project metadata is slightly out of sync. Repairing now...");
              saveCurrentProject(true).then(() => {
                alert("Repair complete. You can now export.");
              });
              return;
            }

            setIsManuscriptEditorOpen(false);
            if (!isProjectUnlocked(storyBible)) {
              setPaywallModal({ isOpen: true, reason: 'export' });
            } else {
              setIsPublicationModalOpen(true);
            }
          }}
          onShowPaywall={(reason) => setPaywallModal({ isOpen: true, reason })}
        />
      )}

      {showReconciliationPrompt && (
        <div className="fixed inset-0 z-[100] bg-cv-page/80 backdrop-blur-sm flex items-center justify-center p-4">
           <div className="bg-cv-panel border border-cv-border-strong rounded-xl max-w-md w-full p-6 shadow-2xl">
              <h3 className="text-xl font-black text-white uppercase tracking-tighter mb-2 text-cv-warning">Update Story Memory</h3>
              <p className="text-cv-muted text-sm mb-6">Your manuscript has edits that have not been added to Story Memory yet. Update Story Memory before starting the next volume.</p>
              <div className="flex flex-col gap-3">
                 <button onClick={reconcileFinalManuscriptToCanon} className="w-full py-3 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-lg shadow-lg" disabled={isInitializing}>
                    {isInitializing ? 'Reconciling...' : 'Update Story Memory'}
                 </button>
                 <button onClick={() => setShowReconciliationPrompt(false)} className="w-full py-3 text-cv-muted hover:text-white font-bold transition-colors" disabled={isInitializing}>
                    Cancel
                 </button>
              </div>
           </div>
        </div>
      )}

      {renderPaywallModal()}

      <SequelOperationPanel
        open={sequelPanelOpen}
        operation={sequelOperation}
        stepMessage={sequelStepMessage}
        busy={isInitializing}
        onResume={handleResumeSequel}
        onCancelSetup={handleCancelSequelSetup}
        onDismiss={() => setSequelPanelOpen(false)}
      />

      {currentProjectId && (
        <>
          <ProjectIntegrityModal
            isOpen={projectIntegrityModalOpen}
            completionState={workspaceState}
            projectId={currentProjectId}
            onClose={() => setProjectIntegrityModalOpen(false)}
          />
          <MissingChapterModal
            isOpen={missingChapterModalOpen}
            projectId={currentProjectId}
            missingNumbers={gapsInTarget}
            onClose={() => setMissingChapterModalOpen(false)}
            onRecovered={() => {
              setMissingChapterModalOpen(false);
              void handleSelectProject(currentProjectId);
            }}
          />
        </>
      )}
    </div>
  );
}
